import React from "react";
import axios from "axios";
import { APIProvider, Map as GoogleMap, AdvancedMarker, Pin, InfoWindow } from "@vis.gl/react-google-maps";
import { MdAccessibility } from "react-icons/md";
import NoteAreaFall2023 from "../StudentOnboardingCardModalFall2023/NoteAreaFall2023/NoteAreaFall2023";
import { format, startOfWeek, addDays, addWeeks, differenceInCalendarWeeks, isSameDay } from "date-fns";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";
import {
  createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime,
  createEventObjectForTeacherWeekView,
  getTeacherEventsForWeek,
} from "../Calendar/calendarFunctions";
import useDriveMatrix from "./useDriveMatrix";

const SCHOOL_OPTIONS = [
  { id: "mma", label: "Mountainside" },
  { id: "lsma", label: "Lakeside" },
  { id: "tma", label: "Treeside" },
  { id: "mor", label: "Morningside" },
  { id: "psma", label: "Parkside" },
  { id: "den", label: "Denton" },
  { id: "tem", label: "Tempe" },
  { id: "tul", label: "Tulsa" },
];

const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const SLOT_MINUTES = 5;
const PIXELS_PER_SLOT = 4; // 5 minutes per slot
const DEFAULT_START_MINUTES = 7 * 60; // 7am
const DEFAULT_END_MINUTES = 21 * 60; // 9pm
const MIN_WINDOW_MINUTES = 14 * 60; // keep a wide window by default

const DragAndDropScheduler2025 = ({ userData }) => {
  const getStudentKey = React.useCallback((obj) => {
    if (!obj) return "";
    const candidates = [
      obj.studentId,
      obj.placementKey,
      obj.id,
      obj.studentName,
      obj.familyName,
    ].filter(Boolean);
    return String(candidates[0] || "");
  }, []);
  const [showLedger, setShowLedger] = React.useState(true);
  const [students, setStudents] = React.useState([]);
  const [allStudents, setAllStudents] = React.useState([]);
  const [teachers, setTeachers] = React.useState([]);
  const [selectedSchool, setSelectedSchool] = React.useState("");
  const [selectedTeacherId, setSelectedTeacherId] = React.useState("");
  const [welcomeStep, setWelcomeStep] = React.useState("welcome"); // "welcome", "new", null
  const [weekOffset, setWeekOffset] = React.useState(0);
  const [plannedPlacements, setPlannedPlacements] = React.useState([]); // stored as recurring patterns
  const [placedStudentIds, setPlacedStudentIds] = React.useState([]);
  const [lockedStudentIds, setLockedStudentIds] = React.useState([]);
  const [manualReassignmentStudents, setManualReassignmentStudents] = React.useState([]);
  const [overrideReassignmentStudents, setOverrideReassignmentStudents] = React.useState(null);
  const [scheduleName, setScheduleName] = React.useState("");
  const [showAddStudentModal, setShowAddStudentModal] = React.useState(false);
  const [showNoDriveModal, setShowNoDriveModal] = React.useState(false);
  const [removedReassignmentIds, setRemovedReassignmentIds] = React.useState([]);
  const [noDriveContext, setNoDriveContext] = React.useState(null);
  const [studentSearchTerm, setStudentSearchTerm] = React.useState("");
  const [selectedStudentToAdd, setSelectedStudentToAdd] = React.useState("");
  const [studentSearchResults, setStudentSearchResults] = React.useState([]);
  const [studentSearchLoading, setStudentSearchLoading] = React.useState(false);
  const [studentSearchError, setStudentSearchError] = React.useState(null);
  const studentSearchTimeoutRef = React.useRef(null);
  const [currentScheduleId, setCurrentScheduleId] = React.useState(null);
  const [showLoadScheduleModal, setShowLoadScheduleModal] = React.useState(false);
  const [loadingSchedules, setLoadingSchedules] = React.useState(false);
  const [savedSchedules, setSavedSchedules] = React.useState([]);
  const skipNewScheduleResetRef = React.useRef(false);
  const [draggingStudent, setDraggingStudent] = React.useState(null);
  const [draggingPlacement, setDraggingPlacement] = React.useState(null);
  const [dragPreview, setDragPreview] = React.useState(null); // custom drag ghost
  const [isHoveringStudentColumn, setIsHoveringStudentColumn] = React.useState(false);
  const [hoverInsertIndex, setHoverInsertIndex] = React.useState(null);
  const studentListRef = React.useRef(null);
  const [loading, setLoading] = React.useState(false);
  const [maxDriveMinutes, setMaxDriveMinutes] = React.useState(""); // user-specified cap
  const [paddingMinutes, setPaddingMinutes] = React.useState(""); // optional buffer between lessons
  const dayRefs = React.useRef({});
  const calendarRef = React.useRef(null);
  const [hoverIndicator, setHoverIndicator] = React.useState(null);
  const lastHoverRef = React.useRef({ day: null, label: null });
  const lastPreviewSlotRef = React.useRef({ day: null, slot: null });
  const saveScheduleDebounceRef = React.useRef(null);
  const [calendarEvents, setCalendarEvents] = React.useState([]);
  const [calendarDaysWithEvents, setCalendarDaysWithEvents] = React.useState({});
  const [weekViewEvents, setWeekViewEvents] = React.useState([]);
  const [familyInfoCache, setFamilyInfoCache] = React.useState({});
  const [availabilityByDay, setAvailabilityByDay] = React.useState({});
  const [teacherCalendarEvents, setTeacherCalendarEvents] = React.useState([]);
  const [calendarEventsLoaded, setCalendarEventsLoaded] = React.useState(false);
  const [openCalendarModalAfterFetch, setOpenCalendarModalAfterFetch] = React.useState(false);
  const [allInstruments, setAllInstruments] = React.useState([]);
  const [creatingPlaceDirectories, setCreatingPlaceDirectories] = React.useState(false);
  const [placeDirectory, setPlaceDirectory] = React.useState([]);
  const [driveEdges, setDriveEdges] = React.useState([]);
  const [missingDrivePairs, setMissingDrivePairs] = React.useState([]);
  const [missingTeacherDrivePairs, setMissingTeacherDrivePairs] = React.useState([]);
  const [showMissingTeacherDriveModal, setShowMissingTeacherDriveModal] = React.useState(false);
  const [missingReassignmentDrivePairs, setMissingReassignmentDrivePairs] = React.useState([]);
  const [showMissingReassignmentDriveModal, setShowMissingReassignmentDriveModal] = React.useState(false);
  const lastMissingTeacherRef = React.useRef({ teacherId: null, count: 0 });
  const [calculatingMissingDriveTimes, setCalculatingMissingDriveTimes] = React.useState(false);
  const [calculatingReassignmentDriveTimes, setCalculatingReassignmentDriveTimes] = React.useState(false);
  const [promptReassignmentDriveTimes, setPromptReassignmentDriveTimes] = React.useState(false);
  const [calendarEventsLoadedForTeacher, setCalendarEventsLoadedForTeacher] = React.useState(false);
  const [showCalendarEventsModal, setShowCalendarEventsModal] = React.useState(false);
  const [optimizeLedger, setOptimizeLedger] = React.useState([]);
  const [showEventDriveModal, setShowEventDriveModal] = React.useState(false);
  const [eventModalData, setEventModalData] = React.useState(null);
  const [eventModalFamily, setEventModalFamily] = React.useState(null);
  const [activeMapMarkerId, setActiveMapMarkerId] = React.useState(null);
  const [showAllWeekMarkers, setShowAllWeekMarkers] = React.useState(false);
  const [showEventMap, setShowEventMap] = React.useState(false);
  const [showMissingPlaceModal, setShowMissingPlaceModal] = React.useState(false);
  const [scheduleLoadedAt, setScheduleLoadedAt] = React.useState(0);
  const [eventDriveEdges, setEventDriveEdges] = React.useState([]);
  const [eventDriveTitle, setEventDriveTitle] = React.useState("");
  const eventHoldTimerRef = React.useRef(null);
  const LONG_PRESS_MS = 2000;
  const transparentDragImg = React.useRef(null);
  // Prefer the backend schoolId if available, otherwise use the abbreviation
  const selectedSchoolId = React.useMemo(() => {
    const teacherMatch = teachers.find(
      (t) =>
        t.School?.toLowerCase() === selectedSchool &&
        (t.SchoolID || t.SchoolId || t.schoolId)
    );
    if (teacherMatch) return teacherMatch.SchoolID || teacherMatch.SchoolId || teacherMatch.schoolId;
    const studentMatch =
      (allStudents.length ? allStudents : students).find(
        (s) =>
          s.school?.toLowerCase() === selectedSchool &&
          (s.schoolId || s.SchoolId || s.SchoolID)
      );
    if (studentMatch) return studentMatch.schoolId || studentMatch.SchoolId || studentMatch.SchoolID;
    return selectedSchool;
  }, [allStudents, selectedSchool, students, teachers]);

  const { minutesBetween, minutesMap, seedEdges, ensureAllPairs } = useDriveMatrix(
    selectedSchoolId,
    "17:00-18:00"
  );
  const transparentDragImage = React.useMemo(() => {
    const img = new Image();
    img.src = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";
    return img;
  }, []);

  const parseTimeToMinutes = (val) => {
    if (val == null) return null;
    if (typeof val === "number") return val;
    if (typeof val === "string") {
      const parts = val.split(":");
      if (parts.length >= 2) {
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) || 0;
        if (!isNaN(h) && !isNaN(m)) return h * 60 + m;
      }
      const dashParts = val.split("-");
      if (dashParts.length === 2) {
        const start = parseTimeToMinutes(dashParts[0]);
        const end = parseTimeToMinutes(dashParts[1]);
        if (start != null && end != null) return [start, end];
      }
    }
    if (val.start && val.end) {
      const start = parseTimeToMinutes(val.start);
      const end = parseTimeToMinutes(val.end);
      if (start != null && end != null) return [start, end];
    }
    if (val.StartTime && val.EndTime) {
      const start = parseTimeToMinutes(val.StartTime);
      const end = parseTimeToMinutes(val.EndTime);
      if (start != null && end != null) return [start, end];
    }
    if (val.startTime && val.endTime) {
      const start = parseTimeToMinutes(val.startTime);
      const end = parseTimeToMinutes(val.endTime);
      if (start != null && end != null) return [start, end];
    }
    return null;
  };

  const formatAddress = React.useCallback((address) => {
    if (!address) return null;
    const addr = address.Address || address.address || address;
    const parts = [
      addr.addressLine1 || addr.AddressLine1 || addr.address1 || addr.streetAddress || addr.StreetAddress || addr.street,
      addr.addressLine2 || addr.AddressLine2 || addr.address2 || addr.unit,
      [addr.city || addr.City, addr.state || addr.State].filter(Boolean).join(", ").trim(),
      addr.zipcode || addr.Zipcode || addr.zip || addr.Zip || addr.zipCode,
    ].filter(Boolean);
    return parts.filter((p) => p && p !== ",").join(", ") || null;
  }, []);

  const normalizeAvailability = React.useCallback(
    (availabilityDetails) => {
      if (!availabilityDetails) return {};
      const map = {};
      weekDays.forEach((day) => {
        const key = day;
        const lower = day.toLowerCase();
        const shortKey = day.slice(0, 3).toLowerCase();
        const entries =
          availabilityDetails[key] ||
          availabilityDetails[lower] ||
          availabilityDetails[shortKey] ||
          [];
        const blocks = [];
        entries.forEach((entry) => {
          const parsed = parseTimeToMinutes(entry);
          if (Array.isArray(parsed) && parsed.length === 2) {
            blocks.push({ start: parsed[0], end: parsed[1] });
          }
        });
        blocks.sort((a, b) => a.start - b.start);
        map[day] = blocks;
      });
      return map;
    },
    []
  );

  const startOfThisWeek = React.useMemo(
    () => startOfWeek(addWeeks(new Date(), weekOffset), { weekStartsOn: 1 }),
    [weekOffset]
  );
  const displayedWeek = React.useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(startOfThisWeek, i)),
    [startOfThisWeek]
  );

  const driveBucket = "17:00-18:00";
  const DRIVE_TIME_COST_PER_PAIR = 0.01;
  const makeDriveKey = React.useCallback(
    (fromKey, toKey) => `${selectedSchoolId || selectedSchool || "na"}|${driveBucket}|${fromKey}=>${toKey}`,
    [selectedSchool, selectedSchoolId]
  );

  const timeWindow = React.useMemo(() => {
    const ranges = [];
    weekViewEvents.forEach((event) => {
      const start = event.startDate || event.parsedDate || (event.dateTime ? new Date(event.dateTime) : null);
      if (!start || isNaN(start)) return;
      const duration = Number(event.lessonLength) || (event.rowLength ? event.rowLength * SLOT_MINUTES : 30);
      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const endMinutes = startMinutes + Math.max(duration, 15);
      ranges.push([startMinutes, endMinutes]);
    });
    plannedPlacements
      .filter((p) => p.teacherId === selectedTeacherId)
      .forEach((placement) => {
        const startMinutes = placement.startMinutes;
        const durationMinutes = Number(placement.lessonLength) || 30;
        ranges.push([startMinutes, startMinutes + durationMinutes]);
      });

    let startMinutes = DEFAULT_START_MINUTES;
    let endMinutes = DEFAULT_END_MINUTES;
    if (ranges.length) {
      const minVal = Math.min(...ranges.map((r) => r[0]));
      const maxVal = Math.max(...ranges.map((r) => r[1]));
      startMinutes = Math.min(minVal, DEFAULT_START_MINUTES);
      endMinutes = Math.max(maxVal, DEFAULT_END_MINUTES);
    }
    if (endMinutes - startMinutes < MIN_WINDOW_MINUTES) {
      endMinutes = startMinutes + MIN_WINDOW_MINUTES;
    }
    const startHour = Math.floor(startMinutes / 60);
    const endHour = Math.ceil(endMinutes / 60);
    const totalMinutes = endMinutes - startMinutes;
    const totalHeightPx = (totalMinutes / SLOT_MINUTES) * PIXELS_PER_SLOT;
    return { startMinutes, endMinutes, startHour, endHour, totalMinutes, totalHeightPx };
  }, [weekViewEvents, plannedPlacements, selectedTeacherId]);

  React.useEffect(() => {
    if (!selectedTeacherId) {
      setAvailabilityByDay({});
      return;
    }
    const teacher = teachers.find(
      (t) => String(t.TeacherID) === String(selectedTeacherId)
    );
    if (teacher?.AvailabilityDetails) {
      setAvailabilityByDay(normalizeAvailability(teacher.AvailabilityDetails));
    } else {
      setAvailabilityByDay({});
    }
  }, [normalizeAvailability, selectedTeacherId, teachers]);

  const getUnavailableSegments = React.useCallback(
    (dayName) => {
      const avail = availabilityByDay[dayName] || [];
      const segments = [];
      let cursor = timeWindow.startMinutes;
      avail.forEach((block) => {
        const blockStart = block.start;
        const blockEnd = block.end;
        if (blockStart > cursor) {
          segments.push([cursor, Math.min(blockStart, timeWindow.endMinutes)]);
        }
        cursor = Math.max(cursor, blockEnd);
      });
      if (cursor < timeWindow.endMinutes) {
        segments.push([cursor, timeWindow.endMinutes]);
      }
      return segments
        .filter((seg) => seg[1] > seg[0])
        .map((seg) => {
          const top = ((seg[0] - timeWindow.startMinutes) / SLOT_MINUTES) * PIXELS_PER_SLOT;
          const height = ((seg[1] - seg[0]) / SLOT_MINUTES) * PIXELS_PER_SLOT;
          return { top, height };
        });
    },
    [availabilityByDay, timeWindow]
  );

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [studentsRes, teachersRes, allStudentsRes, instrumentsRes] = await Promise.all([
          axios.get(`${baseUrl}/engaged-students`, getRequestHeaders()),
          axios.get(`${baseUrl}/initialadminrequest`, getRequestHeaders()),
          axios.get(`${baseUrl}/school-management`, getRequestHeaders()),
          axios.get(`${baseUrl}/inst`, getRequestHeaders()).catch(() => ({ data: [] })),
        ]);
        setStudents(studentsRes.data);
        setTeachers(teachersRes.data);
        setAllStudents(Array.isArray(allStudentsRes.data) ? allStudentsRes.data : []);
        setAllInstruments(Array.isArray(instrumentsRes.data) ? instrumentsRes.data : []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getStudentTeacherId = React.useCallback((student) => {
    if (!student) return null;
    return (
      student.teachersId ||
      student.teacherId ||
      student.teacherID ||
      student.TeacherID ||
      student?.teacher?.TeacherID ||
      null
    );
  }, []);

  const leavingTeacherIds = React.useMemo(() => {
    if (!teachers.length) return new Set();
    return new Set(
      teachers
        .filter(
          (teacher) =>
            teacher.Status === "leaving" ||
            teacher.Status === "Left School" ||
            teacher.Status === "summer-break"
        )
        .map((teacher) => String(teacher.TeacherID))
    );
  }, [teachers]);

  // Fetch place directory for selected school
  React.useEffect(() => {
    if (!selectedSchoolId) return;
    if (String(selectedSchoolId).toLowerCase() === "mor") {
      console.log("Fetching place directory for /place-directory/mor");
    }
    axios
      .get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders())
      .then((res) => {
        const payload = res.data;
        if (payload && Array.isArray(payload.directories)) {
          setPlaceDirectory(payload.directories);
        } else if (Array.isArray(payload)) {
          setPlaceDirectory(payload);
        } else {
          setPlaceDirectory([]);
        }
      })
      .catch(() => setPlaceDirectory([]));
  }, [selectedSchoolId]);

  const familiesNeedingNewTeacher = React.useMemo(() => {
    if (!students.length || !teachers.length) return [];
    return students.filter((family) =>
      family.students?.some((student) => {
        const tid = getStudentTeacherId(student);
        return tid && leavingTeacherIds.has(String(tid));
      })
    );
  }, [getStudentTeacherId, leavingTeacherIds, students, teachers]);

  const studentsInSelectedSchool = React.useMemo(() => {
    if (!selectedSchool) return [];
    const sourceFamilies = allStudents.length ? allStudents : students;
    const list = [];
    const seen = new Set();
    sourceFamilies.forEach((family) => {
      if (family.school?.toLowerCase() !== selectedSchool) return;
      family.students?.forEach((student) => {
        const teacherId = getStudentTeacherId(student);
        if (!teacherId || !leavingTeacherIds.has(String(teacherId))) return;
        const uniqueId = `${family.familyId}-${student.studentId}`;
        if (placedStudentIds.includes(uniqueId) || seen.has(uniqueId)) return;
        list.push({
          id: uniqueId,
          familyId: family.familyId,
          familyName: family.familyName,
          studentId: student.studentId,
          studentName: student.studentName,
          instruments: student.instrumentList || student.instruments || [],
          lessonLength: student.lessonLength,
          availability: student.initialAvailability,
          school: family.school?.toLowerCase(),
          familyAddress: family.familyAddress || family.address || student.address,
          teacherId,
        });
        seen.add(uniqueId);
      });
    });
    return list;
  }, [allStudents, getStudentTeacherId, leavingTeacherIds, placedStudentIds, selectedSchool, students]);

  const allStudentsForSelectedSchool = React.useMemo(() => {
    if (!selectedSchool) return [];
    const sourceFamilies = allStudents.length ? allStudents : students;
    const list = [];
    const seen = new Set();
    sourceFamilies.forEach((family) => {
      if (family.school?.toLowerCase() !== selectedSchool) return;
      family.students?.forEach((student) => {
        const studentId = student.studentId || student.id || student.studentName;
        const uniqueId = `${family.familyId}-${studentId}`;
        if (!studentId || !uniqueId || seen.has(uniqueId)) return;
        list.push({
          id: uniqueId,
          familyId: family.familyId,
          familyName: family.familyName,
          studentId,
          studentName: student.studentName || student.name,
          instruments: student.instrumentList || student.instruments || [],
          lessonLength: student.lessonLength,
          availability: student.initialAvailability,
          school: family.school?.toLowerCase(),
          familyAddress: family.familyAddress || family.address || student.address,
          teacherId: getStudentTeacherId(student),
        });
        seen.add(uniqueId);
      });
    });
    return list;
  }, [allStudents, getStudentTeacherId, selectedSchool, students]);

  const placementStudentKeys = React.useMemo(() => {
    const keys = new Set(placedStudentIds.map((id) => String(id)));
    plannedPlacements.forEach((placement) => {
      if (!placement) return;
      const candidates = [
        placement.placementKey,
        placement.id,
        placement.studentId,
        placement.familyId && placement.studentId
          ? `${placement.familyId}-${placement.studentId}`
          : null,
      ].filter(Boolean);
      candidates.forEach((candidate) => keys.add(String(candidate)));
    });
    return keys;
  }, [placedStudentIds, plannedPlacements]);

  const studentsNeedingReassignment = React.useMemo(() => {
    const list = [];
    const seen = new Set();

    // If we have an override list (e.g., loaded a single teacher's students), use it
    if (overrideReassignmentStudents?.length) {
      overrideReassignmentStudents.forEach((student) => {
        const sid =
          student.id ||
          student.studentId ||
          `${student.familyId || "family"}-${student.studentId || student.studentName}`;
        if (!sid || placementStudentKeys.has(String(sid)) || seen.has(sid) || removedReassignmentIds.includes(sid)) return;
        list.push(student);
        seen.add(sid);
      });
      return list;
    }

    // If a school is selected without an override, start empty and let manual additions fill in.
    if (!selectedSchool) {
      familiesNeedingNewTeacher.forEach((family) => {
        family.students?.forEach((student) => {
          const teacherId = getStudentTeacherId(student);
          if (!teacherId || !leavingTeacherIds.has(String(teacherId))) return;
          const uniqueId = `${family.familyId}-${student.studentId}`;
          if (placementStudentKeys.has(String(uniqueId)) || seen.has(uniqueId) || removedReassignmentIds.includes(uniqueId)) return;
          const item = {
            id: uniqueId,
            familyId: family.familyId,
            familyName: family.familyName,
            studentId: student.studentId,
            studentName: student.studentName,
            instruments: student.instrumentList || [],
            lessonLength: student.lessonLength,
            availability: student.initialAvailability,
            school: family.school?.toLowerCase(),
            familyAddress: family.familyAddress || family.address || student.address,
            teacherId,
          };
          list.push(item);
          seen.add(uniqueId);
        });
      });
    }

    manualReassignmentStudents.forEach((student) => {
      if (!student) return;
      if (selectedSchool && student.school?.toLowerCase() !== selectedSchool) return;
      const sid =
        student.id ||
        student.studentId ||
        `${student.familyId || "family"}-${student.studentId || student.studentName}`;
      if (!sid || placementStudentKeys.has(String(sid)) || seen.has(sid) || removedReassignmentIds.includes(sid)) return;
      list.push({ ...student, id: sid });
      seen.add(sid);
    });
    return list;
  }, [
    familiesNeedingNewTeacher,
    overrideReassignmentStudents,
    getStudentTeacherId,
    leavingTeacherIds,
    manualReassignmentStudents,
    placementStudentKeys,
    removedReassignmentIds,
    selectedSchool,
  ]);

  const existingReassignmentIds = React.useMemo(() => {
    const existingIds = new Set();
    studentsNeedingReassignment.forEach((s) => {
      const id = s.id || s.studentId || `${s.familyId || "family"}-${s.studentId || s.studentName}`;
      if (id) existingIds.add(String(id));
    });
    placementStudentKeys.forEach((id) => existingIds.add(String(id)));
    return existingIds;
  }, [placementStudentKeys, studentsNeedingReassignment]);

  const reassignmentIdSet = React.useMemo(() => {
    const ids = new Set();
    const addStudent = (student) => {
      if (!student) return;
      const id =
        student.id ||
        student.studentId ||
        `${student.familyId || "family"}-${student.studentId || student.studentName}`;
      if (id) ids.add(String(id));
    };
    if (Array.isArray(overrideReassignmentStudents)) {
      overrideReassignmentStudents.forEach(addStudent);
    }
    manualReassignmentStudents.forEach(addStudent);
    return ids;
  }, [manualReassignmentStudents, overrideReassignmentStudents]);

  const instrumentSetForSchool = React.useMemo(() => {
    const set = new Set();
    const addInstruments = (student) => {
      student.instrumentList?.forEach((inst) => {
        if (inst.parentInstrument) set.add(inst.parentInstrument);
      });
      student.instruments?.forEach((inst) => {
        if (inst.parentInstrument) set.add(inst.parentInstrument);
      });
    };
    familiesNeedingNewTeacher
      .filter((family) => !selectedSchool || family.school?.toLowerCase() === selectedSchool)
      .forEach((family) => {
        family.students?.forEach(addInstruments);
      });
    if (selectedSchool) {
      studentsInSelectedSchool.forEach(addInstruments);
    }
    manualReassignmentStudents
      .filter((student) => !selectedSchool || student.school?.toLowerCase() === selectedSchool)
      .forEach(addInstruments);
    return set;
  }, [
    familiesNeedingNewTeacher,
    manualReassignmentStudents,
    selectedSchool,
    studentsInSelectedSchool,
  ]);

  const addableStudentsForSelectedSchool = React.useMemo(() => {
    if (!selectedSchool) return [];
    return allStudentsForSelectedSchool.filter((s) => !existingReassignmentIds.has(String(s.id)));
  }, [allStudentsForSelectedSchool, existingReassignmentIds, selectedSchool]);

  React.useEffect(() => {
    if (!showAddStudentModal || !selectedSchool) {
      setStudentSearchResults([]);
      setStudentSearchLoading(false);
      setStudentSearchError(null);
      return;
    }
    const term = studentSearchTerm.trim();
    if (!term) {
      setStudentSearchResults([]);
      setStudentSearchLoading(false);
      setStudentSearchError(null);
      return;
    }
    if (studentSearchTimeoutRef.current) {
      clearTimeout(studentSearchTimeoutRef.current);
    }
    setStudentSearchLoading(true);
    setStudentSearchError(null);
    const schoolLower = selectedSchool.toLowerCase();
    let cancelled = false;
    studentSearchTimeoutRef.current = setTimeout(() => {
      axios
        .get(`${baseUrl}/search/${encodeURIComponent(term)}`, getRequestHeaders())
        .then((res) => {
          if (cancelled) return;
          const payload = Array.isArray(res.data) ? res.data : [];
          const nextResults = [];
          const seen = new Set();
          payload.forEach((item) => {
            if (!item) return;
            if (Array.isArray(item.students)) {
              const familySchool = (item.school || item.School || "").toLowerCase();
              item.students.forEach((student) => {
                const studentSchool = (student.school || student.School || "").toLowerCase();
                const resolvedSchool = familySchool || studentSchool;
                if (!resolvedSchool || resolvedSchool !== schoolLower) return;
                const studentId = student.studentId || student.id || student.studentName;
                if (!studentId) return;
                const familyId = item.familyId || item.id || item.familyID || null;
                const uniqueId = `${familyId || "family"}-${studentId}`;
                if (seen.has(uniqueId) || existingReassignmentIds.has(String(uniqueId))) return;
                nextResults.push({
                  id: uniqueId,
                  familyId,
                  familyName: item.familyName,
                  studentId,
                  studentName: student.studentName || student.name,
                  instruments: student.instrumentList || student.instruments || [],
                  lessonLength: student.lessonLength,
                  availability: student.initialAvailability,
                  school: resolvedSchool,
                  familyAddress: item.familyAddress || item.address || student.address,
                  teacherId: getStudentTeacherId(student),
                });
                seen.add(uniqueId);
              });
              return;
            }
            const studentId = item.studentId || item.id || item.studentName;
            if (!studentId) return;
            const studentSchool = (
              item.school ||
              item.School ||
              item.schoolName ||
              item.SchoolName ||
              ""
            ).toLowerCase();
            if (!studentSchool || studentSchool !== schoolLower) return;
            const familyId = item.familyId || item.familyID || item.FamilyID || null;
            const uniqueId = `${familyId || "family"}-${studentId}`;
            if (seen.has(uniqueId) || existingReassignmentIds.has(String(uniqueId))) return;
            nextResults.push({
              id: uniqueId,
              familyId,
              familyName: item.familyName,
              studentId,
              studentName: item.studentName || item.name,
              instruments: item.instrumentList || item.instruments || [],
              lessonLength: item.lessonLength,
              availability: item.initialAvailability,
              school: studentSchool,
              familyAddress: item.familyAddress || item.address,
              teacherId: getStudentTeacherId(item),
            });
            seen.add(uniqueId);
          });
          setStudentSearchResults(nextResults.slice(0, 50));
          setStudentSearchLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          console.log(err);
          setStudentSearchResults([]);
          setStudentSearchLoading(false);
          setStudentSearchError("Unable to search students right now.");
        });
    }, 300);
    return () => {
      cancelled = true;
      if (studentSearchTimeoutRef.current) {
        clearTimeout(studentSearchTimeoutRef.current);
      }
    };
  }, [
    existingReassignmentIds,
    getStudentTeacherId,
    selectedSchool,
    showAddStudentModal,
    studentSearchTerm,
  ]);

  const handleAddStudentToReassignment = React.useCallback(() => {
    if (!selectedStudentToAdd) return;
    const match = addableStudentsForSelectedSchool.find(
      (s) => String(s.id) === String(selectedStudentToAdd)
    );
    if (!match) return;
    console.log("I'm here");
    setManualReassignmentStudents((prev) => {
      const exists = prev.some((s) => String(s.id) === String(match.id));
      return exists ? prev : [...prev, match];
    });
    setSelectedStudentToAdd("");
    setStudentSearchTerm("");
  }, [addableStudentsForSelectedSchool, selectedStudentToAdd]);

  const handleRemoveReassignmentStudent = React.useCallback(
    (student) => {
      if (!student) return;
      const sid =
        student.id ||
        student.studentId ||
        `${student.familyId || "family"}-${student.studentId || student.studentName}`;
      if (!sid) return;
      setRemovedReassignmentIds((prev) => (prev.includes(sid) ? prev : [...prev, sid]));
      setManualReassignmentStudents((prev) =>
        prev.filter((s) => String(s.id || s.studentId) !== String(sid))
      );
      setOverrideReassignmentStudents((prev) =>
        prev ? prev.filter((s) => String(s.id || s.studentId) !== String(sid)) : prev
      );
    },
    []
  );

  const handleResetReassignmentPlacements = React.useCallback(() => {
    const idsToReset = new Set(reassignmentIdSet);
    if (!idsToReset.size) {
      plannedPlacements.forEach((p) => {
        if (!p) return;
        const key =
          getStudentKey(p) ||
          p.placementKey ||
          p.studentId ||
          p.id ||
          (p.familyId && p.studentId ? `${p.familyId}-${p.studentId}` : null);
        if (key) idsToReset.add(String(key));
      });
    }
    if (!idsToReset.size) return;
    setPlannedPlacements((prev) =>
      prev.filter((p) => {
        const key =
          getStudentKey(p) ||
          p.placementKey ||
          p.studentId ||
          p.id ||
          (p.familyId && p.studentId ? `${p.familyId}-${p.studentId}` : null);
        return !key || !idsToReset.has(String(key));
      })
    );
    setPlacedStudentIds((prev) => prev.filter((id) => !idsToReset.has(String(id))));
    setLockedStudentIds((prev) => prev.filter((id) => !idsToReset.has(String(id))));
    setRemovedReassignmentIds((prev) => prev.filter((id) => !idsToReset.has(String(id))));
    if (Array.isArray(overrideReassignmentStudents)) {
      const current = new Map();
      overrideReassignmentStudents.forEach((student) => {
        const id =
          student?.id ||
          student?.studentId ||
          `${student?.familyId || "family"}-${student?.studentId || student?.studentName}`;
        if (id) current.set(String(id), student);
      });
      plannedPlacements.forEach((p) => {
        if (!p) return;
        const key =
          getStudentKey(p) ||
          p.placementKey ||
          p.studentId ||
          p.id ||
          (p.familyId && p.studentId ? `${p.familyId}-${p.studentId}` : null);
        if (!key || !idsToReset.has(String(key)) || current.has(String(key))) return;
        current.set(String(key), {
          id: String(key),
          familyId: p.familyId,
          familyName: p.familyName,
          studentId: p.studentId || p.placementKey || p.id,
          studentName: p.studentName,
          instruments: p.instrument
            ? [{ parentInstrument: p.instrument, instrument: p.instrument }]
            : p.instruments || [],
          lessonLength: p.lessonLength,
          availability: p.availability,
          school: selectedSchool,
          familyAddress: p.familyAddress,
          teacherId: p.teacherId,
        });
      });
      setOverrideReassignmentStudents(Array.from(current.values()));
    }
  }, [
    getStudentKey,
    overrideReassignmentStudents,
    plannedPlacements,
    reassignmentIdSet,
    selectedSchool,
  ]);

  const addStudentToReassignmentById = React.useCallback(
    async (studentOrId) => {
      if (!studentOrId) return;
      const match =
        typeof studentOrId === "object"
          ? studentOrId
          : addableStudentsForSelectedSchool.find((s) => String(s.id) === String(studentOrId));
      if (!match) return;
      const matchId =
        match.id || `${match.familyId || "family"}-${match.studentId || match.studentName}`;
      if (!matchId) return;

      // Ensure a place directory entry exists for this family (so drive edges can be computed later).
      const schoolId = selectedSchoolId || selectedSchool;
      const familyId = match.familyId;
      if (schoolId && familyId) {
        const hasDirectoryForFamily = placeDirectory.some((pd) => {
          if (!pd) return false;
          if (pd.familyIds && Array.isArray(pd.familyIds) && pd.familyIds.some((fid) => String(fid) === String(familyId))) {
            return true;
          }
          return String(pd.key || "").toLowerCase() === String(familyId).toLowerCase();
        });
        if (!hasDirectoryForFamily) {
          const address = match.familyAddress || match.address || null;
          const addressString = address ? formatAddress(address) : null;
          if (addressString) {
            const directory = {
              key: String(familyId),
              address: addressString,
              label: match.familyName || match.studentName || String(familyId),
              roles: ["family"],
              familyIds: [familyId],
            };
            try {
              await axios.post(
                `${baseUrl}/place-directories/bulk`,
                { schoolId, directories: [directory] },
                getRequestHeaders()
              );
              setPlaceDirectory((prev) => {
                const already = prev.some((pd) => {
                  if (!pd) return false;
                  if (pd.familyIds && Array.isArray(pd.familyIds) && pd.familyIds.some((fid) => String(fid) === String(familyId))) {
                    return true;
                  }
                  return String(pd.key || "").toLowerCase() === String(familyId).toLowerCase();
                });
                return already ? prev : [...prev, directory];
              });
            } catch (err) {
              console.log(err);
            }
          }
        }
      }

      console.log("I'm here");
      setRemovedReassignmentIds((prev) => prev.filter((id) => String(id) !== String(matchId)));
      setManualReassignmentStudents((prev) => {
        const exists = prev.some((s) => String(s.id) === String(matchId));
        return exists ? prev : [...prev, { ...match, id: matchId }];
      });
      setSelectedStudentToAdd("");
      setStudentSearchTerm("");
      setShowAddStudentModal(false);
    },
    [addableStudentsForSelectedSchool, formatAddress, placeDirectory, selectedSchool, selectedSchoolId]
  );

  const saveSchedule = React.useCallback(async (labelOverride = undefined) => {
    if (!currentScheduleId) return;
    const schoolId = selectedSchoolId || selectedSchool;
    if (!schoolId) return;
    const labelToSave = labelOverride !== undefined ? labelOverride : scheduleName;
    const placementsSafe = Array.isArray(plannedPlacements) ? plannedPlacements : [];
    const lockedSafe = Array.isArray(lockedStudentIds) ? lockedStudentIds : [];
    const manualSafe = Array.isArray(manualReassignmentStudents) ? manualReassignmentStudents : [];
    const overrideSafe = Array.isArray(overrideReassignmentStudents) ? overrideReassignmentStudents : [];
    console.log("Saving schedule", { currentScheduleId, schoolId, labelToSave });
    try {
      await axios.put(
        `${baseUrl}/schedules/${currentScheduleId}`,
        {
          schoolId,
          weekStart: startOfThisWeek,
          placements: placementsSafe,
          lockedStudentIds: lockedSafe,
          manualReassignmentStudents: manualSafe,
          overrideReassignmentStudents: overrideSafe,
          label: labelToSave || undefined,
          status: "draft",
        },
        getRequestHeaders()
      );
    } catch (err) {
      console.log("Failed to save schedule", err?.response?.data || err?.message);
    }
  }, [
    currentScheduleId,
    manualReassignmentStudents,
    overrideReassignmentStudents,
    plannedPlacements,
    lockedStudentIds,
    scheduleName,
    selectedSchool,
    selectedSchoolId,
    startOfThisWeek,
  ]);

  const createSchedule = React.useCallback(
    async (overrides = {}) => {
      const schoolId = selectedSchoolId || selectedSchool;
      if (!schoolId) return null;
      const placementsSafe = Array.isArray(overrides.placements ?? plannedPlacements)
        ? overrides.placements ?? plannedPlacements
        : [];
      const lockedSafe = Array.isArray(overrides.lockedStudentIds ?? lockedStudentIds)
        ? overrides.lockedStudentIds ?? lockedStudentIds
        : [];
      const manualSafe = Array.isArray(overrides.manualReassignmentStudents ?? manualReassignmentStudents)
        ? overrides.manualReassignmentStudents ?? manualReassignmentStudents
        : [];
      const overrideSafe = Array.isArray(overrides.overrideReassignmentStudents ?? overrideReassignmentStudents)
        ? overrides.overrideReassignmentStudents ?? overrideReassignmentStudents
        : [];
      const body = {
        schoolId,
        weekStart: startOfThisWeek,
        placements: placementsSafe,
        lockedStudentIds: lockedSafe,
        manualReassignmentStudents: manualSafe,
        overrideReassignmentStudents: overrideSafe,
        label: overrides.label ?? (scheduleName || undefined),
        status: "draft",
      };
      console.log("Creating schedule", body);
      try {
        const res = await axios.post(`${baseUrl}/schedules`, body, getRequestHeaders());
        const id = res.data?._id || res.data?.id;
        if (id) setCurrentScheduleId(id);
        return id;
      } catch (err) {
        console.log("Failed to create schedule", err?.response?.data || err?.message);
        return null;
      }
    },
    [
      manualReassignmentStudents,
      overrideReassignmentStudents,
      plannedPlacements,
      lockedStudentIds,
      scheduleName,
      selectedSchool,
      selectedSchoolId,
      startOfThisWeek,
    ]
  );

  const handleDeleteSchedule = React.useCallback(
    async (scheduleId) => {
      const id = scheduleId || currentScheduleId;
      if (!id) return;
      const confirmed = window.confirm("Delete this saved schedule? This cannot be undone.");
      if (!confirmed) return;
      const schoolId = selectedSchoolId || selectedSchool;
      try {
        const payload = { schoolId };
        await axios.delete(`${baseUrl}/schedules/${id}`, {
          ...getRequestHeaders(),
          data: payload,
        });
        setSavedSchedules((prev) => prev.filter((s) => String(s._id || s.id) !== String(id)));
        if (String(currentScheduleId) === String(id)) {
          skipNewScheduleResetRef.current = true;
          setCurrentScheduleId(null);
          setScheduleName("");
        }
      } catch (err) {
        console.log("Failed to delete schedule", err?.response?.data || err?.message);
      }
    },
    [currentScheduleId, selectedSchool, selectedSchoolId]
  );

  const placeKeyLookup = React.useMemo(() => {
    const familyToPlace = new Map();
    const teacherToPlace = new Map();
    const addressToPlace = new Map();
    const labelToPlace = new Map();
    placeDirectory.forEach((entry) => {
      if (!entry.key) return;
      entry.familyIds?.forEach((fid) => familyToPlace.set(String(fid), entry.key));
      entry.teacherIds?.forEach((tid) => teacherToPlace.set(String(tid), entry.key));
      const addrStr = formatAddress(entry.address || entry.label || entry.key);
      if (addrStr) addressToPlace.set(addrStr.toLowerCase(), entry.key);
      if (entry.label) labelToPlace.set(String(entry.label).toLowerCase(), entry.key);
      labelToPlace.set(String(entry.key).toLowerCase(), entry.key);
    });
    driveEdges.forEach((edge) => {
      const originKey = edge.origin?.key || edge.fromKey || edge.origin || edge.from;
      const destKey = edge.destination?.key || edge.toKey || edge.destination || edge.to;
      const originLabel = edge.origin?.label;
      const destLabel = edge.destination?.label;
      if (originKey) labelToPlace.set(String(originKey).toLowerCase(), originKey);
      if (destKey) labelToPlace.set(String(destKey).toLowerCase(), destKey);
      if (originLabel && originKey) labelToPlace.set(String(originLabel).toLowerCase(), originKey);
      if (destLabel && destKey) labelToPlace.set(String(destLabel).toLowerCase(), destKey);
    });
    return { familyToPlace, teacherToPlace, addressToPlace, labelToPlace };
  }, [driveEdges, formatAddress, placeDirectory]);

  const resolveStudentPlace = React.useCallback(
    (student) => {
      const familyPlace = new Map();
      placeDirectory.forEach((entry) => {
        entry.familyIds?.forEach((fid) => familyPlace.set(String(fid), entry.key));
      });
      const fid = student.familyId;
      if (fid && familyPlace.has(String(fid))) return familyPlace.get(String(fid));
      const addr = formatAddress(student.familyAddress || student.address);
      if (addr && placeKeyLookup.addressToPlace.has(addr.toLowerCase())) {
        return placeKeyLookup.addressToPlace.get(addr.toLowerCase());
      }
      const label = (student.familyName || student.studentName || "").toLowerCase();
      if (label && placeKeyLookup.labelToPlace.has(label)) {
        return placeKeyLookup.labelToPlace.get(label);
      }
      return fid || student.familyName || student.studentId || student.studentName || null;
    },
    [formatAddress, placeDirectory, placeKeyLookup]
  );

  const updatePlacementField = React.useCallback(
    (matchKey, updater) => {
      if (!matchKey) return;
      setPlannedPlacements((prev) =>
        prev.map((p) => {
          const key = p.placementKey || p.id || p.studentId;
          if (String(key) !== String(matchKey)) return p;
          return updater(p);
        })
      );
    },
    []
  );

  const ensurePlaceForStudent = React.useCallback(
    async (student, resolvedKey) => {
      const schoolId = selectedSchoolId || selectedSchool;
      if (!schoolId || !student) return resolvedKey || null;
      const keyCandidates = [
        resolvedKey,
        student.familyId,
        student.id,
        student.studentId,
        student.familyName,
        student.studentName,
      ].filter(Boolean);
      const key = keyCandidates.length ? String(keyCandidates[0]) : null;
      if (!key) return null;
      const exists = placeDirectory.some(
        (pd) => String(pd.key).toLowerCase() === String(key).toLowerCase()
      );
      const address = student.familyAddress || student.address;
      const addressString = address ? formatAddress(address) : null;
      if (exists || !addressString) return key;
      const directory = {
        key,
        address: addressString,
        familyIds: student.familyId ? [student.familyId] : [],
        label: student.familyName || student.studentName || key,
      };
      try {
        await axios.post(
          `${baseUrl}/place-directories/bulk`,
          { schoolId, directories: [directory] },
          getRequestHeaders()
        );
        setPlaceDirectory((prev) => {
          const already = prev.some(
            (pd) => String(pd.key).toLowerCase() === String(key).toLowerCase()
          );
          return already ? prev : [...prev, directory];
        });
      } catch (err) {
        console.log(err);
      }
      return key;
    },
    [formatAddress, placeDirectory, selectedSchool, selectedSchoolId]
  );

  const ensureStudentPlaceForReassignment = React.useCallback(
    async (student) => {
      if (!student) return null;
      const existingKey = resolveStudentPlace(student);
      return ensurePlaceForStudent(student, existingKey);
    },
    [ensurePlaceForStudent, resolveStudentPlace]
  );

  const getFamilyIdForEvent = React.useCallback((event) => {
    if (!event) return null;
    return event.familyId || event.familyID || event.family?.familyId || event.family?.id || null;
  }, []);

  const getStudentIdForEvent = React.useCallback((event) => {
    if (!event) return null;
    return event.studentId || event.studentID || event.placementKey || event.id || null;
  }, []);

  const getFamilyInfo = React.useCallback(
    async (familyId) => {
      if (!familyId) return null;
      if (familyInfoCache[familyId]) return familyInfoCache[familyId];
      try {
        const res = await axios.get(`${baseUrl}/school-management/${familyId}`, getRequestHeaders());
        const data = res.data;
        setFamilyInfoCache((prev) => ({ ...prev, [familyId]: data }));
        return data;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    [familyInfoCache]
  );

  React.useEffect(() => {
    if (!showEventDriveModal || !eventModalData) {
      setEventModalFamily(null);
      setActiveMapMarkerId(null);
      setShowAllWeekMarkers(false);
      setShowEventMap(false);
      return;
    }
    setShowAllWeekMarkers(true);
    setShowEventMap(true);
    const familyId = getFamilyIdForEvent(eventModalData);
    if (!familyId) {
      setEventModalFamily(null);
      return;
    }
    let cancelled = false;
    getFamilyInfo(familyId).then((data) => {
      if (cancelled) return;
      setEventModalFamily(data || null);
      if (!data || !Array.isArray(data.students)) return;
      setEventModalData((prev) => {
        if (!prev) return prev;
        if (Array.isArray(prev.instruments) && prev.instruments.length) return prev;
        const studentId =
          prev.studentId || prev.studentID || prev.placementKey || prev.id || null;
        if (!studentId) return prev;
        const match = data.students.find(
          (stu) => String(stu.studentId || stu.id) === String(studentId)
        );
        if (!match) return prev;
        const instrumentList = Array.isArray(match.instrumentList) ? match.instrumentList : [];
        const instrumentLabel =
          match.instrument ||
          instrumentList?.[0]?.parentInstrument ||
          instrumentList?.[0]?.instrument ||
          instrumentList?.[0]?.name ||
          prev.instrument ||
          null;
        const nextLessonLength =
          Number(match.lessonLength) || prev.lessonLength || prev.rowLength || prev.lessonLength;
        const hasInstrumentUpdate = instrumentList.length > 0;
        const hasLessonUpdate = Number.isFinite(nextLessonLength) && nextLessonLength !== prev.lessonLength;
        if (!hasInstrumentUpdate && !hasLessonUpdate) return prev;
        const nextInstruments = hasInstrumentUpdate ? instrumentList : prev.instruments;
        return {
          ...prev,
          instruments: nextInstruments,
          instrument: hasInstrumentUpdate ? instrumentLabel : prev.instrument,
          lessonLength: hasLessonUpdate ? nextLessonLength : prev.lessonLength,
        };
      });
    });
    return () => {
      cancelled = true;
    };
  }, [eventModalData, getFamilyIdForEvent, getFamilyInfo, showEventDriveModal]);

  const normalizeInstrumentLabel = React.useCallback((inst) => {
    if (!inst) return null;
    if (typeof inst === "string") return inst.trim();
    return inst.parentInstrument || inst.instrument || inst.name || inst.label || null;
  }, []);

  const normalizeInstrumentList = React.useCallback(
    (list) => {
      if (!Array.isArray(list)) return [];
      const seen = new Set();
      const result = [];
      list.forEach((inst) => {
        const label = normalizeInstrumentLabel(inst);
        if (!label) return;
        const key = String(label).toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        if (inst && typeof inst === "object") {
          result.push({
            id: inst.id,
            name: inst.name || inst.instrument || inst.parentInstrument || label,
            uri: inst.uri,
            parentInstrument: inst.parentInstrument || inst.instrument || inst.name || label,
            instrumentGroup: inst.instrumentGroup,
          });
          return;
        }
        const catalogMatch = (allInstruments || []).find((entry) => {
          const entryLabel = normalizeInstrumentLabel(entry);
          return entryLabel && String(entryLabel).toLowerCase() === key;
        });
        if (catalogMatch) {
          result.push({
            id: catalogMatch.id,
            name: catalogMatch.name || catalogMatch.instrument || catalogMatch.parentInstrument || label,
            uri: catalogMatch.uri,
            parentInstrument:
              catalogMatch.parentInstrument || catalogMatch.instrument || catalogMatch.name || label,
            instrumentGroup: catalogMatch.instrumentGroup,
          });
          return;
        }
        result.push({
          id: undefined,
          name: label,
          uri: undefined,
          parentInstrument: label,
          instrumentGroup: undefined,
        });
      });
      return result;
    },
    [allInstruments, normalizeInstrumentLabel]
  );

  const applyFamilyUpdateToState = React.useCallback((updatedFamily) => {
    if (!updatedFamily?.familyId) return;
    setFamilyInfoCache((prev) => ({ ...prev, [updatedFamily.familyId]: updatedFamily }));
    setAllStudents((prev) =>
      prev.map((fam) => (String(fam.familyId) === String(updatedFamily.familyId) ? updatedFamily : fam))
    );
    setStudents((prev) =>
      prev.map((fam) => (String(fam.familyId) === String(updatedFamily.familyId) ? updatedFamily : fam))
    );
  }, []);

  const updateStudentInStudentTwo = React.useCallback(
    async ({ event, lessonLength, instruments }) => {
      const familyId = getFamilyIdForEvent(event);
      const studentId = getStudentIdForEvent(event);
      if (!familyId || !studentId) return;

      const familyFromCache = (allStudents.length ? allStudents : students).find(
        (f) => String(f.familyId) === String(familyId)
      );
      const family = familyFromCache || (await getFamilyInfo(familyId));
      if (!family || !Array.isArray(family.students)) return;

      let found = false;
      const nextStudents = family.students.map((stu) => {
        if (String(stu.studentId || stu.id) !== String(studentId)) return stu;
        found = true;
        const next = { ...stu };
        if (lessonLength !== undefined && Number.isFinite(Number(lessonLength))) {
          next.lessonLength = Number(lessonLength);
        }
        if (instruments !== undefined) {
          next.instrumentList = normalizeInstrumentList(instruments);
        }
        return next;
      });
      if (!found) return;

      const payload = { ...family, students: nextStudents };
      try {
        const res = await axios.put(
          `${baseUrl}/school-management-new/${familyId}`,
          payload,
          getRequestHeaders()
        );
        applyFamilyUpdateToState(res.data || payload);
      } catch (err) {
        console.log(err);
      }
    },
    [
      allStudents,
      applyFamilyUpdateToState,
      getFamilyIdForEvent,
      getFamilyInfo,
      getStudentIdForEvent,
      normalizeInstrumentList,
      students,
    ]
  );

  const handleModalRemoveInstrument = React.useCallback(
    (inst) => {
      if (!eventModalData) return;
      const matchKey = eventModalData.placementKey || eventModalData.id || eventModalData.studentId;
      const target = inst?.parentInstrument || inst?.instrument || inst?.name || inst;
      updatePlacementField(matchKey, (p) => {
        const filtered = (p.instruments || []).filter((i) => {
          const name = i?.parentInstrument || i?.instrument || i?.name || i;
          return String(name) !== String(target);
        });
        return { ...p, instruments: filtered };
      });
      setEventModalData((prev) => {
        if (!prev) return prev;
        const filtered = (prev.instruments || []).filter((i) => {
          const name = i?.parentInstrument || i?.instrument || i?.name || i;
          return String(name) !== String(target);
        });
        return { ...prev, instruments: filtered };
      });
      const nextInstruments = (eventModalData.instruments || []).filter((i) => {
        const name = i?.parentInstrument || i?.instrument || i?.name || i;
        return String(name) !== String(target);
      });
      updateStudentInStudentTwo({
        event: { ...eventModalData, instruments: nextInstruments },
        instruments: nextInstruments,
      });
      if (currentScheduleId) {
        saveSchedule();
      }
    },
    [currentScheduleId, eventModalData, saveSchedule, updatePlacementField, updateStudentInStudentTwo]
  );

  const handleStartNewScheduleClick = React.useCallback(() => {
    setCurrentScheduleId(null);
    setPlannedPlacements([]);
    setPlacedStudentIds([]);
    setLockedStudentIds([]);
    setManualReassignmentStudents([]);
    setOverrideReassignmentStudents(null);
    setScheduleName("");
    setShowLoadScheduleModal(false);
    setWelcomeStep("new");
  }, []);

  const handleModalLessonLengthChange = React.useCallback(
    (newLen) => {
      if (!eventModalData) return;
      const len = Number(newLen);
      if (!Number.isFinite(len) || len <= 0) return;
      const matchKey = eventModalData.placementKey || eventModalData.id || eventModalData.studentId;
      updatePlacementField(matchKey, (p) => ({ ...p, lessonLength: len }));
      setEventModalData((prev) => (prev ? { ...prev, lessonLength: len } : prev));
      updateStudentInStudentTwo({ event: { ...eventModalData, lessonLength: len }, lessonLength: len });
      if (currentScheduleId) {
        saveSchedule();
      }
    },
    [currentScheduleId, eventModalData, saveSchedule, updatePlacementField, updateStudentInStudentTwo]
  );

  const handleModalInstrumentChange = React.useCallback(
    (instrument) => {
      if (!eventModalData) return;
      const matchKey = eventModalData.placementKey || eventModalData.id || eventModalData.studentId;
      if (!instrument) return;
      updatePlacementField(matchKey, (p) => {
        const existing = p.instruments || [];
        if (existing.some((i) => (i.parentInstrument || i.instrument || i.name) === instrument)) {
          return { ...p, instrument };
        }
        return {
          ...p,
          instrument,
          instruments: [...existing, { parentInstrument: instrument, instrument }],
        };
      });
      setEventModalData((prev) => {
        if (!prev) return prev;
        const existing = prev.instruments || [];
        if (existing.some((i) => (i.parentInstrument || i.instrument || i.name) === instrument)) {
          return { ...prev, instrument };
        }
        return {
          ...prev,
          instrument,
          instruments: [...existing, { parentInstrument: instrument, instrument }],
        };
      });
      const existing = eventModalData.instruments || [];
      const nextInstruments = existing.some((i) => (i.parentInstrument || i.instrument || i.name) === instrument)
        ? existing
        : [...existing, { parentInstrument: instrument, instrument }];
      updateStudentInStudentTwo({
        event: { ...eventModalData, instruments: nextInstruments, instrument },
        instruments: nextInstruments,
      });
      if (currentScheduleId) {
        saveSchedule();
      }
    },
    [currentScheduleId, eventModalData, saveSchedule, updatePlacementField, updateStudentInStudentTwo]
  );

  const handleRemoveFromCalendar = React.useCallback(async () => {
    if (!eventModalData) return;
    const matchKey = eventModalData.placementKey || eventModalData.id || eventModalData.studentId;
    if (!matchKey) return;
    const familyId = getFamilyIdForEvent(eventModalData);
    const studentId = getStudentIdForEvent(eventModalData);
    const familyStudent =
      eventModalFamily?.students?.find(
        (stu) => String(stu.studentId || stu.id) === String(studentId)
      ) || null;
    const sid =
      eventModalData.placementKey ||
      (familyId && studentId ? `${familyId}-${studentId}` : studentId || eventModalData.id || eventModalData.studentName);
    if (!sid) return;

    setPlannedPlacements((prev) =>
      prev.filter((p) => (p.placementKey || p.id || p.studentId) !== matchKey)
    );

    const removeIds = [
      sid,
      matchKey,
      studentId,
      eventModalData.id,
      eventModalData.placementKey,
    ]
      .filter(Boolean)
      .map((id) => String(id));

    setPlacedStudentIds((prev) => prev.filter((id) => !removeIds.includes(String(id))));
    setLockedStudentIds((prev) => prev.filter((id) => !removeIds.includes(String(id))));
    setRemovedReassignmentIds((prev) => prev.filter((id) => String(id) !== String(sid)));

    const instruments = normalizeInstrumentList(
      familyStudent?.instrumentList ||
        eventModalData.instruments ||
        (eventModalData.instrument ? [eventModalData.instrument] : [])
    );

    const reassignmentStudent = {
      id: sid,
      familyId: familyId || eventModalData.familyId || eventModalData.familyID,
      familyName: eventModalFamily?.familyName || eventModalData.familyName,
      studentId: studentId || eventModalData.studentId,
      studentName: familyStudent?.studentName || eventModalData.studentName,
      instruments,
      lessonLength: familyStudent?.lessonLength || eventModalData.lessonLength,
      availability: familyStudent?.initialAvailability || eventModalData.availability,
      school: (eventModalFamily?.school || eventModalData.school || selectedSchool || "").toLowerCase(),
      familyAddress: eventModalFamily?.familyAddress || eventModalFamily?.address || eventModalData.familyAddress,
      teacherId: eventModalData.teacherId || eventModalData.teacherID || eventModalData.TeacherID,
    };

    if (Array.isArray(overrideReassignmentStudents)) {
      setOverrideReassignmentStudents((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        const exists = base.some((s) => String(s.id || s.studentId) === String(sid));
        return exists ? base : [...base, reassignmentStudent];
      });
    } else {
      setManualReassignmentStudents((prev) => {
        const exists = prev.some((s) => String(s.id || s.studentId) === String(sid));
        return exists ? prev : [...prev, reassignmentStudent];
      });
    }

    await ensureStudentPlaceForReassignment(reassignmentStudent);
    setEventModalData(null);
    setShowEventDriveModal(false);
  }, [
    ensureStudentPlaceForReassignment,
    eventModalData,
    eventModalFamily,
    getFamilyIdForEvent,
    getStudentIdForEvent,
    normalizeInstrumentList,
    overrideReassignmentStudents,
    selectedSchool,
  ]);

  const handleLoadExistingSchedule = React.useCallback(
    (schedule) => {
      if (!schedule) return;
      const targetWeek = startOfWeek(new Date(schedule.weekStart || new Date()), { weekStartsOn: 1 });
      const baseWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
      const diffWeeks = differenceInCalendarWeeks(targetWeek, baseWeek);
      setWeekOffset(diffWeeks);
      const hydratePlacement = (p) => {
        if (p.studentName || p.familyName) return p;
        let studentName = p.studentName || null;
        let familyName = p.familyName || null;
        const families = allStudents.length ? allStudents : students;
        const familyMatch = families.find((f) => String(f.familyId) === String(p.familyId));
        if (familyMatch) {
          familyName = familyMatch.familyName || familyName;
          const studentMatch =
            familyMatch.students?.find((s) => String(s.studentId) === String(p.studentId)) || null;
          if (studentMatch) {
            studentName = studentMatch.studentName || studentName;
          }
        } else {
          // fallback: search any family by studentId
          families.some((f) => {
            const sm = f.students?.find((s) => String(s.studentId) === String(p.studentId));
            if (sm) {
              familyName = f.familyName || familyName;
              studentName = sm.studentName || studentName;
              return true;
            }
            return false;
          });
        }
        return {
          ...p,
          studentName,
          familyName,
          label: studentName || familyName || p.label,
        };
      };
      const hydratedPlacements = (schedule.placements || []).map(hydratePlacement);
      setPlannedPlacements(hydratedPlacements);
      const placementIds = hydratedPlacements
        .map((p) => getStudentKey(p))
        .filter(Boolean);
      setPlacedStudentIds(Array.from(new Set(placementIds)));
      setLockedStudentIds(schedule.lockedStudentIds || []);
      setManualReassignmentStudents(schedule.manualReassignmentStudents || []);
      setOverrideReassignmentStudents(schedule.overrideReassignmentStudents || []);
      setScheduleName(schedule.label || "");
      setCurrentScheduleId(schedule._id || schedule.id || null);
      setShowLoadScheduleModal(false);
      setWelcomeStep(null);
      setScheduleLoadedAt(Date.now());
    },
    [allStudents, getStudentKey, students]
  );

  React.useEffect(() => {
    if (!plannedPlacements.length) return;
    const families = allStudents.length ? allStudents : students;
    if (!families.length) return;
    let changed = false;
    const hydrated = plannedPlacements.map((p) => {
      if (!p || (p.studentName && p.familyName)) return p;
      let studentName = p.studentName || null;
      let familyName = p.familyName || null;
      const familyMatch = families.find((f) => String(f.familyId) === String(p.familyId));
      if (familyMatch) {
        familyName = familyMatch.familyName || familyName;
        const studentMatch =
          familyMatch.students?.find((s) => String(s.studentId) === String(p.studentId)) || null;
        if (studentMatch) {
          studentName = studentMatch.studentName || studentName;
        }
      } else {
        families.some((f) => {
          const sm = f.students?.find((s) => String(s.studentId) === String(p.studentId));
          if (sm) {
            familyName = f.familyName || familyName;
            studentName = sm.studentName || studentName;
            return true;
          }
          return false;
        });
      }
      if (studentName !== p.studentName || familyName !== p.familyName) {
        changed = true;
        return { ...p, studentName, familyName, label: studentName || familyName || p.label };
      }
      return p;
    });
    if (changed) {
      setPlannedPlacements(hydrated);
    }
  }, [allStudents, plannedPlacements, students]);

  React.useEffect(() => {
    setSelectedStudentToAdd("");
    setStudentSearchTerm("");
    setOverrideReassignmentStudents(null);
  }, [selectedSchool]);

  // Load saved schedules for the selected school
  React.useEffect(() => {
    const schoolId = selectedSchoolId || selectedSchool;
    if (!schoolId) {
      setSavedSchedules([]);
      return;
    }
    setLoadingSchedules(true);
    console.log("Fetching schedules", { schoolId });
    axios
      .get(`${baseUrl}/schedules`, {
        params: { schoolId },
        ...getRequestHeaders(),
      })
      .then((res) => {
        const list = res.data?.schedules || res.data || [];
        const filtered = (Array.isArray(list) ? list : []).filter(
          (s) => String(s?.status || "").toLowerCase() !== "archived"
        );
        setSavedSchedules(filtered);
      })
      .catch(() => setSavedSchedules([]))
      .finally(() => setLoadingSchedules(false));
  }, [selectedSchool, selectedSchoolId]);

  const handleStartSchedulingWithoutTeacher = React.useCallback(async () => {
    if (!selectedSchool && !selectedSchoolId) return;
    const id = await createSchedule();
    if (id) setCurrentScheduleId(id);
    setWelcomeStep(null);
  }, [createSchedule, selectedSchool, selectedSchoolId]);

  const handleLoadTeacherStudentsAndStart = React.useCallback(async () => {
    if (!selectedTeacherId) {
      setWelcomeStep(null);
      return;
    }
    const sourceFamilies = allStudents.length ? allStudents : students;
    const additions = [];
    const seen = new Set();
    sourceFamilies.forEach((family) => {
      if (selectedSchool && family.school?.toLowerCase() !== selectedSchool) return;
      family.students?.forEach((student) => {
        const tid = getStudentTeacherId(student);
        if (String(tid) !== String(selectedTeacherId)) return;
        const status = student.studentStatus || student.status || student.Status;
        if (status && String(status).toLowerCase() !== "active") return;
        const uniqueId = `${family.familyId}-${student.studentId}`;
        if (seen.has(uniqueId) || placedStudentIds.includes(uniqueId)) return;
        additions.push({
          id: uniqueId,
          familyId: family.familyId,
          familyName: family.familyName,
          studentId: student.studentId,
          studentName: student.studentName,
          instruments: student.instrumentList || student.instruments || [],
          lessonLength: student.lessonLength,
          availability: student.initialAvailability,
          school: family.school?.toLowerCase(),
          familyAddress: family.familyAddress || family.address || student.address,
          teacherId: tid,
        });
        seen.add(uniqueId);
      });
    });
    if (additions.length) {
      await Promise.all(additions.map((s) => ensureStudentPlaceForReassignment(s)));
    }
    const scheduleId = await createSchedule({
      placements: plannedPlacements,
      lockedStudentIds,
      manualReassignmentStudents,
      overrideReassignmentStudents: additions,
    });
    if (scheduleId) setCurrentScheduleId(scheduleId);
    setManualReassignmentStudents([]);
    setOverrideReassignmentStudents(additions);
    setPromptReassignmentDriveTimes(true);
    setWelcomeStep(null);
  }, [
    allStudents,
    createSchedule,
    ensureStudentPlaceForReassignment,
    getStudentTeacherId,
    lockedStudentIds,
    manualReassignmentStudents,
    overrideReassignmentStudents,
    plannedPlacements,
    placedStudentIds,
    selectedSchool,
    selectedSchoolId,
    selectedTeacherId,
    setManualReassignmentStudents,
    students,
  ]);

  React.useEffect(() => {
    if (!currentScheduleId || welcomeStep) return;
    if (saveScheduleDebounceRef.current) {
      clearTimeout(saveScheduleDebounceRef.current);
    }
    saveScheduleDebounceRef.current = setTimeout(() => {
      saveSchedule();
    }, 600);
    return () => {
      if (saveScheduleDebounceRef.current) {
        clearTimeout(saveScheduleDebounceRef.current);
      }
    };
  }, [
    currentScheduleId,
    welcomeStep,
    plannedPlacements,
    lockedStudentIds,
    manualReassignmentStudents,
    overrideReassignmentStudents,
        startOfThisWeek,
        selectedSchool,
        selectedSchoolId,
        saveSchedule,
        scheduleName,
      ]);

  const parseEventDate = React.useCallback((ev) => {
    const candidates = [
      ev.dateTime,
      ev.dateTimeUtc,
      ev.DateTime,
      ev.DateTimeUtc,
      ev.date,
      ev.Date,
      ev.startDate,
      ev.startTime,
      ev.parsedDate,
      ev.start?.dateTime,
      ev.start?.date,
    ].filter(Boolean);
    for (const c of candidates) {
      if (c instanceof Date && !isNaN(c)) return c;
      if (typeof c === "number") {
        const dNum = new Date(c);
        if (dNum && !isNaN(dNum)) return dNum;
      }
      const d = new Date(c);
      if (d && !isNaN(d)) return d;
    }
    return null;
  }, []);

  const teacherNameById = React.useMemo(() => {
    const map = new Map();
    teachers.forEach((t) => {
      map.set(String(t.TeacherID), t.Name || t.name || "Teacher");
    });
    return map;
  }, [teachers]);

  const selectedTeacherInstruments = React.useMemo(() => {
    if (!selectedTeacherId) return [];
    const teacher = teachers.find((t) => String(t.TeacherID) === String(selectedTeacherId));
    if (!teacher?.InstrumentList) return [];
    return teacher.InstrumentList.map(
      (inst) => inst.instrument || inst.name || inst.label || inst.parentInstrument
    ).filter(Boolean);
  }, [selectedTeacherId, teachers]);

  const selectedTeacherName = React.useMemo(() => {
    if (!selectedTeacherId) return null;
    return teacherNameById.get(String(selectedTeacherId)) || null;
  }, [selectedTeacherId, teacherNameById]);

  const optimizationLedgerEntries = React.useMemo(() => {
    if (!plannedPlacements.length) return [];
    const entries = [];
    plannedPlacements.forEach((placement) => {
      if (!placement) return;
      const sid = getStudentKey({
        studentId: placement.studentId || placement.placementKey || placement.id,
        placementKey: placement.placementKey,
        id: placement.id,
        studentName: placement.studentName,
        familyName: placement.familyName,
      });
      const start = Number(placement.startMinutes) || 0;
      const timeLabel = `${Math.floor(start / 60).toString().padStart(2, "0")}:${(start % 60)
        .toString()
        .padStart(2, "0")}`;
      entries.push({
        student: placement.studentName || placement.familyName || sid,
        teacher: teacherNameById.get(String(placement.teacherId)) || placement.teacherId,
        day: weekDays[placement.dayIndex] || "Day",
        time: timeLabel,
      });
    });
    const dayOrder = new Map(weekDays.map((day, idx) => [day, idx]));
    entries.sort((a, b) => {
      const aDay = dayOrder.get(a.day) ?? 99;
      const bDay = dayOrder.get(b.day) ?? 99;
      if (aDay === bDay) return a.time.localeCompare(b.time);
      return aDay - bDay;
    });
    return entries;
  }, [getStudentKey, plannedPlacements, teacherNameById]);

  const availabilityByTeacher = React.useMemo(() => {
    const map = new Map();
    teachers.forEach((t) => {
      if (t.AvailabilityDetails) {
        map.set(String(t.TeacherID), normalizeAvailability(t.AvailabilityDetails));
      }
    });
    return map;
  }, [normalizeAvailability, teachers]);

  const placeKeyByTeacher = React.useMemo(() => {
    const map = new Map();
    placeDirectory.forEach((entry) => {
      if (Array.isArray(entry.teacherIds)) {
        entry.teacherIds.forEach((tid) => map.set(String(tid), entry.key));
      }
    });
    return map;
  }, [placeDirectory]);

  const computeDriveMinutesForPlacements = React.useCallback(
    (placements) => {
      if (!Array.isArray(placements) || !placements.length) return 0;
      const byTeacherDay = new Map();
      placements.forEach((placement) => {
        if (!placement) return;
        const sid = getStudentKey({
          studentId: placement.studentId || placement.placementKey || placement.id,
          placementKey: placement.placementKey,
          id: placement.id,
          studentName: placement.studentName,
          familyName: placement.familyName,
        });
        const tid = String(placement.teacherId || "");
        if (!tid) return;
        const place = resolveStudentPlace(placement);
        if (!place) return;
        const key = `${tid}|${placement.dayIndex}`;
        if (!byTeacherDay.has(key)) byTeacherDay.set(key, []);
        byTeacherDay.get(key).push({
          start: placement.startMinutes,
          end: placement.startMinutes + (placement.lessonLength || 30),
          place,
        });
      });
      let total = 0;
      byTeacherDay.forEach((events, key) => {
        const [tid] = key.split("|");
        const home = placeKeyByTeacher.get(String(tid)) || null;
        if (!events.length) return;
        events.sort((a, b) => a.start - b.start);
        const firstPlace = events[0].place || home;
        const lastPlace = events[events.length - 1].place || home;
        if (home && firstPlace) total += minutesBetween(home, firstPlace) || 0;
        for (let i = 0; i < events.length - 1; i++) {
          total += minutesBetween(events[i].place, events[i + 1].place) || 0;
        }
        if (home && lastPlace) total += minutesBetween(lastPlace, home) || 0;
      });
      return Math.round(total);
    },
    [getStudentKey, minutesBetween, placeKeyByTeacher, resolveStudentPlace]
  );

  const optimizationDriveMinutes = React.useMemo(
    () => computeDriveMinutesForPlacements(plannedPlacements),
    [computeDriveMinutesForPlacements, plannedPlacements]
  );

  const placeDirectoryByKey = React.useMemo(() => {
    const map = new Map();
    placeDirectory.forEach((entry) => {
      if (!entry?.key) return;
      const key = String(entry.key);
      map.set(key, entry);
      map.set(key.toLowerCase(), entry);
    });
    return map;
  }, [placeDirectory]);

  const eventModalLocation = React.useMemo(() => {
    if (!eventModalData) return null;
    const familyId = getFamilyIdForEvent(eventModalData);
    const directKey =
      eventModalData.placeKey || eventModalData.place_key || eventModalData.placeId || null;
    const resolvedKey = directKey || resolveStudentPlace(eventModalData);
    const keyLower = resolvedKey ? String(resolvedKey).toLowerCase() : null;

    let entry = null;
    if (resolvedKey) {
      entry = placeDirectory.find((pd) => String(pd.key) === String(resolvedKey));
    }
    if (!entry && keyLower) {
      entry = placeDirectory.find((pd) => String(pd.key || "").toLowerCase() === keyLower);
    }
    if (!entry && familyId) {
      entry = placeDirectory.find((pd) =>
        Array.isArray(pd.familyIds) && pd.familyIds.some((id) => String(id) === String(familyId))
      );
    }

    const lat = entry?.lat ?? entry?.location?.lat ?? eventModalData?.familyAddress?.lat ?? null;
    const lng = entry?.lng ?? entry?.location?.lng ?? eventModalData?.familyAddress?.lng ?? null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    return {
      lat: Number(lat),
      lng: Number(lng),
      label: entry?.label || eventModalData.studentName || eventModalData.familyName || "Student",
    };
  }, [eventModalData, getFamilyIdForEvent, placeDirectory, resolveStudentPlace]);

  const eventModalTeacherLocation = React.useMemo(() => {
    if (!eventModalData) return null;
    const teacherId =
      eventModalData.teacherId ||
      eventModalData.teacherID ||
      eventModalData.TeacherID ||
      null;
    if (!teacherId) return null;
    const placeKey = placeKeyByTeacher.get(String(teacherId));
    if (!placeKey) return null;
    const entry = placeDirectory.find((pd) => String(pd.key) === String(placeKey));
    const lat = entry?.lat ?? entry?.location?.lat ?? null;
    const lng = entry?.lng ?? entry?.location?.lng ?? null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return {
      lat: Number(lat),
      lng: Number(lng),
      label: entry?.label || "Teacher",
    };
  }, [eventModalData, placeDirectory, placeKeyByTeacher]);

  const resolveEventLocation = React.useCallback(
    (ev) => {
      if (!ev) return null;
      const fid = ev.familyId || ev.familyID;
      let placeKey = null;
      if (fid && placeKeyLookup.familyToPlace.has(String(fid))) {
        placeKey = placeKeyLookup.familyToPlace.get(String(fid));
      } else {
        const addr = formatAddress(ev.familyAddress || ev.address);
        if (addr && placeKeyLookup.addressToPlace.has(addr.toLowerCase())) {
          placeKey = placeKeyLookup.addressToPlace.get(addr.toLowerCase());
        } else {
          const label = (ev.familyName || ev.studentName || ev.label || addr || "").toLowerCase();
          if (label && placeKeyLookup.labelToPlace.has(label)) {
            placeKey = placeKeyLookup.labelToPlace.get(label);
          }
        }
      }
      const entry =
        (placeKey ? placeDirectoryByKey.get(String(placeKey)) : null) ||
        (placeKey ? placeDirectoryByKey.get(String(placeKey).toLowerCase()) : null);
      const lat =
        entry?.lat ??
        entry?.location?.lat ??
        ev?.familyAddress?.lat ??
        ev?.address?.lat ??
        null;
      const lng =
        entry?.lng ??
        entry?.location?.lng ??
        ev?.familyAddress?.lng ??
        ev?.address?.lng ??
        null;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return {
        lat: Number(lat),
        lng: Number(lng),
        key: entry?.key || placeKey || null,
        label: entry?.label || ev.studentName || ev.familyName || "Student",
      };
    },
    [formatAddress, placeDirectoryByKey, placeKeyLookup]
  );

  const mapMarkers = React.useMemo(() => {
    const MAX_MAP_MARKERS = 80;
    const markers = [];
    const usedKeys = new Set();
    const formatLessonTime = (ev) => {
      const candidates = [
        ev?.startDate,
        ev?.parsedDate,
        ev?.dateTime,
        ev?.date,
        ev?.start?.dateTime,
        ev?.start?.date,
      ].filter(Boolean);
      for (const c of candidates) {
        const d = c instanceof Date ? c : new Date(c);
        if (d && !isNaN(d)) return format(d, "EEEE h:mma");
      }
      if (Number.isFinite(ev?.dayIndex) && Number.isFinite(ev?.startMinutes)) {
        const h = Math.floor(ev.startMinutes / 60)
          .toString()
          .padStart(2, "0");
        const m = (ev.startMinutes % 60).toString().padStart(2, "0");
        const day = weekDays[ev.dayIndex] || "Day";
        return `${day} ${h}:${m}`;
      }
      return null;
    };
    if (eventModalLocation) {
      const familyName =
        eventModalFamily?.familyName ||
        eventModalData?.familyName ||
        eventModalData?.studentName ||
        "Family";
      const address = formatAddress(eventModalFamily?.address || eventModalData?.familyAddress) || "";
      const studentsCount = Array.isArray(eventModalFamily?.students) ? eventModalFamily.students.length : null;
      const instrumentNames = [];
      (eventModalFamily?.students || []).forEach((stu) => {
        (stu.instrumentList || []).forEach((inst) => {
          const name = inst?.name || inst?.parentInstrument || inst?.instrument;
          if (name && !instrumentNames.includes(name)) instrumentNames.push(name);
        });
      });
      markers.push({
        id: `family-${eventModalFamily?.familyId || eventModalData?.familyId || eventModalData?.studentId || "student"}`,
        name: familyName,
        address,
        students: studentsCount,
        inst: instrumentNames,
        location: { lat: eventModalLocation.lat, lng: eventModalLocation.lng },
        role: "selected",
        lessonTime: formatLessonTime(eventModalData),
      });
      usedKeys.add(
        String(eventModalLocation.key || eventModalData?.placeKey || eventModalData?.familyId || "").toLowerCase()
      );
    }

    if (showAllWeekMarkers) {
      const modalTeacherId =
        eventModalData?.teacherId ||
        eventModalData?.teacherID ||
        eventModalData?.TeacherID ||
        null;
      const otherEvents = weekViewEvents.filter((ev) => {
        const tid = ev.teacherId || ev.teacherID || ev.TeacherID;
        return modalTeacherId && String(tid) === String(modalTeacherId);
      });
      for (let idx = 0; idx < otherEvents.length; idx += 1) {
        if (markers.length >= MAX_MAP_MARKERS) break;
        const ev = otherEvents[idx];
        const isSameStudent =
          (eventModalData?.studentId && String(ev.studentId || ev.studentID) === String(eventModalData.studentId)) ||
          (eventModalData?.familyId && String(ev.familyId || ev.familyID) === String(eventModalData.familyId));
        if (isSameStudent) continue;
        const loc = resolveEventLocation(ev);
        if (!loc) continue;
        const dedupeKey = String(loc.key || `${loc.lat},${loc.lng}`).toLowerCase();
        if (usedKeys.has(dedupeKey)) continue;
        usedKeys.add(dedupeKey);
        const instLabel = ev.instrument || ev.lessonName || ev.name || null;
        const inst = instLabel ? [instLabel] : [];
        const address = formatAddress(ev.familyAddress || ev.address) || "";
        const timeKey = ev.startDate || ev.parsedDate || ev.dateTime || ev.date || "";
        markers.push({
          id: `event-${ev.studentId || ev.id || "student"}-${timeKey || idx}-${idx}`,
          name: ev.familyName || ev.studentName || "Student",
          address,
          students: null,
          inst,
          location: { lat: loc.lat, lng: loc.lng },
          role: "other",
          lessonTime: formatLessonTime(ev),
        });
      }
    }

    if (eventModalTeacherLocation) {
      const teacherId =
        eventModalData?.teacherId ||
        eventModalData?.teacherID ||
        eventModalData?.TeacherID ||
        null;
      const teacher = teachers.find((t) => String(t.TeacherID) === String(teacherId));
      const instrumentNames = [];
      (teacher?.InstrumentList || []).forEach((inst) => {
        const name = inst?.parentInstrument || inst?.instrument || inst?.name;
        if (name && !instrumentNames.includes(name)) instrumentNames.push(name);
      });
      markers.push({
        id: `teacher-${teacherId || "teacher"}`,
        name: teacherNameById.get(String(teacherId)) || "Teacher",
        address: eventModalTeacherLocation.label || "",
        students: null,
        inst: instrumentNames,
        location: { lat: eventModalTeacherLocation.lat, lng: eventModalTeacherLocation.lng },
        role: "teacher",
        lessonTime: null,
      });
    }
    return markers;
  }, [
    eventModalData,
    eventModalFamily,
    eventModalLocation,
    eventModalTeacherLocation,
    formatAddress,
    resolveEventLocation,
    teacherNameById,
    teachers,
    weekViewEvents,
  ]);

  const instrumentsForModal = React.useMemo(() => {
    if (eventModalData?.instruments?.length) return eventModalData.instruments;
    const studentId =
      eventModalData?.studentId || eventModalData?.studentID || eventModalData?.placementKey || eventModalData?.id || null;
    if (!studentId || !eventModalFamily?.students) return [];
    const match = eventModalFamily.students.find(
      (stu) => String(stu.studentId || stu.id) === String(studentId)
    );
    return Array.isArray(match?.instrumentList) ? match.instrumentList : [];
  }, [eventModalData, eventModalFamily]);

  const driveEdgesForPlaceDirectory = React.useMemo(() => {
    if (!eventModalData || !driveEdges.length) return [];
    const directKey =
      eventModalData.placeKey || eventModalData.place_key || eventModalData.placeId || null;
    const resolvedKey = directKey || resolveStudentPlace(eventModalData);
    if (!resolvedKey) return [];
    const targetKey = String(resolvedKey);
    const labelByKey = new Map();
    placeDirectory.forEach((pd) => {
      if (!pd?.key) return;
      labelByKey.set(String(pd.key), pd.label || pd.key);
    });
    return driveEdges
      .filter((edge) => {
        const originKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
        const destKey = edge.destination?.key || edge.destination || edge.toKey || edge.to;
        return String(originKey) === targetKey || String(destKey) === targetKey;
      })
      .map((edge, idx) => {
        const originKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from || "";
        const destKey = edge.destination?.key || edge.destination || edge.toKey || edge.to || "";
        return {
          id: edge.id || edge._id || `${originKey}=>${destKey}-${idx}`,
          fromLabel: edge.origin?.label || labelByKey.get(String(originKey)) || originKey,
          toLabel: edge.destination?.label || labelByKey.get(String(destKey)) || destKey,
          minutes: edge.minutes,
        };
      })
      .sort((a, b) => {
        const ma = Number(a.minutes);
        const mb = Number(b.minutes);
        if (Number.isFinite(ma) && Number.isFinite(mb)) return ma - mb;
        if (Number.isFinite(ma)) return -1;
        if (Number.isFinite(mb)) return 1;
        return 0;
      });
  }, [driveEdges, eventModalData, placeDirectory, resolveStudentPlace]);


  const activeMapMarker = React.useMemo(
    () => mapMarkers.find((marker) => marker.id === activeMapMarkerId) || null,
    [activeMapMarkerId, mapMarkers]
  );

  const teacherOptions = React.useMemo(() => {
    // Active teachers for scheduling
    if (!selectedSchool) return [];
    return teachers
      .filter(
        (teacher) =>
          teacher.School?.toLowerCase() === selectedSchool &&
          String(teacher.Status || "").toLowerCase() === "active"
      )
      .map((teacher) => ({
        value: teacher.TeacherID,
        label: teacher.Name,
      }));
  }, [teachers, selectedSchool]);

  const teacherOptionsAll = React.useMemo(() => {
    // All teachers who have taught at the school (any status)
    if (!selectedSchool) return [];
    const list = teachers
      .filter((teacher) => teacher.School?.toLowerCase() === selectedSchool)
      .map((teacher) => ({
        value: teacher.TeacherID,
        label: teacher.Name,
      }));
    list.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    return list;
  }, [teachers, selectedSchool]);

  const teacherIdsForCalendar = React.useMemo(() => {
    const ids = new Set();
    teacherOptions.forEach((t) => {
      if (t.value) ids.add(String(t.value));
    });
    return Array.from(ids);
  }, [teacherOptions]);

  // Fetch next-month calendar events for all teachers in the selected school
  React.useEffect(() => {
    if (!selectedSchoolId || !teacherIdsForCalendar.length) {
      setTeacherCalendarEvents([]);
      setCalendarEventsLoaded(false);
      if (openCalendarModalAfterFetch) {
        setOpenCalendarModalAfterFetch(false);
      }
      return;
    }
    setCalendarEventsLoaded(false);
    Promise.all(
      teacherIdsForCalendar.map((tid) =>
        axios
          .get(`${baseUrl}/calendar/teacher/${tid}`, getRequestHeaders())
          .then((res) => (Array.isArray(res.data) ? res.data : []))
          .catch(() => [])
      )
    ).then((all) => {
      const merged = all.flat();
      setTeacherCalendarEvents(merged);
      setCalendarEventsLoaded(true);
      if (openCalendarModalAfterFetch) {
        setOpenCalendarModalAfterFetch(false);
      }
    });
  }, [openCalendarModalAfterFetch, parseEventDate, selectedSchoolId, teacherIdsForCalendar]);

  // Refresh place directory when modal opens to ensure badges are current
  React.useEffect(() => {
    if (!showCalendarEventsModal || !selectedSchoolId) return;
    axios
      .get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders())
      .then((res) => {
        const payload = res.data;
        if (payload && Array.isArray(payload.directories)) {
          setPlaceDirectory(payload.directories);
        } else if (Array.isArray(payload)) {
          setPlaceDirectory(payload);
        } else {
          setPlaceDirectory([]);
        }
      })
      .catch(() => {});
  }, [selectedSchoolId, setPlaceDirectory, showCalendarEventsModal]);

  // Fetch drive edges for this school on selection change and seed matrix
  React.useEffect(() => {
    if (!selectedSchoolId) return;
    axios
      .get(`${baseUrl}/drive-edges/${selectedSchoolId}`, getRequestHeaders())
      .then((res) => {
        const payload = res.data;
        const edges = payload?.edges || (Array.isArray(payload) ? payload : []);
        setDriveEdges(edges);
        seedEdges(edges);
      })
      .catch(() => {
        setDriveEdges([]);
      });
  }, [seedEdges, selectedSchoolId]);

  React.useEffect(() => {
    if (!selectedTeacherId) {
      setCalendarEvents([]);
      setCalendarDaysWithEvents({});
      setWeekViewEvents([]);
      setCalendarEventsLoadedForTeacher(false);
      return;
    }
    setLoading(true);
    setCalendarEventsLoadedForTeacher(false);
    axios
      .get(`${baseUrl}/calendar/teacher/${selectedTeacherId}`, getRequestHeaders())
      .then((res) => {
        setCalendarEvents(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.log(err);
        setCalendarEvents([]);
      })
      .finally(() => {
        setLoading(false);
        setCalendarEventsLoadedForTeacher(true);
      });
  }, [selectedTeacherId]);

  React.useEffect(() => {
    if (!calendarEvents.length) {
      setCalendarDaysWithEvents({});
      setWeekViewEvents([]);
      return;
    }
    createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
      displayedWeek,
      calendarEvents,
      setCalendarDaysWithEvents
    );
  }, [calendarEvents, displayedWeek]);

  React.useEffect(() => {
    if (!Object.keys(calendarDaysWithEvents).length) {
      setWeekViewEvents([]);
      return;
    }
    createEventObjectForTeacherWeekView(
      displayedWeek,
      calendarDaysWithEvents,
      setWeekViewEvents
    );
  }, [calendarDaysWithEvents, displayedWeek]);

  const positionedEventsByDay = React.useMemo(() => {
    const grouped = {};
    weekDays.forEach((day) => (grouped[day] = []));

    const getLastName = (event) => {
      if (event.familyName) return event.familyName;
      if (event.studentName && event.studentName.includes(" ")) {
        const parts = event.studentName.trim().split(" ");
        return parts[parts.length - 1];
      }
      return event.studentName || "Lesson";
    };

    const buildTooltip = (event, start) => {
      const instrument =
        event.instrument ||
        event.lessonName ||
        event.name ||
        "Instrument N/A";
      const length = event.lessonLength || event.rowLength * SLOT_MINUTES || 30;
      const addr = event.familyAddress || {};
      const addressParts = [
        addr.addressLine1,
        addr.addressLine2,
        addr.city,
        addr.state,
        addr.zipcode,
      ].filter(Boolean);
      const address = addressParts.join(", ") || "Address N/A";
      const fullName = event.studentName || "Student";
      return `${fullName}\n${instrument}\n${length} mins\n${address}`;
    };

    const mapWeekViewEvent = (event) => {
      const start = event.startDate || event.parsedDate || new Date(event.dateTime);
      if (!start || isNaN(start)) return null;
      const dayName = format(start, "EEEE");
      const minutesFromStart = start.getHours() * 60 + start.getMinutes() - timeWindow.startMinutes;
      if (minutesFromStart < 0 || minutesFromStart > timeWindow.totalMinutes) return null;
      const durationMinutes =
        event.lessonLength ||
        (event.rowLength ? event.rowLength * 5 : null) ||
        30;
      const top = (minutesFromStart / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const height = (durationMinutes / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const startMinutesOfDay = start.getHours() * 60 + start.getMinutes();
      return {
        ...event,
        planned: false,
        dayName,
        top,
        height,
        startMinutesOfDay,
        endMinutesOfDay: startMinutesOfDay + durationMinutes,
        label: getLastName(event),
        tooltip: buildTooltip(event, start),
        timeLabel: format(start, "h:mma"),
      };
    };

    weekViewEvents
      .filter((event) => !event.isPendingOffer)
      .forEach((event) => {
        const positioned = mapWeekViewEvent(event);
        if (positioned && grouped[positioned.dayName]) grouped[positioned.dayName].push(positioned);
      });

    plannedPlacements.forEach((placement) => {
      if (placement.teacherId !== selectedTeacherId) return;
      const targetDate = new Date(displayedWeek[placement.dayIndex]);
      targetDate.setHours(Math.floor(placement.startMinutes / 60));
      targetDate.setMinutes(placement.startMinutes % 60);
      targetDate.setSeconds(0);
      const dayName = format(targetDate, "EEEE");
      const minutesFromStart =
        (targetDate.getHours() * 60 + targetDate.getMinutes()) - timeWindow.startMinutes;
      if (minutesFromStart < 0 || minutesFromStart > timeWindow.totalMinutes) return;
      const top = (minutesFromStart / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const height = ((placement.lessonLength || 30) / SLOT_MINUTES) * PIXELS_PER_SLOT;
      const startMinutesOfDay = targetDate.getHours() * 60 + targetDate.getMinutes();
      grouped[dayName].push({
        ...placement,
        date: targetDate,
        planned: true,
        dayName,
        top,
        height,
        startMinutesOfDay,
        endMinutesOfDay: startMinutesOfDay + (placement.lessonLength || 30),
        label: getLastName(placement),
        tooltip: buildTooltip(placement, targetDate),
        timeLabel: format(targetDate, "h:mma"),
      });
    });

    Object.keys(grouped).forEach((day) => {
      grouped[day].sort((a, b) => a.top - b.top);
    });
    return grouped;
  }, [weekViewEvents, plannedPlacements, selectedTeacherId, timeWindow]);

  const getDriveKey = React.useCallback(
    (event) => {
      const fid = event.familyId || event.familyID || event.FamilyID;
      if (fid && placeKeyLookup.familyToPlace.has(String(fid))) {
        return placeKeyLookup.familyToPlace.get(String(fid));
      }
      const tid = event.teacherId || event.teacherID || event.TeacherID;
      if (tid && placeKeyLookup.teacherToPlace.has(String(tid))) {
        return placeKeyLookup.teacherToPlace.get(String(tid));
      }
      const addr = formatAddress(event.familyAddress || event.address);
      if (addr && placeKeyLookup.addressToPlace.has(addr.toLowerCase())) {
        return placeKeyLookup.addressToPlace.get(addr.toLowerCase());
      }
      const label = (event.familyName || event.studentName || event.label || addr || "").toLowerCase();
      if (label && placeKeyLookup.labelToPlace.has(label)) {
        return placeKeyLookup.labelToPlace.get(label);
      }
      return (
        fid ||
        tid ||
        event.studentId ||
        event.familyName ||
        event.studentName ||
        event.label
      );
    },
    [formatAddress, placeKeyLookup]
  );

  const driveSegmentsByDay = React.useMemo(() => {
    const res = {};
    weekDays.forEach((d) => (res[d] = []));
    weekDays.forEach((day) => {
      const events = (positionedEventsByDay[day] || []).slice().sort((a, b) => a.startMinutesOfDay - b.startMinutesOfDay);

      // Between consecutive lessons
      for (let i = 0; i < events.length - 1; i++) {
        const current = events[i];
        const next = events[i + 1];
        // Skip drive time when consecutive events belong to the same family
        if (
          current.familyId &&
          next.familyId &&
          String(current.familyId) === String(next.familyId)
        ) {
          continue;
        }
        const minutes = minutesBetween(getDriveKey(current), getDriveKey(next));
        const top = (current.top + current.height + next.top) / 2;
        const prevEnd =
          current.endMinutesOfDay != null
            ? current.endMinutesOfDay
            : current.startMinutesOfDay + (current.lessonLength || 30);
        const nextStart = next.startMinutesOfDay;
        res[day].push({ minutes, top, prevEnd, nextStart });
      }

      // Home -> first lesson and last lesson -> home (per availability block)
      const teacherHome = placeKeyByTeacher.get(String(selectedTeacherId));
      if (teacherHome && selectedTeacherId) {
        const dayBlocks = availabilityByDay[day] || [];
        dayBlocks.forEach((block) => {
          const inBlock = events.filter(
            (ev) => ev.startMinutesOfDay >= block.start && ev.startMinutesOfDay < block.end
          );
          if (!inBlock.length) return;
          const first = inBlock[0];
          const last = inBlock[inBlock.length - 1];

          const homeToFirst = minutesBetween(teacherHome, getDriveKey(first));
          if (Number.isFinite(homeToFirst)) {
            const mid = (block.start + first.startMinutesOfDay) / 2;
            const top = ((mid - timeWindow.startMinutes) / SLOT_MINUTES) * PIXELS_PER_SLOT;
            if (top >= 0 && top <= timeWindow.totalHeightPx) {
              res[day].push({
                minutes: homeToFirst,
                top,
                prevEnd: block.start,
                nextStart: first.startMinutesOfDay,
              });
            }
          }

          const lastToHome = minutesBetween(getDriveKey(last), teacherHome);
          if (Number.isFinite(lastToHome)) {
            const mid = (last.endMinutesOfDay + block.end) / 2;
            const top = ((mid - timeWindow.startMinutes) / SLOT_MINUTES) * PIXELS_PER_SLOT;
            if (top >= 0 && top <= timeWindow.totalHeightPx) {
              const lastEnd =
                last.endMinutesOfDay != null
                  ? last.endMinutesOfDay
                  : last.startMinutesOfDay + (last.lessonLength || 30);
              res[day].push({
                minutes: lastToHome,
                top,
                prevEnd: lastEnd,
                nextStart: block.end,
              });
            }
          }
        });
      }
    });
    return res;
  }, [availabilityByDay, getDriveKey, minutesBetween, placeKeyByTeacher, positionedEventsByDay, selectedTeacherId, timeWindow]);

  const driveSegmentsWithWarn = React.useMemo(() => {
    const availMap = availabilityByDay;
    const result = {};
    weekDays.forEach((day) => {
      const blocks = availMap[day] || [];
      const earliest = blocks.length ? Math.min(...blocks.map((b) => b.start)) : null;
      result[day] = (driveSegmentsByDay[day] || []).map((seg) => {
        const gap = seg.nextStart != null && seg.prevEnd != null ? seg.nextStart - seg.prevEnd : null;
        let warn = false;
        if (Number.isFinite(seg.minutes)) {
          if (gap != null && gap < seg.minutes) warn = true;
          if (seg.prevEnd == null && earliest != null && seg.nextStart != null) {
            if (seg.nextStart - earliest < seg.minutes) warn = true;
          }
        }
        return { ...seg, warn, gap };
      });
    });
    return result;
  }, [availabilityByDay, driveSegmentsByDay]);

  const totalDriveMinutes = React.useMemo(() => {
    if (!selectedTeacherId) return null;
    let sum = 0;
    weekDays.forEach((day) => {
      (driveSegmentsWithWarn[day] || []).forEach((seg) => {
        if (Number.isFinite(seg.minutes)) sum += seg.minutes;
      });
    });
    return sum;
  }, [driveSegmentsWithWarn, selectedTeacherId]);

  const totalAvailabilityMinutes = React.useMemo(() => {
    if (!selectedTeacherId) return null;
    let sum = 0;
    weekDays.forEach((day) => {
      const blocks = availabilityByDay[day] || [];
      blocks.forEach((b) => {
        if (b.start != null && b.end != null && b.end > b.start) {
          sum += b.end - b.start;
        }
      });
    });
    return sum;
  }, [availabilityByDay, selectedTeacherId]);

  const totalTeachingMinutes = React.useMemo(() => {
    if (!selectedTeacherId) return null;
    let sum = 0;
    weekDays.forEach((day) => {
      (positionedEventsByDay[day] || []).forEach((ev) => {
        if (ev.endMinutesOfDay != null && ev.startMinutesOfDay != null) {
          sum += ev.endMinutesOfDay - ev.startMinutesOfDay;
        } else if (ev.lessonLength) {
          sum += ev.lessonLength;
        }
      });
    });
    return sum;
  }, [positionedEventsByDay, selectedTeacherId]);

  // Helper: get day index within displayedWeek by date
  const getDayIndexForDate = React.useCallback(
    (d) => {
      if (!d || isNaN(d)) return -1;
      for (let i = 0; i < displayedWeek.length; i++) {
        const dayDate = displayedWeek[i];
        if (format(d, "yyyy-MM-dd") === format(dayDate, "yyyy-MM-dd")) {
          return i;
        }
      }
      return -1;
    },
    [displayedWeek]
  );

  const ensureTransparentImg = () => {
    if (transparentDragImg.current) return transparentDragImg.current;
    const img = new Image();
    img.src =
      "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxJyBoZWlnaHQ9JzEnPjwvc3ZnPg==";
    transparentDragImg.current = img;
    return img;
  };

  const dropdownTeachersForModal = React.useMemo(() => {
    const directoryTeacherIds = new Set();
    const directoryAddresses = new Set();
    placeDirectory.forEach((entry) => {
      entry.teacherIds?.forEach((tid) => directoryTeacherIds.add(String(tid)));
      if (entry.address) directoryAddresses.add(String(entry.address).toLowerCase());
      if (entry.label) directoryAddresses.add(String(entry.label).toLowerCase());
      if (entry.key) directoryAddresses.add(String(entry.key).toLowerCase());
    });

    const optionIds = new Set(teacherOptions.map((t) => String(t.value)));
    const list = [];
    teachers
      .filter(
        (t) =>
          optionIds.has(String(t.TeacherID)) &&
          t.School?.toLowerCase() === selectedSchool
      )
      .forEach((t) => {
        const id = String(t.TeacherID);
        const address = formatAddress(
          t.Address ||
            t.address ||
            t.HomeAddress || {
              addressLine1: t.Address1,
              addressLine2: t.Address2,
              city: t.City,
              state: t.State,
              zipcode: t.Zip,
            }
        );
        list.push({
          id: `teacher-${id}`,
          teacherId: id,
          teacherName: t.Name || "Teacher",
          address,
          hasDirectory:
            directoryTeacherIds.has(id) ||
            (address && directoryAddresses.has(address.toLowerCase())),
        });
      });
    return list;
  }, [formatAddress, placeDirectory, selectedSchool, teacherOptions, teachers]);

  // Recurring-aware calendar events for dropdown teachers within the displayed week
  const weeklyTeacherEventsForModal = React.useMemo(() => {
    if (!selectedSchool || !startOfThisWeek) return [];
    const ids = dropdownTeachersForModal
      .map((t) => String(t.teacherId || t.TeacherID || t.id))
      .filter(Boolean);
    if (!ids.length) return [];
    const events = getTeacherEventsForWeek(teacherCalendarEvents || [], ids, startOfThisWeek);
    return events.map((ev, idx) => {
      const dt = ev.dateTime ? new Date(ev.dateTime) : parseEventDate(ev);
      return { ev, date: dt, idx };
    });
  }, [dropdownTeachersForModal, selectedSchool, startOfThisWeek, teacherCalendarEvents, parseEventDate]);

  const missingPlaceSummary = React.useMemo(() => {
    const missingTeachers = dropdownTeachersForModal.filter((t) => !t.hasDirectory);
    const missingStudents = studentsNeedingReassignment.filter((student) => {
      const key = resolveStudentPlace(student);
      if (!key) return true;
      const k = String(key);
      return !placeDirectoryByKey.has(k) && !placeDirectoryByKey.has(k.toLowerCase());
    });

    const missingEventFamilies = new Set();
    weeklyTeacherEventsForModal.forEach(({ ev }) => {
      const fid = ev?.familyId || ev?.familyID;
      if (fid && missingStudents.some((s) => String(s.familyId) === String(fid))) return;
      if (!resolveEventLocation(ev)) {
        if (fid) missingEventFamilies.add(String(fid));
      }
    });

    const totalMissing =
      missingTeachers.length + missingStudents.length + missingEventFamilies.size;

    return {
      missingTeachers,
      missingStudents,
      missingEventFamilies,
      totalMissing,
    };
  }, [
    dropdownTeachersForModal,
    placeDirectoryByKey,
    resolveEventLocation,
    resolveStudentPlace,
    studentsNeedingReassignment,
    weeklyTeacherEventsForModal,
  ]);

  React.useEffect(() => {
    if (!scheduleLoadedAt) return;
    if (missingPlaceSummary.totalMissing > 0) {
      setShowMissingPlaceModal(true);
    }
  }, [missingPlaceSummary.totalMissing, scheduleLoadedAt]);


  // ----- Debug helper: slice open time into lesson-sized slots (no drive logic) -----
  const candidateSlotsByTeacher = React.useMemo(() => {
    if (!selectedSchool) return new Map();
    const slotLength = 30; // minutes
    const step = SLOT_MINUTES;

    const candidateIds = teacherOptions
      .map((t) => String(t.value))
      .filter(Boolean)
      .filter((tid) =>
        teachers.some(
          (teacher) =>
            String(teacher.TeacherID) === tid &&
            teacher.School?.toLowerCase() === selectedSchool
        )
      );

    const busyMap = new Map();
    const addBusy = (tid, dayIndex, start, end, place) => {
      if (dayIndex < 0 || dayIndex > 6) return;
      if (!busyMap.has(tid)) busyMap.set(tid, {});
      if (!busyMap.get(tid)[dayIndex]) busyMap.get(tid)[dayIndex] = [];
      busyMap.get(tid)[dayIndex].push({ start, end, place });
    };

    const collect = (ev, explicitTid) => {
      const tid = explicitTid || String(ev.teacherId || ev.teacherID || ev.TeacherID || "");
      if (!candidateIds.includes(tid)) return;
      const d = parseEventDate(ev);
      if (!d) return;
      let dayIndex = getDayIndexForDate(d);
      if (dayIndex < 0 || dayIndex > 6) {
        const name = format(d, "EEEE");
        const fallbackIdx = weekDays.indexOf(name);
        dayIndex = fallbackIdx;
      }
      if (dayIndex < 0 || dayIndex > 6) return;
      const start = d.getHours() * 60 + d.getMinutes();
      const len =
        Number(ev.lessonLength) && Number(ev.lessonLength) > 0
          ? Number(ev.lessonLength)
          : ev.rowLength
          ? ev.rowLength * SLOT_MINUTES
          : 60;
      addBusy(tid, dayIndex, start, start + len, getDriveKey(ev));
    };

    // Recurring-aware events for this week
    weeklyTeacherEventsForModal.forEach(({ ev, date }) => {
      const tid = String(ev.teacherId || ev.teacherID || ev.TeacherID || "");
      if (!candidateIds.includes(tid)) return;
      const d = date || parseEventDate(ev);
      if (!d) return;
      let dayIndex = getDayIndexForDate(d);
      if (dayIndex < 0 || dayIndex > 6) {
        const name = format(d, "EEEE");
        const fallbackIdx = weekDays.indexOf(name);
        dayIndex = fallbackIdx;
      }
      if (dayIndex < 0 || dayIndex > 6) return;
      const start = d.getHours() * 60 + d.getMinutes();
      const len =
        Number(ev.lessonLength) && Number(ev.lessonLength) > 0
          ? Number(ev.lessonLength)
          : ev.rowLength
          ? ev.rowLength * SLOT_MINUTES
          : 60;
      addBusy(tid, dayIndex, start, start + len, getDriveKey(ev));
    });
    teacherCalendarEvents.forEach((ev) => collect(ev));
    calendarEvents.forEach((ev) => collect(ev, selectedTeacherId ? String(selectedTeacherId) : ""));
    weekViewEvents.forEach((ev) => collect(ev, ev.teacherId || selectedTeacherId));
    plannedPlacements.forEach((p) => {
      const tid = String(p.teacherId || "");
      if (!candidateIds.includes(tid)) return;
      addBusy(tid, p.dayIndex, p.startMinutes, p.startMinutes + (p.lessonLength || 30), getDriveKey(p));
    });
    // Also account for currently positioned events in the displayed week for the selected teacher
    if (selectedTeacherId && candidateIds.includes(String(selectedTeacherId))) {
      weekDays.forEach((day, idx) => {
        (positionedEventsByDay[day] || []).forEach((ev) => {
          if (ev.startMinutesOfDay != null && ev.endMinutesOfDay != null) {
            addBusy(String(selectedTeacherId), idx, ev.startMinutesOfDay, ev.endMinutesOfDay, getDriveKey(ev));
          }
        });
      });
    }

    candidateIds.forEach((tid) => {
      const dayMap = busyMap.get(tid) || {};
      Object.keys(dayMap).forEach((k) => {
        dayMap[k].sort((a, b) => a.start - b.start);
      });
      busyMap.set(tid, dayMap);
    });

    const slotsByTeacher = new Map();
    candidateIds.forEach((tid) => {
      const teacher = teachers.find((t) => String(t.TeacherID) === String(tid));
      const availMap =
        availabilityByTeacher.get(String(tid)) ||
        (teacher?.AvailabilityDetails ? normalizeAvailability(teacher.AvailabilityDetails) : {});
      const dayBusy = busyMap.get(tid) || {};
      const slotByDay = {};

      weekDays.forEach((_, dayIdx) => {
        const dayName = weekDays[dayIdx];
        const availBlocks = availMap[dayName] || availMap[dayIdx] || [];
        const busyList = (dayBusy[dayIdx] || []).slice().sort((a, b) => a.start - b.start);
        const free = [];

        availBlocks.forEach((block) => {
          let cursor = block.start;
          busyList.forEach((b) => {
            if (b.end <= block.start || b.start >= block.end) return;
            if (b.start > cursor) free.push({ start: cursor, end: Math.min(b.start, block.end) });
            cursor = Math.max(cursor, b.end);
          });
          if (cursor < block.end) free.push({ start: cursor, end: block.end });
        });

        const slots = [];
        const home = placeKeyByTeacher.get(String(tid)) || null;
        free.forEach((f) => {
          for (let s = f.start; s + slotLength <= f.end; s += step) {
            const e = s + slotLength;
            // find prev/next events with place context
            const prev =
              busyList
                .filter((b) => b.end <= s)
                .sort((a, b) => b.end - a.end)[0] || { end: f.start, place: home };
            const next =
              busyList
                .filter((b) => b.start >= e)
                .sort((a, b) => a.start - b.start)[0] || { start: f.end, place: home };
            slots.push({
              start: s,
              end: e,
              prevPlace: prev.place || home,
              nextPlace: next.place || home,
            });
          }
        });
        slotByDay[dayIdx] = slots;
      });

      slotsByTeacher.set(tid, slotByDay);
    });

    return slotsByTeacher;
  }, [
    availabilityByTeacher,
    calendarEvents,
    parseEventDate,
    plannedPlacements,
    selectedSchool,
    selectedTeacherId,
    startOfThisWeek,
    teacherCalendarEvents,
    teacherOptions,
    teachers,
    weekViewEvents,
    weeklyTeacherEventsForModal,
    getDayIndexForDate,
  ]);

  // Student options built from candidate slots with drive deltas
  const studentSlotOptions = React.useMemo(() => {
    if (!selectedSchool || !candidateSlotsByTeacher.size) return [];
    const travel = (fromKey, toKey) => {
      if (!fromKey || !toKey) return Infinity;
      if (fromKey === toKey) return 0;
      const m = minutesBetween(fromKey, toKey);
      return Number.isFinite(m) ? m : Infinity;
    };

    return studentsNeedingReassignment
      .filter((s) => !selectedSchool || s.school === selectedSchool)
      .map((student) => {
        const studentPlace = resolveStudentPlace(student);
        const lessonLength = student.lessonLength || 30;
        const options = [];
        if (!studentPlace) return { student, options };

        candidateSlotsByTeacher.forEach((dayMap, tid) => {
          if (!tid) return;
          // instrument check
          const teacher = teachers.find((t) => String(t.TeacherID) === String(tid));
          const studentInst =
            student.instruments?.[0]?.parentInstrument || student.instruments?.[0]?.instrument;
          if (studentInst && teacher?.InstrumentList && !teacher.InstrumentList.some((inst) => inst.parentInstrument === studentInst || inst.instrument === studentInst)) {
            return;
          }

          Object.keys(dayMap || {}).forEach((k) => {
            const dayIdx = Number(k);
            (dayMap?.[dayIdx] || []).forEach((slot, idx) => {
              // compute extra drive
              const prevKey = slot.prevPlace || null;
              const nextKey = slot.nextPlace || null;
              const driveIn = travel(prevKey, studentPlace);
              const driveOut = travel(studentPlace, nextKey);
              const direct = travel(prevKey, nextKey);
              if (!Number.isFinite(driveIn) || !Number.isFinite(driveOut)) return;
              const slotId = `${tid}-${dayIdx}-${slot.start}-${idx}`;
              options.push({
                teacherId: tid,
                slotId,
                dayIndex: dayIdx,
                startMinutes: slot.start,
                endMinutes: slot.end,
                lessonLength,
                extraDriveMinutes: driveIn + driveOut - (Number.isFinite(direct) ? direct : 0),
                prevPlace: prevKey,
                nextPlace: nextKey,
              });
            });
          });
        });

        options.sort((a, b) => (a.score ?? a.extraDriveMinutes) - (b.score ?? b.extraDriveMinutes));
        return { student, options };
      });
  }, [candidateSlotsByTeacher, minutesBetween, resolveStudentPlace, selectedSchool, studentsNeedingReassignment, teachers, maxDriveMinutes]);

  const onEventClick = React.useCallback(
    (event) => {
      const eventKey = getDriveKey(event);
      if (!eventKey) return;

      const labelByKey = new Map();
      placeDirectory.forEach((pd) => {
        const label = pd.label || pd.key || pd.address || "Place";
        if (pd.key) labelByKey.set(pd.key, label);
      });

      // Build set of place keys for reassigned students
      const reassignmentKeys = new Set();
      studentsNeedingReassignment.forEach((s) => {
        const fid = s.familyId ? String(s.familyId) : null;
        if (fid && placeKeyLookup.familyToPlace.has(fid)) {
          reassignmentKeys.add(placeKeyLookup.familyToPlace.get(fid));
        }
        const fallback = s.familyName || s.studentName || fid;
        if (fallback) reassignmentKeys.add(String(fallback));
      });

      const edges = (driveEdges || []).filter((edge) => {
        const originKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
        const destKey = edge.destination?.key || edge.destination || edge.toKey || edge.to;
        if (!originKey || !destKey) return false;
        const connectsEvent = originKey === eventKey || destKey === eventKey;
        const connectsReassign =
          reassignmentKeys.has(originKey) || reassignmentKeys.has(destKey);
        return connectsEvent && connectsReassign;
      });

      edges.sort((a, b) => {
        const ma = Number(a.minutes);
        const mb = Number(b.minutes);
        if (Number.isFinite(ma) && Number.isFinite(mb)) return ma - mb;
        if (Number.isFinite(ma)) return -1;
        if (Number.isFinite(mb)) return 1;
        return 0;
      });

      const enriched = edges.map((edge, idx) => {
        const originKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from || "";
        const destKey = edge.destination?.key || edge.destination || edge.toKey || edge.to || "";
        return {
          id: edge.id || edge._id || `${originKey}=>${destKey}-${idx}`,
          fromLabel: edge.origin?.label || labelByKey.get(originKey) || originKey,
          toLabel: edge.destination?.label || labelByKey.get(destKey) || destKey,
          minutes: edge.minutes,
        };
      });

      setEventDriveEdges(enriched);
      setEventDriveTitle(
        `${event.studentName || event.familyName || event.label || "Lesson"} ↔ reassigned students`
      );
      setEventModalData(event);
      setShowEventDriveModal(true);
    },
    [driveEdges, getDriveKey, placeDirectory, placeKeyLookup.familyToPlace, plannedPlacements, studentsNeedingReassignment]
  );

  const optimizeSchedule = React.useCallback(() => {
    console.log("Optimize button clicked");
    if (!selectedSchool) return;

    // 1. Candidate teachers for the selected school
    const candidateTeachers = teacherOptions
      .map((t) => String(t.value))
      .filter(Boolean)
      .filter((tid) =>
        teachers.some(
          (teacher) =>
            String(teacher.TeacherID) === tid &&
            teacher.School?.toLowerCase() === selectedSchool
        )
      );
    if (!candidateTeachers.length) return;

    // Helpers
    const teacherHomeById = new Map();
    placeDirectory.forEach((entry) => {
      entry.teacherIds?.forEach((tid) => teacherHomeById.set(String(tid), entry.key));
    });

  const getAvailForTeacher = (tid) => {
    const avail = availabilityByTeacher.get(String(tid)) || {};
    const map = {};
    weekDays.forEach((dayName, idx) => {
      map[idx] = (avail[dayName] || []).map((b) => ({
          start: b.start,
          end: b.end,
        }));
      });
    return map;
  };

  const isExceptionDay = (d, ev) => {
    if (!ev?.recurringExceptions) return false;
    return ev.recurringExceptions.some((skip) => {
      const skipDate = new Date(skip);
      return isSameDay(skipDate, d);
    });
  };

  const expandEventIntoDisplayedWeek = (ev) => {
    const baseDate = parseEventDate(ev);
    if (!baseDate || isNaN(baseDate)) return [];
    const occurrences = [];
    const freq = ev.recurring?.frequency;
    const isRecurring = ev.recurring?.isOn;
    const endDate = ev.recurringEndDate ? new Date(ev.recurringEndDate) : null;

    displayedWeek.forEach((day) => {
      if (endDate && day > endDate) return;
      if (isSameDay(day, baseDate)) {
        occurrences.push(day);
        return;
      }
      if (!isRecurring) return;
      if (day.getDay() !== baseDate.getDay()) return;
      if (isExceptionDay(day, ev)) return;
      const diffWeeks = differenceInCalendarWeeks(day, baseDate);
      if (freq === "weekly" && diffWeeks >= 0) {
        occurrences.push(day);
      } else if (freq === "every-other-week" && diffWeeks >= 0 && diffWeeks % 2 === 0) {
        occurrences.push(day);
      }
    });
    return occurrences;
  };

    const travelMinutes = (fromKey, toKey, fallbackFrom, fallbackTo) => {
      const startKey = fromKey || fallbackFrom;
      const endKey = toKey || fallbackTo;
      if (!startKey || !endKey) return 0;
      if (startKey === endKey) return 0;
      const m = minutesBetween(startKey, endKey);
      return Number.isFinite(m) ? m : 0;
    };
    const maxDriveCap =
      maxDriveMinutes && Number.isFinite(Number(maxDriveMinutes))
        ? Number(maxDriveMinutes)
        : null;
    const paddingCap =
      paddingMinutes && Number.isFinite(Number(paddingMinutes))
        ? Number(paddingMinutes)
        : 0;

    // Build busy map (existing lessons + planned placements)
    const busyByTeacher = new Map();
    const addBusy = (tid, dayIndex, start, end, place) => {
      if (dayIndex < 0 || dayIndex > 6) return;
      if (!busyByTeacher.has(tid)) busyByTeacher.set(tid, {});
      if (!busyByTeacher.get(tid)[dayIndex]) busyByTeacher.get(tid)[dayIndex] = [];
      busyByTeacher.get(tid)[dayIndex].push({ start, end, place });
    };

    const addEventBusy = (ev, explicitTeacherId) => {
      const tid = explicitTeacherId || String(ev.teacherId || ev.teacherID || ev.TeacherID || "");
      if (!tid) return;
      const d = parseEventDate(ev);
      if (!d) return;
      let dayIndex = getDayIndexForDate(d);
      if (dayIndex < 0 || dayIndex > 6) {
        const name = format(d, "EEEE");
        const fallbackIdx = weekDays.indexOf(name);
        dayIndex = fallbackIdx;
      }
      if (dayIndex < 0 || dayIndex > 6) return;
      const start = d.getHours() * 60 + d.getMinutes();
      const len =
        Number(ev.lessonLength) && Number(ev.lessonLength) > 0
          ? Number(ev.lessonLength)
          : ev.rowLength
          ? ev.rowLength * SLOT_MINUTES
          : 60;
      const end = start + len;
      addBusy(tid, dayIndex, start, end, getDriveKey(ev));
    };

    const weeklyTeacherEvents = getTeacherEventsForWeek(
      teacherCalendarEvents,
      candidateTeachers,
      startOfThisWeek
    );
    (weeklyTeacherEvents || []).forEach((ev) => {
      const d = parseEventDate(ev);
      if (!d) return;
      const dayIndex = getDayIndexForDate(d);
      if (dayIndex < 0 || dayIndex > 6) return;
      const start = d.getHours() * 60 + d.getMinutes();
      const len =
        Number(ev.lessonLength) && Number(ev.lessonLength) > 0
          ? Number(ev.lessonLength)
          : ev.rowLength
          ? ev.rowLength * SLOT_MINUTES
          : 60;
      addBusy(
        String(ev.teacherId || ev.teacherID || ev.TeacherID || ev.teacher?.id || ""),
        dayIndex,
        start,
        start + len,
        getDriveKey(ev)
      );
    });
    calendarEvents.forEach((ev) => addEventBusy(ev, selectedTeacherId ? String(selectedTeacherId) : ""));
    weekViewEvents.forEach((ev) => addEventBusy(ev, ev.teacherId || selectedTeacherId));
    // also include currently positioned events in the displayed week grid
    weekDays.forEach((day, idx) => {
      (positionedEventsByDay[day] || []).forEach((ev) => {
        const tid = String(ev.teacherId || ev.teacherID || ev.TeacherID || selectedTeacherId || "");
        if (!tid) return;
        const start = ev.startMinutesOfDay ?? (ev.startDate ? (ev.startDate.getHours() * 60 + ev.startDate.getMinutes()) : null);
        const end = ev.endMinutesOfDay ?? (start != null ? start + (ev.lessonLength || 30) : null);
        if (start == null || end == null) return;
        addBusy(tid, idx, start, end, getDriveKey(ev));
      });
    });
    const lockedPlacementSet = new Set(lockedStudentIds.map(String));
    const isPlacementLocked = (pl) => lockedPlacementSet.has(getStudentKey(pl));

    plannedPlacements.forEach((p) => {
      if (!isPlacementLocked(p)) return;
      const tid = String(p.teacherId || "");
      if (!candidateTeachers.includes(tid)) return;
      addBusy(tid, p.dayIndex, p.startMinutes, p.startMinutes + (p.lessonLength || 30), getDriveKey(p));
    });

    const canTeach = (tid, student) => {
      const teacher = teachers.find((t) => String(t.TeacherID) === String(tid));
      if (!teacher?.InstrumentList) return false;
      const studentInst =
        student.instruments?.[0]?.parentInstrument || student.instruments?.[0]?.instrument;
      if (!studentInst) return false;
      return teacher.InstrumentList.some(
        (inst) => inst.parentInstrument === studentInst || inst.instrument === studentInst
      );
    };

    // Helper to compute options for a single student given current busy timelines
    const computeOptionsForStudent = (student) => {
      const studentPlace = resolveStudentPlace(student);
      if (!studentPlace) return [];
      const lessonLength = student.lessonLength || 30;
      const opts = [];

      candidateTeachers.forEach((tid) => {
        if (!canTeach(tid, student)) return;
        const home = teacherHomeById.get(String(tid)) || placeKeyByTeacher.get(String(tid)) || null;
        const availMap = getAvailForTeacher(tid);
        const dayBusyMap = busyByTeacher.get(tid) || {};

        weekDays.forEach((dayName, dayIdx) => {
          const availBlocks = availMap[dayName] || availMap[dayIdx] || [];
          const busyList = (dayBusyMap[dayIdx] || []).slice().sort((a, b) => a.start - b.start);

          availBlocks.forEach((block) => {
            // Build prev/next with pseudo home events at availability bounds
            const pseudoPrev = { end: block.start, place: home };
            const pseudoNext = { start: block.end, place: home };

            // iterate candidate starts within block in SLOT_MINUTES increments
            for (let start = block.start; start + lessonLength <= block.end; start += SLOT_MINUTES) {
              const end = start + lessonLength;

              // Hard overlap check against existing events on that day
              const overlapsExisting = busyList.some(
                (b) => !(b.end <= start || b.start >= end)
              );
              if (overlapsExisting) continue;

              // find prev event
              const prevEvent =
                busyList
                  .filter((b) => b.end <= start)
                  .sort((a, b) => b.end - a.end)[0] || pseudoPrev;

              // find next event
              const nextEvent =
                busyList
                  .filter((b) => b.start >= end)
                  .sort((a, b) => a.start - b.start)[0] || pseudoNext;

              const prevPlace = prevEvent.place || home;
              const nextPlace = nextEvent.place || home;

              const drivePrevToStudent = travelMinutes(prevPlace, studentPlace, home, studentPlace);
              const driveStudentToNext = travelMinutes(studentPlace, nextPlace, studentPlace, home);
              const directPrevToNext = travelMinutes(prevPlace, nextPlace, home, home);

              if (!Number.isFinite(drivePrevToStudent) || !Number.isFinite(driveStudentToNext)) {
                continue;
              }

              // Gap penalty: prefer slots adjacent to existing lessons, penalize large idle windows
              const gapBefore = Math.max(0, start - (prevEvent.end ?? start));
              const gapAfter = Math.max(0, (nextEvent.start ?? end) - end);
              const localGap = gapBefore + gapAfter;

              const feasible =
                prevEvent.end + drivePrevToStudent + paddingCap <= start &&
                end + driveStudentToNext + paddingCap <= nextEvent.start;

              if (!feasible) continue;

              if (maxDriveCap != null) {
                if (drivePrevToStudent > maxDriveCap || driveStudentToNext > maxDriveCap) continue;
              }

              const extraDrive = drivePrevToStudent + driveStudentToNext - directPrevToNext;
              const score = extraDrive + localGap; // combined cost: drive + idle gap

              opts.push({
                teacherId: tid,
                dayIndex: dayIdx,
                startMinutes: start,
                endMinutes: end,
                extraDriveMinutes: extraDrive,
                score,
                prevPlace,
                nextPlace,
                studentPlace,
              });
            }
          });
        });
      });
      return opts;
    };

    // Initial ordering by constrainedness using initial snapshot
    // Build pool: unplaced reassign students (excluding locked) + currently placed reassignments (non-locked)
    const lockedSet = new Set(lockedStudentIds.map(String));
    const isStudentLocked = (s) => lockedSet.has(getStudentKey(s));
    const handledStudents = new Set();

    const pool = [];
    const seenPool = new Set();
    studentsNeedingReassignment.forEach((s) => {
      if (isStudentLocked(s)) return;
      const key = getStudentKey(s);
      if (seenPool.has(key)) return;
      seenPool.add(key);
      pool.push(s);
    });
    plannedPlacements.forEach((p) => {
      if (selectedSchool && p.school && p.school !== selectedSchool) return;
      // Skip locked ones from pool so they remain fixed
      if (isPlacementLocked(p)) return;
      const sid = getStudentKey({
        studentId: p.studentId || p.placementKey || p.id,
        placementKey: p.placementKey,
        id: p.id,
        studentName: p.studentName,
        familyName: p.familyName,
      });
      if (seenPool.has(sid)) return;
      seenPool.add(sid);
      pool.push({
        id: sid,
        familyId: p.familyId,
        familyName: p.familyName,
        studentId: p.studentId || p.placementKey || p.id || sid,
        studentName: p.studentName,
        instruments: p.instrument ? [{ parentInstrument: p.instrument, instrument: p.instrument }] : [],
        lessonLength: p.lessonLength,
        availability: p.availability,
        school: selectedSchool,
      });
    });

    const initialOptionsCount = pool.map((student) => {
      if (selectedSchool && student.school !== selectedSchool) return { student, count: Infinity };
      // skip if already bundled
      if (handledStudents.has(student.id || student.studentId)) return { student, count: Infinity };
      const opts = computeOptionsForStudent(student);
      return { student, count: opts.length };
    });
    initialOptionsCount.sort((a, b) => a.count - b.count);
    const sortedStudents = initialOptionsCount.map((entry) => entry.student).filter(Boolean);

    const newPlacements = [];
    const newPlacedIds = [];
    const ledger = [];
    const takenSlots = new Set(); // ensure we never reuse the same slot
    const assignedStudents = new Set();

    sortedStudents.forEach((student) => {
      const sid = getStudentKey(student);
      if (assignedStudents.has(sid)) return;
      if (lockedStudentIds.includes(sid)) return;
      if (selectedSchool && student.school !== selectedSchool) return;
      const options = computeOptionsForStudent(student).filter(
        (opt) => !takenSlots.has(`${opt.teacherId}|${opt.dayIndex}|${opt.startMinutes}`)
      );
      if (!options.length) return;
      options.sort((a, b) => (a.score ?? a.extraDriveMinutes) - (b.score ?? b.extraDriveMinutes));
      const choice = options[0];
      takenSlots.add(`${choice.teacherId}|${choice.dayIndex}|${choice.startMinutes}`);
      assignedStudents.add(sid);

      const placement = {
        id: `opt-${getStudentKey(student)}-${choice.dayIndex}-${choice.startMinutes}`,
        placementKey: student.id || student.studentId || getStudentKey(student),
        studentId: student.studentId || student.id || getStudentKey(student),
        familyId: student.familyId,
        studentName: student.studentName,
        familyName: student.familyName,
        dayIndex: choice.dayIndex,
        teacherId: choice.teacherId,
        startMinutes: choice.startMinutes,
        lessonLength: student.lessonLength || 30,
        instrument:
          student.instruments?.[0]?.parentInstrument ||
          student.instruments?.[0]?.instrument ||
          null,
        familyAddress: student.familyAddress || null,
      };

      newPlacements.push(placement);
      newPlacedIds.push(student.id);

      ledger.push({
        student: placement.studentName,
        teacher: teacherNameById.get(String(placement.teacherId)) || placement.teacherId,
        day: weekDays[placement.dayIndex],
        time: `${Math.floor(placement.startMinutes / 60)
          .toString()
          .padStart(2, "0")}:${(placement.startMinutes % 60).toString().padStart(2, "0")}`,
      });

      // Update busy timeline for the chosen teacher before next student
      const tKey = String(choice.teacherId);
      const tMap = busyByTeacher.get(tKey) || {};
      tMap[choice.dayIndex] = (tMap[choice.dayIndex] || []).concat([
        {
          start: choice.startMinutes,
          end: choice.endMinutes,
          place: choice.studentPlace,
        },
      ]).sort((a, b) => a.start - b.start);
      busyByTeacher.set(tKey, tMap);
    });

    // Optional local improvement: try one beneficial swap based on drive delta
    if (newPlacements.length > 1) {
      const studentById = new Map();
      studentsNeedingReassignment.forEach((s) =>
        studentById.set(String(s.id || s.studentId || s.studentName), s)
      );

      const placementPlace = (pl) => {
        const student = studentById.get(String(pl.placementKey)) || {};
        return resolveStudentPlace(student);
      };

      const computeCostForTeacherDay = (tid, dayIdx, extraPlacements = []) => {
        const dayEvents = ((busyByTeacher.get(String(tid)) || {})[dayIdx] || []).slice();
        extraPlacements.forEach((pl) => {
          dayEvents.push({
            start: pl.startMinutes,
            end: pl.startMinutes + (pl.lessonLength || 30),
            place: placementPlace(pl),
          });
        });
        if (!dayEvents.length) return 0;
        dayEvents.sort((a, b) => a.start - b.start);
        const home = placeKeyByTeacher.get(String(tid)) || null;
        let cost = 0;
        const firstPlace = dayEvents[0].place || home;
        const lastPlace = dayEvents[dayEvents.length - 1].place || home;
        const firstStart = dayEvents[0].start;
        const lastEnd = dayEvents[dayEvents.length - 1].end;
        // home -> first
        cost += minutesBetween(home, firstPlace) || 0;
        // between events
        for (let i = 0; i < dayEvents.length - 1; i++) {
          const a = dayEvents[i];
          const b = dayEvents[i + 1];
          cost += minutesBetween(a.place || home, b.place || home) || 0;
        }
        // last -> home
        cost += minutesBetween(lastPlace, home) || 0;
        // lightly penalize if overlaps (shouldn't)
        for (let i = 0; i < dayEvents.length - 1; i++) {
          if (dayEvents[i].end > dayEvents[i + 1].start) cost += 1e6;
        }
        // keep in window
        const availMap = getAvailForTeacher(String(tid));
        const blocks = availMap[weekDays[dayIdx]] || availMap[dayIdx] || [];
        if (blocks.length) {
          const window = blocks.reduce(
            (acc, b) => ({
              start: Math.min(acc.start, b.start),
              end: Math.max(acc.end, b.end),
            }),
            { start: blocks[0].start, end: blocks[0].end }
          );
          if (firstStart < window.start || lastEnd > window.end) cost += 1e5;
        }
        return cost;
      };

      const trySwap = () => {
        let improved = false;
        for (let i = 0; i < newPlacements.length; i++) {
          for (let j = i + 1; j < newPlacements.length; j++) {
            const A = newPlacements[i];
            const B = newPlacements[j];
            const studentA = studentById.get(String(A.placementKey)) || {};
            const studentB = studentById.get(String(B.placementKey)) || {};
            // can both teachers teach swapped students?
            if (
              !canTeach(String(B.teacherId), studentA) ||
              !canTeach(String(A.teacherId), studentB)
            ) {
              continue;
            }

            const originalCost =
              computeCostForTeacherDay(A.teacherId, A.dayIndex, [A]) +
              computeCostForTeacherDay(B.teacherId, B.dayIndex, [B]);

            const swappedA = { ...A, teacherId: B.teacherId, dayIndex: B.dayIndex, startMinutes: B.startMinutes };
            const swappedB = { ...B, teacherId: A.teacherId, dayIndex: A.dayIndex, startMinutes: A.startMinutes };

            const swappedCost =
              computeCostForTeacherDay(swappedA.teacherId, swappedA.dayIndex, [swappedA]) +
              computeCostForTeacherDay(swappedB.teacherId, swappedB.dayIndex, [swappedB]);

            if (swappedCost + 0.1 < originalCost) {
              newPlacements[i] = swappedA;
              newPlacements[j] = swappedB;
              improved = true;
              break;
            }
          }
          if (improved) break;
        }
      };

      trySwap();
    }

    const lockedPlacements = plannedPlacements.filter((p) => isPlacementLocked(p));
    const finalPlacements = [...lockedPlacements, ...newPlacements];

    if (finalPlacements.length) {
      const normalizePlacements = (placements) => {
        const map = new Map();
        placements.forEach((p) => {
          if (!p) return;
          const key = getStudentKey({
            studentId: p.studentId || p.placementKey || p.id,
            placementKey: p.placementKey,
            id: p.id,
            studentName: p.studentName,
            familyName: p.familyName,
          });
          if (!key) return;
          map.set(
            String(key),
            `${p.teacherId || ""}|${p.dayIndex ?? ""}|${p.startMinutes ?? ""}`
          );
        });
        return map;
      };
      const currentMap = normalizePlacements(plannedPlacements);
      const candidateMap = normalizePlacements(finalPlacements);
      let samePlacements = currentMap.size === candidateMap.size;
      if (samePlacements) {
        for (const [key, val] of currentMap.entries()) {
          if (candidateMap.get(key) !== val) {
            samePlacements = false;
            break;
          }
        }
      }
      const currentDrive = computeDriveMinutesForPlacements(plannedPlacements);
      const candidateDrive = computeDriveMinutesForPlacements(finalPlacements);
      if (plannedPlacements.length && candidateDrive > currentDrive && samePlacements) {
        return;
      }
      setPlannedPlacements(finalPlacements);
      const lockedIdsFromPlacements = lockedPlacements.map((p) => getStudentKey(p));
      setPlacedStudentIds([...lockedIdsFromPlacements, ...newPlacedIds]);
      setOptimizeLedger(ledger);
    }
  }, [
    availabilityByTeacher,
    calendarEvents,
    formatAddress,
    getDriveKey,
    getTeacherEventsForWeek,
    minutesBetween,
    placeDirectory,
    placeKeyByTeacher,
    placeKeyLookup,
    plannedPlacements,
    computeDriveMinutesForPlacements,
    selectedSchool,
    selectedTeacherId,
    startOfThisWeek,
    studentsNeedingReassignment,
    teacherCalendarEvents,
    teacherNameById,
    teacherOptions,
    teachers,
    weekViewEvents,
    resolveStudentPlace,
    lockedStudentIds,
    positionedEventsByDay,
    weekDays,
    displayedWeek,
    maxDriveMinutes,
    paddingMinutes,
  ]);

  // Track missing drive edges only between consecutive lessons for the selected teacher.
  React.useEffect(() => {
    if (!selectedSchool || !selectedTeacherId) {
      setMissingDrivePairs([]);
      setMissingTeacherDrivePairs([]);
      setShowMissingTeacherDriveModal(false);
      return;
    }
    const labelByKey = new Map();
    placeDirectory.forEach((pd) => {
      if (!pd?.key) return;
      labelByKey.set(String(pd.key), pd.label || pd.key);
    });
    const missing = [];
    const teacherHomeKey = placeKeyByTeacher.get(String(selectedTeacherId)) || null;
    const teacherHomeLabel = selectedTeacherName ? `${selectedTeacherName} (Home)` : "Teacher Home";
    weekDays.forEach((day) => {
      const events = (positionedEventsByDay[day] || []).slice();
      if (!events.length) return;
      events.sort((a, b) => a.startMinutesOfDay - b.startMinutesOfDay);
      if (teacherHomeKey) {
        const firstEvent = events[0];
        const lastEvent = events[events.length - 1];
        const firstKey = getDriveKey(firstEvent);
        const lastKey = getDriveKey(lastEvent);
        if (firstKey && String(firstKey) !== String(teacherHomeKey)) {
          const hasMinutes = minutesMap.has(makeDriveKey(teacherHomeKey, firstKey));
          if (!hasMinutes) {
            missing.push({
              id: `${day}-home-first-${teacherHomeKey}-${firstKey}`,
              day,
              time: `Home → ${firstEvent.timeLabel}`,
              fromKey: teacherHomeKey,
              toKey: firstKey,
              fromLabel: teacherHomeLabel,
              toLabel:
                labelByKey.get(String(firstKey)) ||
                firstEvent.familyName ||
                firstEvent.studentName ||
                firstEvent.label ||
                firstKey,
            });
          }
        }
        if (lastKey && String(lastKey) !== String(teacherHomeKey)) {
          const hasMinutes = minutesMap.has(makeDriveKey(lastKey, teacherHomeKey));
          if (!hasMinutes) {
            missing.push({
              id: `${day}-last-home-${lastKey}-${teacherHomeKey}`,
              day,
              time: `${lastEvent.timeLabel} → Home`,
              fromKey: lastKey,
              toKey: teacherHomeKey,
              fromLabel:
                labelByKey.get(String(lastKey)) ||
                lastEvent.familyName ||
                lastEvent.studentName ||
                lastEvent.label ||
                lastKey,
              toLabel: teacherHomeLabel,
            });
          }
        }
      }
      if (events.length > 1) {
        for (let i = 0; i < events.length - 1; i++) {
          const fromEvent = events[i];
          const toEvent = events[i + 1];
          const fromKey = getDriveKey(fromEvent);
          const toKey = getDriveKey(toEvent);
          if (!fromKey || !toKey || String(fromKey) === String(toKey)) continue;
          const hasMinutes = minutesMap.has(makeDriveKey(fromKey, toKey));
          if (hasMinutes) continue;
          missing.push({
            id: `${day}-${i}-${fromKey}-${toKey}`,
            day,
            time: `${fromEvent.timeLabel} → ${toEvent.timeLabel}`,
            fromKey,
            toKey,
            fromLabel:
              labelByKey.get(String(fromKey)) ||
              fromEvent.familyName ||
              fromEvent.studentName ||
              fromEvent.label ||
              fromKey,
            toLabel:
              labelByKey.get(String(toKey)) ||
              toEvent.familyName ||
              toEvent.studentName ||
              toEvent.label ||
              toKey,
          });
        }
      }
    });
    setMissingDrivePairs(missing);
    setMissingTeacherDrivePairs(missing);
    const prev = lastMissingTeacherRef.current;
    const shouldOpen =
      missing.length > 0 &&
      (String(prev.teacherId) !== String(selectedTeacherId) || prev.count === 0);
    if (shouldOpen) {
      setShowMissingTeacherDriveModal(true);
    }
    lastMissingTeacherRef.current = { teacherId: selectedTeacherId, count: missing.length };
  }, [
    getDriveKey,
    makeDriveKey,
    minutesMap,
    placeKeyByTeacher,
    placeDirectory,
    positionedEventsByDay,
    selectedSchool,
    selectedTeacherId,
    selectedTeacherName,
  ]);

  const buildMissingReassignmentDrivePairs = React.useCallback(() => {
    if (!studentsNeedingReassignment.length) return [];
    const studentKeys = [];
    studentsNeedingReassignment.forEach((student) => {
      const key = resolveStudentPlace(student);
      if (key) studentKeys.push(String(key));
    });
    const targetKeys = [];
    dropdownTeachersForModal.forEach((teacher) => {
      const tid = String(teacher.teacherId || teacher.TeacherID || teacher.id || "");
      if (!tid) return;
      const homeKey = placeKeyByTeacher.get(tid);
      if (homeKey) targetKeys.push(String(homeKey));
    });
    weeklyTeacherEventsForModal.forEach(({ ev }) => {
      if (ev?.isPendingOffer) return;
      const loc = resolveEventLocation(ev);
      const key = loc?.key;
      if (key) targetKeys.push(String(key));
    });
    const uniqueStudentKeys = Array.from(new Set(studentKeys));
    const uniqueTargetKeys = Array.from(new Set(targetKeys));
    const missing = [];
    const seen = new Set();
    uniqueStudentKeys.forEach((studentKey) => {
      uniqueTargetKeys.forEach((targetKey) => {
        if (String(studentKey) === String(targetKey)) return;
        const forwardKey = makeDriveKey(studentKey, targetKey);
        if (!minutesMap.has(forwardKey)) {
          const pairKey = `${studentKey}=>${targetKey}`;
          if (!seen.has(pairKey)) {
            seen.add(pairKey);
            missing.push({ fromKey: studentKey, toKey: targetKey });
          }
        }
        const backwardKey = makeDriveKey(targetKey, studentKey);
        if (!minutesMap.has(backwardKey)) {
          const pairKey = `${targetKey}=>${studentKey}`;
          if (!seen.has(pairKey)) {
            seen.add(pairKey);
            missing.push({ fromKey: targetKey, toKey: studentKey });
          }
        }
      });
    });
    return missing;
  }, [
    makeDriveKey,
    minutesMap,
    dropdownTeachersForModal,
    placeKeyByTeacher,
    resolveEventLocation,
    resolveStudentPlace,
    studentsNeedingReassignment,
    weeklyTeacherEventsForModal,
  ]);

  React.useEffect(() => {
    if (!promptReassignmentDriveTimes) return;
    if (!selectedTeacherId || !calendarEventsLoaded) return;
    const missingPairs = buildMissingReassignmentDrivePairs();
    setMissingReassignmentDrivePairs(missingPairs);
    if (missingPairs.length) {
      setShowMissingReassignmentDriveModal(true);
    }
    setPromptReassignmentDriveTimes(false);
  }, [
    buildMissingReassignmentDrivePairs,
    calendarEventsLoaded,
    promptReassignmentDriveTimes,
    selectedTeacherId,
  ]);

  const mergeDriveEdges = React.useCallback((edges) => {
    if (!edges?.length) return;
    setDriveEdges((prev) => {
      const next = new Map();
      prev.forEach((edge) => {
        const fromKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
        const toKey = edge.destination?.key || edge.destination || edge.toKey || edge.to;
        const key = `${fromKey}=>${toKey}`;
        next.set(key, edge);
      });
      edges.forEach((edge) => {
        const fromKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
        const toKey = edge.destination?.key || edge.destination || edge.toKey || edge.to;
        const key = `${fromKey}=>${toKey}`;
        next.set(key, edge);
      });
      return Array.from(next.values());
    });
  }, []);

  const handleCalculateMissingTeacherDriveTimes = React.useCallback(async () => {
    if (!missingTeacherDrivePairs.length) return;
    const uniquePairs = [];
    const seen = new Set();
    missingTeacherDrivePairs.forEach((pair) => {
      if (!pair?.fromKey || !pair?.toKey) return;
      const key = `${pair.fromKey}=>${pair.toKey}`;
      if (seen.has(key)) return;
      seen.add(key);
      uniquePairs.push({ fromKey: pair.fromKey, toKey: pair.toKey });
    });
    if (!uniquePairs.length) return;
    setCalculatingMissingDriveTimes(true);
    try {
      const edges = (await ensureAllPairs(uniquePairs)) || [];
      mergeDriveEdges(edges);
      setShowMissingTeacherDriveModal(false);
    } finally {
      setCalculatingMissingDriveTimes(false);
    }
  }, [ensureAllPairs, mergeDriveEdges, missingTeacherDrivePairs]);

  const handleCalculateMissingReassignmentDriveTimes = React.useCallback(async () => {
    if (!missingReassignmentDrivePairs.length) return;
    const uniquePairs = [];
    const seen = new Set();
    missingReassignmentDrivePairs.forEach((pair) => {
      if (!pair?.fromKey || !pair?.toKey) return;
      const key = `${pair.fromKey}=>${pair.toKey}`;
      if (seen.has(key)) return;
      seen.add(key);
      uniquePairs.push({ fromKey: pair.fromKey, toKey: pair.toKey });
    });
    if (!uniquePairs.length) return;
    setCalculatingReassignmentDriveTimes(true);
    try {
      const edges = (await ensureAllPairs(uniquePairs)) || [];
      mergeDriveEdges(edges);
      setShowMissingReassignmentDriveModal(false);
    } finally {
      setCalculatingReassignmentDriveTimes(false);
    }
  }, [ensureAllPairs, mergeDriveEdges, missingReassignmentDrivePairs]);

  const placeDirectoriesForModal = React.useMemo(() => {
    if (!placeDirectory.length) return [];
    return placeDirectory.map((entry, idx) => ({
      id: entry.key || entry.label || entry.address || `pd-${idx}`,
      label: entry.label || entry.key || entry.address || "Place",
      address: entry.address || entry.label || entry.key || "",
      roles: Array.isArray(entry.roles) && entry.roles.length ? entry.roles.join(", ") : "location",
    }));
  }, [placeDirectory]);

  const placeDirectoryPairsCount = React.useMemo(() => {
    const n = placeDirectory.length || 0;
    return (n * (n - 1)) / 2;
  }, [placeDirectory]);

  const driveEdgesForModal = React.useMemo(() => {
    const labelByKey = new Map();
    placeDirectory.forEach((pd) => {
      const label = pd.label || pd.key || pd.address || "Place";
      if (pd.key) labelByKey.set(pd.key, label);
    });
    const edges = (driveEdges || []).map((edge, idx) => {
      const fromKey =
        edge.origin?.key || edge.fromKey || edge.origin || edge.from || edge.key || `edge-${idx}-from`;
      const toKey =
        edge.destination?.key || edge.toKey || edge.destination || edge.to || `edge-${idx}-to`;
      return {
        id: edge.id || edge._id || `${fromKey}=>${toKey}-${idx}`,
        fromKey,
        toKey,
        fromLabel: edge.origin?.label || labelByKey.get(fromKey) || fromKey,
        toLabel: edge.destination?.label || labelByKey.get(toKey) || toKey,
        minutes: edge.minutes,
        bucket: edge.todBucket || edge.bucket,
      };
    });
    edges.sort((a, b) => (a.fromLabel || "").localeCompare(b.fromLabel || ""));
    return edges;
  }, [driveEdges, placeDirectory]);

  const createPlaceDirectoriesFromModal = React.useCallback(async () => {
    if (!dropdownTeachersForModal.length || !selectedSchoolId) return;
    setCreatingPlaceDirectories(true);
    try {
      const directories = dropdownTeachersForModal
        .filter((f) => f.address)
        .map((f) => ({
          key: f.address || f.teacherName,
          address: f.address,
          label: f.teacherName,
          roles: ["teacher"],
          familyIds: [],
          teacherIds: f.teacherId ? [f.teacherId] : [],
        }));

      if (!directories.length) {
        setCreatingPlaceDirectories(false);
        return;
      }

      await axios
        .post(
          `${baseUrl}/place-directories/bulk`,
          {
            schoolId: selectedSchoolId,
            directories,
          },
          getRequestHeaders()
        )
        .catch((err) => {
          console.log("place-directories bulk failed", err?.response?.data || err?.message);
        });

      // refresh place directory
      axios
        .get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders())
        .then((res) => setPlaceDirectory(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    } finally {
      setCreatingPlaceDirectories(false);
    }
  }, [dropdownTeachersForModal, selectedSchoolId, setPlaceDirectory]);

  const createMissingPlaceDirectories = React.useCallback(async () => {
    if (!selectedSchoolId) return;
    setCreatingPlaceDirectories(true);
    try {
      await createPlaceDirectoriesFromModal();
      for (const student of missingPlaceSummary.missingStudents) {
        await ensureStudentPlaceForReassignment(student);
      }
      if (missingPlaceSummary.missingEventFamilies.size) {
        for (const { ev } of weeklyTeacherEventsForModal) {
          const fid = ev?.familyId || ev?.familyID;
          if (!fid || !missingPlaceSummary.missingEventFamilies.has(String(fid))) continue;
          const tempStudent = {
            familyId: fid,
            familyName: ev.familyName,
            studentName: ev.studentName,
            familyAddress: ev.familyAddress || ev.address,
            address: ev.address,
          };
          const existingKey = resolveStudentPlace(tempStudent);
          await ensurePlaceForStudent(tempStudent, existingKey);
        }
      }
      const payload = await axios.get(`${baseUrl}/place-directory/${selectedSchoolId}`, getRequestHeaders());
      const dirs = payload?.data?.directories || payload?.data || [];
      setPlaceDirectory(Array.isArray(dirs) ? dirs : []);
      setShowMissingPlaceModal(false);
    } catch (err) {
      console.log(err);
    } finally {
      setCreatingPlaceDirectories(false);
    }
  }, [
    createPlaceDirectoriesFromModal,
    ensurePlaceForStudent,
    ensureStudentPlaceForReassignment,
    missingPlaceSummary.missingEventFamilies,
    missingPlaceSummary.missingStudents,
    resolveStudentPlace,
    selectedSchoolId,
    weeklyTeacherEventsForModal,
  ]);

  const onDragStart = (student, domEvent) => {
    if (domEvent?.dataTransfer) {
      domEvent.dataTransfer.setDragImage(ensureTransparentImg(), 0, 0);
    }
    setDraggingStudent(student);
    setDraggingPlacement(null);
    setDragPreview({ x: domEvent.clientX, y: domEvent.clientY, label: student.familyName || student.studentName || "…" });
  };

  const onDragStartPlacement = (placement, domEvent) => {
    if (!placement.planned) return;
    setDraggingPlacement(placement);
    setDraggingStudent(null);
    if (domEvent?.dataTransfer) {
      domEvent.dataTransfer.setDragImage(ensureTransparentImg(), 0, 0);
    }
    setDragPreview({
      x: domEvent?.clientX || 0,
      y: domEvent?.clientY || 0,
      label: placement.familyName || placement.studentName || "…",
    });
  };

  const onDragOverDayArea = (dayName, e) => {
    if ((!draggingStudent && !draggingPlacement) || !selectedTeacherId) return;
    const ref = dayRefs.current[dayName];
    if (!ref) return;
    const rect = ref.getBoundingClientRect();
    const offsetY = e.clientY - rect.top + ref.scrollTop;
    const rawMinutes = Math.round((offsetY / PIXELS_PER_SLOT) * SLOT_MINUTES);
    const clampedMinutes = Math.min(Math.max(rawMinutes, 0), timeWindow.totalMinutes);
    const slotMinutes = Math.round(clampedMinutes / SLOT_MINUTES) * SLOT_MINUTES;
    // throttle updates: only when day or slot changes
    if (lastPreviewSlotRef.current.day === dayName && lastPreviewSlotRef.current.slot === slotMinutes) {
      e.preventDefault();
      return;
    }
    lastPreviewSlotRef.current = { day: dayName, slot: slotMinutes };
    const slotDate = new Date(startOfThisWeek);
    slotDate.setDate(slotDate.getDate() + weekDays.indexOf(dayName));
    const totalMinutesFromMidnight = timeWindow.startMinutes + slotMinutes;
    slotDate.setHours(Math.floor(totalMinutesFromMidnight / 60));
    slotDate.setMinutes(totalMinutesFromMidnight % 60);
    slotDate.setSeconds(0);
    const label = format(slotDate, "h:mma");
    if (lastHoverRef.current.day !== dayName || lastHoverRef.current.label !== label) {
      lastHoverRef.current = { day: dayName, label };
      setHoverIndicator({ day: dayName, label });
    }
    setDragPreview({ x: e.clientX, y: e.clientY, label: `${dayName} ${label}` });
    e.preventDefault();
  };

  const onDropOnDayArea = async (dayName, e) => {
    console.log('hi yo')
    if ((!draggingStudent && !draggingPlacement) || !selectedTeacherId) return;
    const ref = dayRefs.current[dayName];
    if (!ref) return;
    const rect = ref.getBoundingClientRect();
    const offsetY = e.clientY - rect.top + ref.scrollTop;
    const rawMinutes = Math.round((offsetY / PIXELS_PER_SLOT) * SLOT_MINUTES);
    const clampedMinutes = Math.min(Math.max(rawMinutes, 0), timeWindow.totalMinutes);
    const slotMinutes = Math.round(clampedMinutes / SLOT_MINUTES) * SLOT_MINUTES;
    const dayIndex = weekDays.indexOf(dayName);
    const slotDate = new Date(displayedWeek[dayIndex]);
    const totalMinutesFromMidnight = timeWindow.startMinutes + slotMinutes;
    slotDate.setHours(Math.floor(totalMinutesFromMidnight / 60));
    slotDate.setMinutes(totalMinutesFromMidnight % 60);
    slotDate.setSeconds(0);
    // hydrate placement details
    if (draggingStudent) {
      let instrumentLabel =
        draggingStudent.instruments?.[0]?.parentInstrument ||
        draggingStudent.instruments?.[0]?.instrument ||
        null;
      let lessonLength = draggingStudent.lessonLength || 30;
      let familyAddress = draggingStudent.familyAddress || null;

      const familyInfo = await getFamilyInfo(draggingStudent.familyId);
      if (familyInfo) {
        const match =
          familyInfo.students?.find(
            (s) => String(s.studentId) === String(draggingStudent.studentId)
          ) || {};
        if (!instrumentLabel && match.instrumentList?.length) {
          instrumentLabel = match.instrumentList[0]?.parentInstrument || null;
        }
        if (!lessonLength && match.lessonLength) {
          lessonLength = match.lessonLength;
        }
        familyAddress = familyInfo.address || familyInfo.familyAddress || familyAddress;
      }

      let studentPlaceKey = resolveStudentPlace(draggingStudent);
      const teacherPlaceKey = placeKeyByTeacher.get(String(selectedTeacherId));
      console.log("Looking for Drivetime");
      let driveMinutes =
        teacherPlaceKey && studentPlaceKey ? minutesBetween(teacherPlaceKey, studentPlaceKey) : Infinity;
      let missingDriveInfo = !Number.isFinite(driveMinutes);
      if (missingDriveInfo) {
        studentPlaceKey = await ensurePlaceForStudent(draggingStudent, studentPlaceKey);
        if (teacherPlaceKey && studentPlaceKey) {
          const edges = (await ensureAllPairs([{ fromKey: teacherPlaceKey, toKey: studentPlaceKey }])) || [];
          const found = edges.find(
            (edge) =>
              (edge.origin?.key || edge.fromKey) === teacherPlaceKey &&
              (edge.destination?.key || edge.toKey) === studentPlaceKey
          );
          driveMinutes =
            Number.isFinite(Number(found?.minutes)) && Number(found?.minutes) > 0
              ? Number(found.minutes)
              : minutesBetween(teacherPlaceKey, studentPlaceKey);
          missingDriveInfo = !Number.isFinite(driveMinutes);
          console.log("Drive edge lookup (student drop)", {
            teacherPlaceKey,
            studentPlaceKey,
            foundMinutes: found?.minutes,
            driveMinutes,
            edge: found,
            edgesReturned: edges.length,
          });
        }
      } else {
        console.log("Drive edge cache hit (student drop)", {
          teacherPlaceKey,
          studentPlaceKey,
          driveMinutes,
        });
      }
      // Also ensure edges between this student and other known places for the teacher/day (home + events on this day)
      const neighborPlaceKeys = new Set();
      const dayEvents = positionedEventsByDay[dayName] || [];
      dayEvents.forEach((ev) => {
        const tid = String(ev.teacherId || ev.teacherID || ev.TeacherID || selectedTeacherId || "");
        if (tid === String(selectedTeacherId)) {
          const pk = getDriveKey(ev);
          if (pk) neighborPlaceKeys.add(pk);
        }
      });
      if (teacherPlaceKey) neighborPlaceKeys.add(teacherPlaceKey);
      const pairRequests = [];
      neighborPlaceKeys.forEach((pk) => {
        if (!studentPlaceKey || pk === studentPlaceKey) return;
        pairRequests.push({ fromKey: pk, toKey: studentPlaceKey });
        pairRequests.push({ fromKey: studentPlaceKey, toKey: pk });
      });
      if (pairRequests.length) {
        await ensureAllPairs(pairRequests);
        driveMinutes =
          teacherPlaceKey && studentPlaceKey ? minutesBetween(teacherPlaceKey, studentPlaceKey) : Infinity;
        missingDriveInfo = !Number.isFinite(driveMinutes);
      }

      const placement = {
        id: `${draggingStudent.id}-${dayName}-${slotMinutes}`,
        placementKey: draggingStudent.id,
        studentId: draggingStudent.studentId || draggingStudent.id,
        familyId: draggingStudent.familyId,
        studentName: draggingStudent.studentName,
        familyName: draggingStudent.familyName,
        dayIndex,
        teacherId: selectedTeacherId,
        startMinutes: timeWindow.startMinutes + slotMinutes,
        lessonLength,
        instrument: instrumentLabel,
        familyAddress,
      };
      setPlannedPlacements((prev) => {
        const filtered = prev.filter((p) => !(p.id === placement.id));
        return [...filtered, placement];
      });
      setPlacedStudentIds((prev) => (prev.includes(draggingStudent.id) ? prev : [...prev, draggingStudent.id]));
      setDraggingStudent(null);
      if (missingDriveInfo) {
        setNoDriveContext({
          teacher: teacherNameById.get(String(selectedTeacherId)) || selectedTeacherId,
          student: draggingStudent.studentName || draggingStudent.familyName || draggingStudent.id,
          fromKey: teacherPlaceKey,
          toKey: studentPlaceKey,
        });
        setShowNoDriveModal(true);
      }
    } else if (draggingPlacement) {
      const placementKey = draggingPlacement.placementKey || draggingPlacement.id;
      const lessonLength = draggingPlacement.lessonLength || 30;
      let studentPlaceKey = resolveStudentPlace(draggingPlacement);
      const teacherPlaceKey = placeKeyByTeacher.get(String(selectedTeacherId));
      let driveMinutes =
        teacherPlaceKey && studentPlaceKey ? minutesBetween(teacherPlaceKey, studentPlaceKey) : Infinity;
      let missingDriveInfo = !Number.isFinite(driveMinutes);
      if (missingDriveInfo) {
        studentPlaceKey = await ensurePlaceForStudent(draggingPlacement, studentPlaceKey);
        if (teacherPlaceKey && studentPlaceKey) {
          const edges = (await ensureAllPairs([{ fromKey: teacherPlaceKey, toKey: studentPlaceKey }])) || [];
          const found = edges.find(
            (edge) =>
              (edge.origin?.key || edge.fromKey) === teacherPlaceKey &&
              (edge.destination?.key || edge.toKey) === studentPlaceKey
          );
          driveMinutes =
            Number.isFinite(Number(found?.minutes)) && Number(found?.minutes) > 0
              ? Number(found.minutes)
              : minutesBetween(teacherPlaceKey, studentPlaceKey);
          missingDriveInfo = !Number.isFinite(driveMinutes);
          console.log("Drive edge lookup (placement drop)", {
            teacherPlaceKey,
            studentPlaceKey,
            foundMinutes: found?.minutes,
            driveMinutes,
            edge: found,
            edgesReturned: edges.length,
          });
        }
      } else {
        console.log("Drive edge cache hit (placement drop)", {
          teacherPlaceKey,
          studentPlaceKey,
          driveMinutes,
        });
      }
      const neighborPlaceKeys = new Set();
      const dayEvents = positionedEventsByDay[dayName] || [];
      dayEvents.forEach((ev) => {
        const tid = String(ev.teacherId || ev.teacherID || ev.TeacherID || selectedTeacherId || "");
        if (tid === String(selectedTeacherId)) {
          const pk = getDriveKey(ev);
          if (pk) neighborPlaceKeys.add(pk);
        }
      });
      if (teacherPlaceKey) neighborPlaceKeys.add(teacherPlaceKey);
      const pairRequests = [];
      neighborPlaceKeys.forEach((pk) => {
        if (!studentPlaceKey || pk === studentPlaceKey) return;
        pairRequests.push({ fromKey: pk, toKey: studentPlaceKey });
        pairRequests.push({ fromKey: studentPlaceKey, toKey: pk });
      });
      if (pairRequests.length) {
        await ensureAllPairs(pairRequests);
        driveMinutes =
          teacherPlaceKey && studentPlaceKey ? minutesBetween(teacherPlaceKey, studentPlaceKey) : Infinity;
        missingDriveInfo = !Number.isFinite(driveMinutes);
      }
      const updatedPlacement = {
        ...draggingPlacement,
        id: `${placementKey}-${dayName}-${slotMinutes}`,
        placementKey,
        studentId: draggingPlacement.studentId || placementKey,
        dayIndex,
        startMinutes: timeWindow.startMinutes + slotMinutes,
        lessonLength,
      };
      setPlannedPlacements((prev) => {
        const filtered = prev.filter((p) => (p.placementKey || p.id) !== placementKey);
        return [...filtered, updatedPlacement];
      });
      setDraggingPlacement(null);
      if (missingDriveInfo) {
        setNoDriveContext({
          teacher: teacherNameById.get(String(selectedTeacherId)) || selectedTeacherId,
          student:
            draggingPlacement.studentName || draggingPlacement.familyName || draggingPlacement.id,
          fromKey: teacherPlaceKey,
          toKey: studentPlaceKey,
        });
        setShowNoDriveModal(true);
      }
    }
    lastHoverRef.current = { day: null, label: null };
    setHoverIndicator(null);
    setDragPreview(null);
  };

  const onDragOverStudentColumn = (e) => {
    if (draggingPlacement || draggingStudent) {
      e.preventDefault();
      if (!isHoveringStudentColumn) setIsHoveringStudentColumn(true);
      // compute insert index based on cursor position
      if (studentListRef.current) {
        const cards = Array.from(
          studentListRef.current.querySelectorAll(".dds2025-student-card")
        );
        let insertAt = cards.length;
        for (let i = 0; i < cards.length; i++) {
          const rect = cards[i].getBoundingClientRect();
          if (e.clientY < rect.top + rect.height / 2) {
            insertAt = i;
            break;
          }
        }
        if (insertAt !== hoverInsertIndex) {
          setHoverInsertIndex(insertAt);
        }
      }
    }
  };

  const onDragLeaveStudentColumn = (e) => {
    if (e && studentListRef.current && e.relatedTarget) {
      // Ignore leave events when moving between children inside the list
      if (studentListRef.current.contains(e.relatedTarget)) return;
    }
    if (isHoveringStudentColumn) setIsHoveringStudentColumn(false);
    if (hoverInsertIndex !== null) setHoverInsertIndex(null);
  };

  const onDropOnStudentColumn = (e) => {
    e.preventDefault();
    setIsHoveringStudentColumn(false);
    setHoverInsertIndex(null);
    if (draggingPlacement) {
      const sid = getStudentKey(draggingPlacement);
      // Don't remove locked placements
      if (!lockedStudentIds.includes(sid)) {
        setPlannedPlacements((prev) =>
          prev.filter(
            (p) =>
              (p.placementKey || p.studentId || p.id) !==
              (draggingPlacement.placementKey || draggingPlacement.studentId || draggingPlacement.id)
          )
        );
        setPlacedStudentIds((prev) =>
          prev.filter(
            (id) =>
              id !== sid &&
              id !== (draggingPlacement.placementKey || draggingPlacement.studentId || draggingPlacement.id)
          )
        );
        setRemovedReassignmentIds((prev) => prev.filter((id) => String(id) !== String(sid)));
        const reassignmentStudent = {
          id: sid,
          familyId: draggingPlacement.familyId,
          familyName: draggingPlacement.familyName,
          studentId: draggingPlacement.studentId || draggingPlacement.placementKey || draggingPlacement.id,
          studentName: draggingPlacement.studentName,
          instruments: draggingPlacement.instrument
            ? [{ parentInstrument: draggingPlacement.instrument, instrument: draggingPlacement.instrument }]
            : draggingPlacement.instruments || [],
          lessonLength: draggingPlacement.lessonLength,
          availability: draggingPlacement.availability,
          school: selectedSchool,
          familyAddress: draggingPlacement.familyAddress,
          teacherId: draggingPlacement.teacherId,
        };
        if (Array.isArray(overrideReassignmentStudents)) {
          setOverrideReassignmentStudents((prev) => {
            const base = Array.isArray(prev) ? prev : [];
            const exists = base.some((s) => String(s.id || s.studentId) === String(sid));
            return exists ? base : [...base, reassignmentStudent];
          });
        } else {
          setManualReassignmentStudents((prev) => {
            const exists = prev.some((s) => String(s.id || s.studentId) === String(sid));
            return exists ? prev : [...prev, reassignmentStudent];
          });
        }
      }
      setDraggingPlacement(null);
      setDragPreview(null);
    } else if (draggingStudent) {
      setDraggingStudent(null);
      setDragPreview(null);
    }
  };

  const renderStudentCard = (student) => {
    const sid = getStudentKey(student);
    const isLocked = lockedStudentIds.includes(sid);

    return (
      <div
        key={sid}
        className="dds2025-student-card"
        style={{
          background: "#f1f5f9",
          border: "1px solid #e2e8f0",
          padding: "14px",
          borderRadius: "10px",
        }}
        draggable
        onDragStart={(e) => onDragStart(student, e)}
        onDragEnd={() => {
          setDraggingStudent(null);
          setDragPreview(null);
        }}
      >
        <div className="dds2025-student-header">
          <div className="dds2025-student-name-row">
            <span className="dds2025-drag-hint">⋮⋮</span>
            <div className="dds2025-student-name">
              {student.studentName} {student.familyName}
            </div>
          </div>
          <div className="dds2025-student-chips">
            <span className="dds2025-chip">{student.lessonLength || "--"} mins</span>
            {isLocked && <span className="dds2025-chip" title="Locked for optimization">🔒 Locked</span>}
            <button
              type="button"
              className="dds2025__chip-remove"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRemoveReassignmentStudent(student);
              }}
              aria-label="Remove student from reassignment list"
              title="Remove from reassignment list"
            >
              ×
            </button>
            {student.instruments?.length ? (
              student.instruments.map((inst, idx) => (
                <span
                  key={`${inst.id || inst.parentInstrument || inst.instrument || inst.name || "inst"}-${idx}`}
                  className="dds2025-chip dds2025-chip--neutral"
                >
                  {inst.parentInstrument || inst.instrument || inst.name || "Instrument"}
                </span>
              ))
            ) : (
              <span className="dds2025-chip dds2025-chip--neutral">Instrument TBD</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (welcomeStep) {
    return (
      <div className="dds2025 dds2025__welcome">
        <div className="dds2025__welcome-card" style={{ position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
          {welcomeStep === "welcome" && (
            <>
              <h1 className="dds2025__welcome-title">Welcome to Atharo Scheduling</h1>
              <p className="dds2025__welcome-subtitle">
                Build a new schedule or pick up where you left off.
              </p>
              <div className="dds2025__welcome-actions">
                <button
                  className="dds2025__nav-btn dds2025__nav-btn--primary"
                  onClick={handleStartNewScheduleClick}
                >
                  Start New Schedule
                </button>
                <button
                  className="dds2025__nav-btn"
                  onClick={() => {
                    if (!selectedSchool && !selectedSchoolId) {
                      setWelcomeStep(null);
                      return;
                    }
                    setShowLoadScheduleModal(true);
                  }}
                >
                  Load Schedule
                </button>
              </div>
            </>
          )}

          {welcomeStep === "new" && (
            <>
              <h1 className="dds2025__welcome-title">Select a school</h1>
              <p className="dds2025__welcome-subtitle">
                Choose which school, then start new project or choose a leaving teacher
              </p>
              <div className="dds2025__welcome-field">
                <label className="dds2025__welcome-label">School</label>
                <select
                  value={selectedSchool}
                  onChange={(e) => {
                    setSelectedSchool(e.target.value);
                    setSelectedTeacherId("");
                  }}
                >
                  <option value="">Select a school</option>
                  {SCHOOL_OPTIONS.map((school) => (
                    <option key={school.id} value={school.id}>
                      {school.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="dds2025__welcome-field">
                <label className="dds2025__welcome-label">Leaving Teacher (optional)</label>
                <select
                  value={selectedTeacherId}
                  onChange={(e) => setSelectedTeacherId(e.target.value)}
                  disabled={!teacherOptionsAll.length}
                >
                  <option value="">Choose a teacher studio</option>
                  {teacherOptionsAll.map((teacher) => (
                    <option key={teacher.value} value={teacher.value}>
                      {teacher.label}
                    </option>
                  ))}
                </select>
                {!teacherOptionsAll.length && (
                  <div className="dds2025__welcome-hint">No teachers loaded yet.</div>
                )}
              </div>
              <div className="dds2025__welcome-actions">
                <button className="dds2025__nav-btn" onClick={() => setWelcomeStep("welcome")}>
                  Back
                </button>
                <button
                  className="dds2025__nav-btn"
                  onClick={handleLoadTeacherStudentsAndStart}
                  disabled={!selectedSchool || !selectedTeacherId}
                >
                  Load Teacher's Students
                </button>
                <button
                  className="dds2025__nav-btn dds2025__nav-btn--primary"
                  onClick={handleStartSchedulingWithoutTeacher}
                  disabled={!selectedSchool}
                >
                  Just Start Scheduling
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dds2025">
      {/* Ledger floating panel */}
      <div className="dds2025__ledger-panel open">
        <div className="dds2025__ledger-header">
          <span>Optimization Ledger</span>
          <button
            className="dds2025__ledger-toggle"
            type="button"
            onClick={() => setShowLedger((prev) => !prev)}
            aria-label="Toggle optimization ledger"
          >
            {showLedger ? "Hide" : "Show"}
          </button>
        </div>
        <div className="dds2025__simple-sub" style={{ padding: "0 12px 10px" }}>
          Overall drive time: {Math.floor(optimizationDriveMinutes / 60)}h{" "}
          {String(optimizationDriveMinutes % 60).padStart(2, "0")}m
        </div>
        {showLedger && (
          <div className="dds2025__ledger-list">
            {optimizationLedgerEntries.length ? (
              optimizationLedgerEntries.map((entry, idx) => (
                <div key={`${entry.student}-${idx}`} className="dds2025__ledger-row">
                  <span className="dds2025__ledger-student">{entry.student}</span>
                  <span className="dds2025__ledger-teacher">→ {entry.teacher}</span>
                  <span className="dds2025__ledger-time">
                    {entry.day} @ {entry.time}
                  </span>
                </div>
              ))
            ) : (
              <div className="dds2025__simple-sub" style={{ padding: "10px 12px" }}>
                No placements yet.
              </div>
            )}
          </div>
        )}
      </div>
      {showAddStudentModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal dds2025__modal--wide">
            <div className="dds2025__modal-title">Add student to reassignment</div>
            <div className="dds2025__modal-body">
              <input
                type="text"
                value={studentSearchTerm}
                onChange={(e) => setStudentSearchTerm(e.target.value)}
                placeholder={selectedSchool ? "Search students in this school" : "Select a school first"}
                disabled={!selectedSchool}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d0d5dd",
                  borderRadius: "8px",
                  marginBottom: "12px",
                }}
              />
              <div className="dds2025__simple-list" style={{ maxHeight: 320, overflow: "auto" }}>
                {!selectedSchool && (
                  <div className="dds2025__simple-sub">Select a school first.</div>
                )}
                {selectedSchool && !studentSearchTerm.trim() && (
                  <div className="dds2025__simple-sub">Start typing to search.</div>
                )}
                {selectedSchool && studentSearchTerm.trim() && studentSearchLoading && (
                  <div className="dds2025__simple-sub">Searching…</div>
                )}
                {selectedSchool && studentSearchTerm.trim() && studentSearchError && (
                  <div className="dds2025__simple-sub">{studentSearchError}</div>
                )}
                {selectedSchool &&
                  studentSearchTerm.trim() &&
                  !studentSearchLoading &&
                  !studentSearchError &&
                  (studentSearchResults.length ? (
                    studentSearchResults.map((student) => (
                      <button
                        key={student.id}
                        className="dds2025__simple-row dds2025__simple-row--clickable"
                        onClick={() => addStudentToReassignmentById(student)}
                        style={{ textAlign: "left", width: "100%" }}
                      >
                        <div className="dds2025__simple-name">
                          {student.studentName} {student.familyName ? `(${student.familyName})` : ""}
                        </div>
                        <div className="dds2025__simple-sub">
                          {student.instruments?.[0]?.parentInstrument ||
                            student.instruments?.[0]?.instrument ||
                            "Instrument TBD"}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="dds2025__simple-sub">No students found for this search.</div>
                  ))}
              </div>
            </div>
            <div className="dds2025__modal-actions" style={{ justifyContent: "flex-end" }}>
              <button className="dds2025__nav-btn" onClick={() => setShowAddStudentModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showMissingTeacherDriveModal && missingTeacherDrivePairs.length > 0 && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal dds2025__modal--wide">
            <div className="dds2025__modal-title">
              Missing drive times for {selectedTeacherName || "teacher"}
            </div>
            <div className="dds2025__modal-body">
              <div className="dds2025__simple-sub" style={{ marginBottom: 8 }}>
                These are consecutive lessons without a stored drive time.
              </div>
              <div className="dds2025__simple-list" style={{ maxHeight: 320, overflow: "auto" }}>
                {missingTeacherDrivePairs.map((pair) => (
                  <div key={pair.id} className="dds2025__simple-row">
                    <div className="dds2025__simple-name">
                      {pair.fromLabel} → {pair.toLabel}
                    </div>
                    <div className="dds2025__simple-sub">
                      {pair.day} • {pair.time}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="dds2025__modal-actions" style={{ justifyContent: "flex-end" }}>
              <button
                className="dds2025__nav-btn"
                onClick={() => setShowMissingTeacherDriveModal(false)}
                disabled={calculatingMissingDriveTimes}
              >
                Close
              </button>
              <button
                className="dds2025__nav-btn dds2025__nav-btn--primary"
                onClick={handleCalculateMissingTeacherDriveTimes}
                disabled={calculatingMissingDriveTimes}
              >
                {calculatingMissingDriveTimes ? "Calculating..." : "Calculate Drive Times"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showMissingReassignmentDriveModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal">
            <div className="dds2025__modal-title">
              {missingReassignmentDrivePairs.length} Missing Drive Times
            </div>
            <div className="dds2025__modal-body">
              <div className="dds2025__simple-sub" style={{ marginBottom: 6 }}>
                These are between reassignment students and other teachers' homes/calendar events.
              </div>
              <div className="dds2025__simple-sub">
                Estimated cost: $
                {(missingReassignmentDrivePairs.length * DRIVE_TIME_COST_PER_PAIR).toFixed(2)} (
                {missingReassignmentDrivePairs.length} × ${DRIVE_TIME_COST_PER_PAIR.toFixed(2)})
              </div>
            </div>
            <div className="dds2025__modal-actions" style={{ justifyContent: "flex-end" }}>
              <button
                className="dds2025__nav-btn"
                onClick={() => setShowMissingReassignmentDriveModal(false)}
                disabled={calculatingReassignmentDriveTimes}
              >
                Close
              </button>
              <button
                className="dds2025__nav-btn dds2025__nav-btn--primary"
                onClick={handleCalculateMissingReassignmentDriveTimes}
                disabled={calculatingReassignmentDriveTimes}
              >
                {calculatingReassignmentDriveTimes ? "Calculating..." : "Create Drive Times"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showLoadScheduleModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal dds2025__modal--wide">
            <div className="dds2025__modal-title">Load a saved schedule</div>
            <div className="dds2025__modal-body">
              {!selectedSchool && (
                <div className="dds2025__simple-sub" style={{ marginBottom: 8 }}>
                  Select a school first to view saved schedules.
                </div>
              )}
              {loadingSchedules && <div className="dds2025__simple-sub">Loading schedules…</div>}
              {!loadingSchedules && selectedSchool && (
                <div className="dds2025__simple-list" style={{ maxHeight: 360, overflow: "auto" }}>
                  {savedSchedules.length ? (
                    savedSchedules.map((s) => {
                      const week =
                        s.weekStart ? format(new Date(s.weekStart), "MMM d, yyyy") : "Week not set";
                      return (
                        <div
                          key={s._id || s.id}
                          className="dds2025__simple-row dds2025__simple-row--clickable"
                          onClick={() => handleLoadExistingSchedule(s)}
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
                        >
                          <div>
                            <div className="dds2025__simple-name">{s.label || "Untitled schedule"}</div>
                            <div className="dds2025__simple-sub">
                              {week} • {s.status || "draft"} • {s.placements?.length || 0} placements
                            </div>
                          </div>
                          <button
                            className="dds2025__nav-btn"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSchedule(s._id || s.id);
                            }}
                            style={{ padding: "6px 10px" }}
                            aria-label={`Delete ${s.label || "schedule"}`}
                          >
                            Delete
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="dds2025__simple-sub">No schedules saved for this school.</div>
                  )}
                </div>
              )}
            </div>
            <div className="dds2025__modal-actions">
              <button className="dds2025__nav-btn" onClick={() => setShowLoadScheduleModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showMissingPlaceModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal">
            <div className="dds2025__modal-title">Missing place directories</div>
            <div className="dds2025__modal-body">
              <div className="dds2025__simple-sub" style={{ marginBottom: 8 }}>
                We found missing place directories after loading this schedule.
              </div>
              <div className="dds2025__simple-list">
                <div className="dds2025__simple-row">
                  <div className="dds2025__simple-name">Teachers missing</div>
                  <div className="dds2025__simple-sub">{missingPlaceSummary.missingTeachers.length}</div>
                </div>
                <div className="dds2025__simple-row">
                  <div className="dds2025__simple-name">Calendar event families missing</div>
                  <div className="dds2025__simple-sub">{missingPlaceSummary.missingEventFamilies.size}</div>
                </div>
                <div className="dds2025__simple-row">
                  <div className="dds2025__simple-name">Reassignment students missing</div>
                  <div className="dds2025__simple-sub">{missingPlaceSummary.missingStudents.length}</div>
                </div>
                <div className="dds2025__simple-row">
                  <div className="dds2025__simple-name">Total missing</div>
                  <div className="dds2025__simple-sub">{missingPlaceSummary.totalMissing}</div>
                </div>
              </div>
              <div className="dds2025__simple-sub" style={{ marginTop: 8 }}>
                Only entries with addresses can be created automatically.
              </div>
            </div>
            <div className="dds2025__modal-actions">
              <button
                className="dds2025__nav-btn dds2025__nav-btn--primary"
                onClick={createMissingPlaceDirectories}
                disabled={creatingPlaceDirectories}
              >
                {creatingPlaceDirectories ? "Creating..." : "Create place directories"}
              </button>
              {/* <button className="dds2025__nav-btn" onClick={() => setShowMissingPlaceModal(false)}>
                Close
              </button> */}
            </div>
          </div>
        </div>
      )}
      {showNoDriveModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal">
            <div className="dds2025__modal-title">No drive time info</div>
            <div className="dds2025__modal-body">
              No drive time info is available for this route.
              <div style={{ marginTop: 8, fontSize: "12px", color: "#475569" }}>
                {noDriveContext?.teacher || "Teacher"} ({noDriveContext?.fromKey || "from"}) →{" "}
                {noDriveContext?.student || "Student"} ({noDriveContext?.toKey || "to"})
              </div>
            </div>
            <div className="dds2025__modal-actions">
              <button
                className="dds2025__nav-btn"
                onClick={() => {
                  setShowNoDriveModal(false);
                  setNoDriveContext(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="dds2025__filters">
        <div className="dds2025__filter">
          <label>School</label>
          <select
            value={selectedSchool}
            onChange={(e) => {
      setSelectedSchool(e.target.value);
      setSelectedTeacherId("");
      setShowCalendarEventsModal(false);
      setOpenCalendarModalAfterFetch(false);
    }}
  >
            <option value="">Select a school</option>
            {SCHOOL_OPTIONS.map((school) => (
              <option key={school.id} value={school.id}>
                {school.label}
              </option>
            ))}
          </select>
        </div>
        <div className="dds2025__filter">
          <label>Schedule name</label>
          <input
            type="text"
            value={scheduleName}
            onChange={(e) => setScheduleName(e.target.value)}
            onBlur={() => {
              if (currentScheduleId) {
                // Use the latest typed value to avoid stale label in the save call
                saveSchedule(scheduleName);
              }
            }}
            placeholder="Untitled schedule"
            style={{ padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: "6px", minWidth: 200 }}
          />
        </div>
        <div className="dds2025__filter">
          <label>Select schedule</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              value={currentScheduleId || ""}
              onChange={(e) => {
                const val = e.target.value;
                if (!val) {
                  if (skipNewScheduleResetRef.current) {
                    skipNewScheduleResetRef.current = false;
                    return;
                  }
                  handleStartNewScheduleClick();
                  return;
                }
                const match = savedSchedules.find((s) => String(s._id || s.id) === String(val));
                if (match) handleLoadExistingSchedule(match);
              }}
              disabled={!savedSchedules.length}
            >
              <option value="">New / unsaved schedule</option>
              {savedSchedules.map((s) => (
                <option key={s._id || s.id} value={s._id || s.id}>
                  {s.label || "Untitled"} – {s.status || "draft"}
                </option>
              ))}
            </select>
            <button
              className="dds2025__nav-btn"
              type="button"
              onClick={() => handleDeleteSchedule(currentScheduleId)}
              disabled={!currentScheduleId}
              style={{ padding: "6px 10px" }}
            >
              Delete schedule
            </button>
          </div>
        </div>
        <div className="dds2025__filter">
          <label>Teacher (instrument match)</label>
          <select
            value={selectedTeacherId}
            onChange={(e) => setSelectedTeacherId(e.target.value)}
            disabled={!teacherOptions.length}
          >
            <option value="">Select a teacher</option>
            {teacherOptions.map((teacher) => (
              <option key={teacher.value} value={teacher.value}>
                {teacher.label}
              </option>
            ))}
          </select>
        </div>
        {loading && <div className="dds2025__status">Loading data…</div>}
        <button
          className="dds2025__nav-btn"
          style={{ alignSelf: "flex-end" }}
          onClick={optimizeSchedule}
        >
          Optimize Schedule
        </button>
      </div>

      <div className="dds2025__grid">
        <div className="dds2025__students">
          <div className="dds2025__column-title">
            Students needing reassignment ({studentsNeedingReassignment.length})
            <button
              className="dds2025__icon-btn"
              onClick={() => {
                setStudentSearchTerm("");
                setShowAddStudentModal(true);
              }}
              disabled={!selectedSchool}
              aria-label="Add student to reassignment"
            >
              +
            </button>
          </div>
          <div
            className="dds2025__student-list"
            ref={studentListRef}
            onDragOver={onDragOverStudentColumn}
            onDragLeave={onDragLeaveStudentColumn}
            onDrop={onDropOnStudentColumn}
            style={{ minHeight: 220 }}
          >
            {studentsNeedingReassignment.map((student, idx) => (
              <React.Fragment key={student.id || student.studentId || idx}>
                {hoverInsertIndex === idx &&
                  (draggingStudent || draggingPlacement) &&
                  isHoveringStudentColumn && (
                    <div className="dds2025__drop-ghost">Release to return student</div>
                  )}
                {renderStudentCard(student)}
              </React.Fragment>
            ))}
            {hoverInsertIndex === studentsNeedingReassignment.length &&
              (draggingStudent || draggingPlacement) &&
              isHoveringStudentColumn && (
                <div className="dds2025__drop-ghost">Release to return student</div>
              )}
            {!studentsNeedingReassignment.length && (
              <div className="dds2025__empty">No students to reassign for this school.</div>
            )}
          </div>
        </div>

        <div className="dds2025__calendar">
          <div className="dds2025__column-title">
            <div className="dds2025__calendar-header">
              <div className="dds2025__calendar-week">
                {format(displayedWeek[0], "MMM d")} – {format(displayedWeek[6], "MMM d, yyyy")}
              </div>
              <div className="dds2025__calendar-nav">
                <button className="dds2025__nav-btn" onClick={handleResetReassignmentPlacements}>
                  Reset
                </button>
                <button className="dds2025__nav-btn" onClick={() => setWeekOffset((prev) => prev - 1)}>&lt;</button>
                <button className="dds2025__nav-btn" onClick={() => setWeekOffset((prev) => prev + 1)}>&gt;</button>
              </div>
            </div>
            {selectedTeacherId && (
              <div className="dds2025__teacher-meta">
                {selectedTeacherName && (
                  <span className="dds2025__teacher-name">Teacher: {selectedTeacherName}</span>
                )}
                {typeof totalDriveMinutes === "number" && (
                  <span className="dds2025-chip dds2025-chip--neutral">
                    Drive time: {Math.floor(totalDriveMinutes / 60)}h {totalDriveMinutes % 60}m
                  </span>
                )}
                {typeof totalAvailabilityMinutes === "number" && typeof totalTeachingMinutes === "number" && totalAvailabilityMinutes > 0 && (
                  <span className="dds2025-chip dds2025-chip--neutral">
                    TAUI: {Math.round((totalTeachingMinutes / totalAvailabilityMinutes) * 100)}% ({Math.floor(totalTeachingMinutes / 60)}h {totalTeachingMinutes % 60}m of {Math.floor(totalAvailabilityMinutes / 60)}h {totalAvailabilityMinutes % 60}m)
                  </span>
                )}
              </div>
            )}
            {selectedTeacherId && selectedTeacherInstruments.length > 0 && (
              <div className="dds2025__teacher-instruments">
                <span className="dds2025__teacher-instruments-label">Instruments:</span>
                {selectedTeacherInstruments.map((inst) => (
                  <span key={inst} className="dds2025-chip dds2025-chip--neutral">
                    {inst}
                  </span>
                ))}
              </div>
            )}
            {/* <div className="dds2025__drive-cta">
              <span className="dds2025__drive-text">
                Drive pairs missing: {missingDrivePairs.length}
              </span>
            </div> */}
            {!selectedTeacherId && <div>Choose a teacher to view their week</div>}
          </div>
          <div className="dds2025__week-grid" ref={calendarRef}>
            <div className="dds2025__time-col" style={{ height: timeWindow.totalHeightPx }}>
              {Array.from({ length: timeWindow.endHour - timeWindow.startHour + 1 }).map((_, i) => {
                const hour = timeWindow.startHour + i;
                return (
                  <div key={hour} className="dds2025__time-marker" style={{ top: (((hour * 60) - timeWindow.startMinutes) / SLOT_MINUTES) * PIXELS_PER_SLOT }}>
                    {format(new Date().setHours(hour, 0, 0, 0), "h a")}
                  </div>
        );
      })}
            </div>
            {weekDays.map((day) => (
              <div key={day} className="dds2025__day-grid">
                <div className="dds2025__day-header">{day}</div>
                <div
                  className="dds2025__day-body"
                  ref={(el) => (dayRefs.current[day] = el)}
                  style={{ height: timeWindow.totalHeightPx }}
                  onDragOver={(e) => onDragOverDayArea(day, e)}
                  onDrop={(e) => onDropOnDayArea(day, e)}
                  onDragLeave={() => {
                    lastHoverRef.current = { day: null, label: null };
                    setHoverIndicator(null);
                  }}
                >
      {(driveSegmentsWithWarn[day] || []).map((seg, i) => (
        <div
          key={`drive-${day}-${i}`}
          className={`dds2025__drive-chip ${seg.warn ? "dds2025__drive-chip--warn" : ""}`}
          style={{ top: seg.top }}
          title={Number.isFinite(seg.minutes) ? `Drive ${seg.minutes}m` : "Drive time unavailable"}
        >
          Drive {Number.isFinite(seg.minutes) ? `${seg.minutes}m` : "--"}
        </div>
      ))}
                  {getUnavailableSegments(day).map((seg, idx) => (
                    <div
                      key={`unavail-${day}-${idx}`}
                      className="dds2025__unavailable"
                      style={{ top: seg.top, height: seg.height }}
                    />
                  ))}
                  {hoverIndicator?.day === day && !draggingStudent && !draggingPlacement && (
                    <div className="dds2025__hover-badge">{hoverIndicator.label}</div>
                  )}
                  <div className="dds2025__grid-lines">
                    {Array.from({ length: Math.ceil(timeWindow.totalMinutes / SLOT_MINUTES) }).map((_, idx) => {
                      const minute = idx * SLOT_MINUTES;
                      const top = (minute / SLOT_MINUTES) * PIXELS_PER_SLOT;
                      const isHalfHour = minute % 30 === 0;
                      const isHour = minute % 60 === 0;
                      return (
                        <div
                          key={idx}
                          className={`dds2025__grid-line ${isHour ? "dds2025__grid-line--hour" : isHalfHour ? "dds2025__grid-line--half" : ""}`}
                          style={{ top }}
                        />
                      );
                    })}
                  </div>
                  {(positionedEventsByDay[day] || []).map((event) => {
                    const eventKey = event.placementKey || event.id;
                    const sid = getStudentKey(event);
                    const isLockedPlacement = lockedStudentIds.includes(sid);
                    const startEventHold = () => {
                      if (eventHoldTimerRef.current) clearTimeout(eventHoldTimerRef.current);
                      eventHoldTimerRef.current = setTimeout(() => {
                        setLockedStudentIds((prev) =>
                          prev.includes(sid) ? prev.filter((id) => id !== sid) : [...prev, sid]
                        );
                        eventHoldTimerRef.current = null;
                      }, LONG_PRESS_MS);
                    };
                    const cancelEventHold = () => {
                      if (eventHoldTimerRef.current) {
                        clearTimeout(eventHoldTimerRef.current);
                        eventHoldTimerRef.current = null;
                      }
                    };
                    const isDraggingThis =
                      draggingPlacement && (draggingPlacement.placementKey || draggingPlacement.id) === eventKey;
                    const displayTime =
                      isDraggingThis && hoverIndicator?.day === day ? hoverIndicator.label : event.timeLabel;
                    return (
                      <div
                        key={event.id || `${event.studentId}-${event.timeLabel}`}
                        className={`dds2025__event-block ${event.planned ? "dds2025__event-block--planned" : ""}`}
                        style={{
                          top: event.top,
                          height: event.height,
                          borderColor: isLockedPlacement ? "#f97316" : undefined,
                          background: isLockedPlacement
                            ? "#fff7ed"
                            : event.planned
                            ? "#dcfce7"
                            : "#e0f2fe",
                        }}
                        data-tooltip={isDraggingThis ? "" : event.tooltip}
                        draggable={event.planned}
                        onDragStart={(e) => {
                          cancelEventHold();
                          onDragStartPlacement(event, e);
                        }}
        onDragEnd={() => {
          setDraggingPlacement(null);
          setDragPreview(null);
        }}
                        onClick={() => onEventClick(event)}
                        onMouseDown={startEventHold}
                        onMouseUp={cancelEventHold}
                        onMouseLeave={cancelEventHold}
                        onTouchStart={startEventHold}
                        onTouchEnd={cancelEventHold}
                      >
                        <div className="dds2025__event-row">
                          <div className="dds2025__event-time">
                            {displayTime} {isLockedPlacement ? "🔒" : ""}
                          </div>
                          <div className="dds2025__event-name">{event.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        .dds2025 { display: flex; flex-direction: column; gap: 16px; margin-left: -50px; }
        .dds2025__drag-preview { position: fixed; left: 0; top: 0; transform: translate(-50%, -50%); pointer-events: none; z-index: 5000; background: #dcfce7; color: #0f172a; padding: 6px 10px; border-radius: 999px; font-size: 12px; box-shadow: 0 8px 20px rgba(15,23,42,0.2); opacity: 0.95; border: 1px solid #a7f3d0; }
        .dds2025__filters { display: flex; gap: 12px; flex-wrap: wrap; align-items: flex-end; }
        .dds2025__filter { display: flex; flex-direction: column; font-size: 14px; }
        .dds2025__filter select { padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 6px; }
        .dds2025__status { font-size: 12px; color: #6b7280; }
        .dds2025__grid { display: grid; grid-template-columns: 320px 1fr; gap: 16px; }
        .dds2025__column-title { font-weight: 700; margin-bottom: 8px; }
        .dds2025__students { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%); box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08); }
        .dds2025__student-list { display: flex; flex-direction: column; gap: 14px; max-height: 70vh; overflow: auto; padding: 6px 2px; }
        .dds2025__student-card { border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; background: #f1f5f9; cursor: grab; box-shadow: 0 6px 14px rgba(15, 23, 42, 0.06); transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease; }
        .dds2025__student-card:active { cursor: grabbing; }
        .dds2025__student-card:hover { transform: translateY(-2px); border-color: #cbd5e1; box-shadow: 0 10px 20px rgba(15, 23, 42, 0.12); }
        .dds2025-student-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
        .dds2025-student-name-row { display: grid; grid-template-columns: 24px 1fr; align-items: center; gap: 6px; }
        .dds2025-drag-hint { font-size: 14px; color: #94a3b8; }
        .dds2025-chip { background: #eef2ff; color: #4338ca; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; }
        .dds2025-chip--neutral { background: #f1f5f9; color: #475569; }
        .dds2025__student-name { font-weight: 800; font-size: 13px; }
        .dds2025__student-family { font-size: 10px; color: #6b7280; }
        .dds2025-student-name { font-weight: 800; font-size: 13px; }
        .dds2025-student-chips { display: flex; flex-wrap: wrap; gap: 6px; justify-content: flex-end; }
        .dds2025__calendar { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; background: #fff; overflow: auto; }
        .dds2025__calendar-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
        .dds2025__calendar-week { font-weight: 700; }
        .dds2025__calendar-nav { display: flex; gap: 6px; }
        .dds2025__nav-btn { border: 1px solid #e2e8f0; background: #fff; padding: 4px 8px; border-radius: 6px; cursor: pointer; }
        .dds2025__teacher-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 6px 0; font-size: 12px; color: #334155; }
        .dds2025__teacher-name { font-weight: 700; }
        .dds2025__teacher-instruments { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin: 6px 0; }
        .dds2025__teacher-instruments-label { font-size: 12px; color: #475569; font-weight: 700; }
        .dds2025__drive-cta { display: flex; align-items: center; gap: 8px; margin-top: 6px; font-size: 12px; color: #475569; }
        .dds2025__drive-text { font-size: 12px; }
        .dds2025__week-grid { display: grid; grid-template-columns: 64px repeat(7, minmax(160px, 1fr)); gap: 10px; align-items: start; }
        .dds2025__time-col { position: relative; border-right: 1px solid #e2e8f0; }
        .dds2025__time-marker { position: absolute; left: 4px; font-size: 11px; color: #94a3b8; transform: translateY(14px); }
        .dds2025__day-grid { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; background: #f8fafc; }
        .dds2025__day-header { background: #eef2ff; padding: 6px 10px; font-weight: 700; position: sticky; top: 0; z-index: 2; }
        .dds2025__day-body { position: relative; overflow-y: auto; background: #fff; }
        .dds2025__grid-lines { position: absolute; inset: 0; }
        .dds2025__grid-line { position: absolute; left: 0; right: 0; height: 1px; background: #f1f5f9; }
        .dds2025__grid-line--half { background: #e2e8f0; }
        .dds2025__grid-line--hour { background: #cbd5e1; }
        .dds2025__unavailable { position: absolute; left: 0; right: 0; background: rgba(148, 163, 184, 0.25); pointer-events: none; }
        .dds2025__drive-chip { position: absolute; left: 50%; transform: translate(-50%, -50%); background: #ecfdf3; color: #166534; border: 1px solid #86efac; border-radius: 999px; padding: 2px 8px; font-size: 10px; box-shadow: 0 2px 6px rgba(15,23,42,0.12); pointer-events: none; white-space: nowrap; z-index: 4; }
        .dds2025__drive-chip--warn { background: #fef2f2; color: #991b1b; border-color: #fca5a5; }
        .dds2025__event-block { position: absolute; left: 6px; right: 6px; padding: 4px 6px; border-radius: 6px; border: 1px solid #e2e8f0; background: #e0f2fe; font-size: 12px; box-shadow: 0 6px 14px rgba(15, 23, 42, 0.06); box-sizing: border-box; }
        .dds2025__event-block--planned { background: #dcfce7; border-color: #a7f3d0; }
        .dds2025__event-row { display: flex; align-items: center; gap: 6px; }
        .dds2025__event-time { font-weight: 700; font-size: 11px; white-space: nowrap; }
        .dds2025__event-name { font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .dds2025__event-block::after { content: attr(data-tooltip); position: absolute; left: 0; top: -4px; transform: translateY(-100%); background: #0f172a; color: #fff; padding: 6px 8px; border-radius: 8px; box-shadow: 0 8px 16px rgba(15,23,42,0.24); font-size: 11px; white-space: pre-line; opacity: 0; pointer-events: none; transition: opacity 80ms ease, transform 80ms ease; z-index: 5; }
        .dds2025__event-block:hover::after { opacity: 1; transform: translateY(-110%); }
        .dds2025__hover-badge { position: absolute; top: 8px; right: 8px; padding: 4px 8px; font-size: 11px; font-weight: 700; color: #0f172a; background: rgba(255,255,255,0.95); border: 1px solid #cbd5e1; border-radius: 8px; z-index: 3; box-shadow: 0 4px 10px rgba(15,23,42,0.08); pointer-events: none; }
        .dds2025__empty { font-size: 12px; color: #9ca3af; padding: 12px 0; }
        .dds2025__modal-backdrop { position: fixed; inset: 0; background: rgba(15,23,42,0.35); display: flex; align-items: center; justify-content: center; z-index: 3000; }
        .dds2025__modal { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px; max-width: 360px; width: 90%; box-shadow: 0 10px 30px rgba(15,23,42,0.25); }
        .dds2025__modal--wide { max-width: 640px; }
        .dds2025__modal--event { max-width: 1100px; }
        .dds2025__modal-title { font-weight: 700; margin-bottom: 8px; }
        .dds2025__modal-body { font-size: 14px; color: #475569; margin-bottom: 12px; }
        .dds2025__modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
        .dds2025__event-modal-system { display: flex; flex-direction: row; align-items: center; justify-content: center; gap: 10px; width: 90%; height: 90%; }
        .dds2025__event-modal-x { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: flex-start; height: 95%; }
        .dds2025__event-modal-left { flex: 10; background: #fff; border-radius: 10px; height: 95%; padding: 16px; display: flex; flex-direction: column; gap: 12px; overflow: auto; }
        .dds2025__event-modal-center { flex: 20; background: #fff; border-radius: 10px; height: 95%; padding: 16px; display: flex; flex-direction: column; gap: 10px; }
        .dds2025__event-modal-right { flex: 10; background: #1e293b; border-radius: 10px; height: 95%; overflow: hidden; }
        .dds2025__event-modal-student { font-size: 18px; font-weight: 800; color: #0f172a; }
        .dds2025__event-modal-map { width: 100%; flex: 1; min-height: 360px; border-radius: 10px; overflow: hidden; border: 1px solid #e2e8f0; }
        .dds2025__event-modal-edges { display: flex; flex-direction: column; gap: 6px; flex: 1; min-height: 0; }
        .dds2025__event-modal-edges .dds2025__simple-list { flex: 1; min-height: 0; max-height: none; overflow: auto; }
        .dds2025__map-pin { background-color: #475569; width: 30px; height: 30px; display: flex; justify-content: center; align-items: center; border-radius: 5px; font-size: 14px; z-index: 1; }
        .dds2025__map-marker { position: relative; background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 10px 12px; box-shadow: 0 16px 30px rgba(15,23,42,0.12); min-width: 210px; max-width: 260px; }
        .dds2025__map-marker__name { font-size: 14px; font-weight: 700; color: #0f172a; }
        .dds2025__map-marker__address { font-size: 11px; color: #64748b; margin-top: 2px; }
        .dds2025__map-marker__icon-row { display: flex; flex-direction: row; justify-content: flex-start; align-items: center; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
        .dds2025__map-marker__students { display: inline-flex; gap: 6px; align-items: center; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 999px; padding: 2px 8px; font-size: 11px; color: #0f172a; }
        .dds2025__map-marker__instruments { font-size: 10px; display: flex; gap: 6px; flex-wrap: wrap; }
        .dds2025__map-marker__inst { display: inline-flex; align-items: center; justify-content: center; border: 1px solid #e2e8f0; border-radius: 999px; padding: 2px 8px; height: 20px; color: #0f172a; background: #f8fafc; }
        .dds2025__map-marker__tip { position: absolute; bottom: -7px; left: 24px; rotate: 45deg; background-color: #ffffff; border-right: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; width: 12px; height: 12px; }
        .dds2025__section { margin-bottom: 14px; }
        .dds2025__section-title { font-weight: 700; margin-bottom: 6px; }
        .dds2025__simple-list { display: flex; flex-direction: column; gap: 6px; max-height: 320px; overflow: auto; }
        .dds2025__simple-row { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; background: #f8fafc; }
        .dds2025__simple-row--clickable { cursor: pointer; }
        .dds2025__simple-row--clickable:hover { border-color: #cbd5e1; background: #e2e8f0; }
        .dds2025__simple-name { font-weight: 700; color: #0f172a; }
        .dds2025__simple-sub { font-size: 12px; color: #475569; }
        .dds2025__missing-list { display: flex; flex-direction: column; gap: 8px; max-height: 440px; overflow: auto; }
        .dds2025__missing-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; border: 1px solid #e2e8f0; background: #f8fafc; border-radius: 8px; padding: 8px 10px; }
        .dds2025__missing-name { font-weight: 700; color: #0f172a; }
        .dds2025__missing-address { font-size: 12px; color: #475569; }
        .dds2025__tag { display: inline-flex; align-items: center; gap: 6px; margin-bottom: 2px; font-size: 11px; color: #334155; }
        .dds2025__badge { display: inline-flex; align-items: center; padding: 2px 6px; margin-left: 8px; border-radius: 999px; background: #ecfdf3; color: #166534; font-size: 10px; font-weight: 700; border: 1px solid #86efac; }
        .dds2025__ledger-panel { position: fixed; top: 16px; right: 16px; z-index: 40; width: 340px; max-height: 60vh; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; box-shadow: 0 10px 30px rgba(15,23,42,0.12); overflow: hidden; }
        .dds2025__ledger-panel.closed { height: auto; }
        .dds2025__ledger-header { display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; font-weight: 700; font-size: 13px; background: #e2e8f0; }
        .dds2025__ledger-toggle { background: #0ea5e9; color: white; border: none; border-radius: 6px; padding: 4px 10px; font-size: 12px; cursor: pointer; }
        .dds2025__ledger-list { max-height: 48vh; overflow: auto; padding: 8px 10px; }
        .dds2025__ledger-row { display: flex; align-items: center; gap: 6px; font-size: 12px; padding: 4px 0; border-bottom: 1px solid #e2e8f0; }
        .dds2025__ledger-row:last-child { border-bottom: none; }
        .dds2025__ledger-student { font-weight: 700; }
        .dds2025__ledger-teacher { color: #475569; }
        .dds2025__ledger-time { margin-left: auto; color: #334155; font-weight: 600; }
        .dds2025__drop-ghost { border: 2px dashed #0ea5e9; color: #0ea5e9; padding: 10px; margin-bottom: 10px; border-radius: 8px; text-align: center; background: #e0f2fe; font-weight: 600; }
        .dds2025.dds2025__welcome { position: fixed; inset: 0; width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #f6f7f9; padding: 32px; color: #111827; margin: 0; }
        .dds2025__welcome-card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 16px; padding: 40px 36px; width: min(640px, 92vw); margin: 0 auto; box-shadow: 0 20px 60px rgba(15,23,42,0.12); text-align: center; }
        .dds2025__welcome-title { margin: 0 0 10px 0; font-size: 28px; font-weight: 700; color: #0f172a; letter-spacing: -0.02em; }
        .dds2025__welcome-subtitle { margin: 0 auto 24px; color: #6b7280; font-size: 15px; max-width: 480px; }
        .dds2025__welcome-actions { display: flex; gap: 14px; justify-content: center; align-items: center; flex-wrap: wrap; margin-top: 8px; }
        .dds2025__welcome .dds2025__nav-btn { padding: 11px 18px; border-radius: 999px; font-weight: 600; letter-spacing: 0.2px; transition: transform 120ms ease, box-shadow 120ms ease, background 120ms ease, border 120ms ease, color 120ms ease; }
        .dds2025__welcome .dds2025__nav-btn--primary { background: #111827; color: #ffffff; border: 1px solid #111827; box-shadow: 0 12px 22px rgba(17,24,39,0.2); }
        .dds2025__welcome .dds2025__nav-btn--primary:hover { background: #0b1220; transform: translateY(-1px); box-shadow: 0 16px 28px rgba(17,24,39,0.24); }
        .dds2025__welcome .dds2025__nav-btn--primary:active { transform: translateY(0); box-shadow: 0 10px 18px rgba(17,24,39,0.18); }
        .dds2025__welcome .dds2025__nav-btn:not(.dds2025__nav-btn--primary) { background: #ffffff; color: #111827; border: 1px solid #d1d5db; box-shadow: 0 8px 18px rgba(15,23,42,0.08); }
        .dds2025__welcome .dds2025__nav-btn:not(.dds2025__nav-btn--primary):hover { background: #f8fafc; transform: translateY(-1px); box-shadow: 0 12px 22px rgba(15,23,42,0.12); }
        .dds2025__welcome .dds2025__nav-btn:not(.dds2025__nav-btn--primary):active { transform: translateY(0); box-shadow: 0 6px 14px rgba(15,23,42,0.08); }
        .dds2025__welcome-field { display: flex; flex-direction: column; gap: 8px; margin: 16px 0; text-align: center; }
        .dds2025__welcome-label { font-weight: 600; color: #111827; font-size: 13px; }
        .dds2025__welcome-field select { padding: 12px 14px; border: 1px solid #d1d5db; border-radius: 10px; background: #ffffff; color: #111827; text-align: center; }
        .dds2025__welcome-hint { font-size: 12px; color: #6b7280; margin-top: 6px; }
        .dds2025__icon-btn { margin-left: 8px; border: 1px solid #d0d5dd; background: #f9fafb; color: #0f172a; border-radius: 50%; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
        .dds2025__icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .dds2025__icon-btn:hover:not(:disabled) { background: #edf2f7; }
        .dds2025__chip-remove { margin-left: auto; border: 1px solid #e2e8f0; background: #fff; color: #ef4444; width: 22px; height: 22px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; font-size: 14px; cursor: pointer; }
        .dds2025__chip-remove:hover { background: #fee2e2; }
        .dds2025__remove-calendar-btn { margin-top: 8px; align-self: center; background: transparent; border: none; color: #ef4444; border: 1px solid #ef4444; padding: 10px 20px; font-weight: 600; cursor: pointer; border-radius: 5px;}
        .dds2025__remove-calendar-btn:hover { color: #dc2626; text-decoration: underline; }
      `}</style>
      {false && showCalendarEventsModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__modal dds2025__modal--wide">
            <div className="dds2025__modal-title">
              Dropdown Teachers — {dropdownTeachersForModal.length}
            </div>
            <div className="dds2025__modal-body">
              <div className="dds2025__modal-actions" style={{ justifyContent: "flex-start", marginBottom: 8 }}>
              </div>
              <div className="dds2025__section">
                <div className="dds2025__section-title">
                  Calendar Events for Dropdown Teachers
                </div>
                <div className="dds2025__simple-list">
                  {weeklyTeacherEventsForModal.length ? (
                    weeklyTeacherEventsForModal.map(({ ev, date, idx }) => {
                      const when = date && !isNaN(date) ? format(date, "EEE MMM d h:mma") : "Date N/A";
                      return (
                        <div key={ev.id || ev._id || idx} className="dds2025__simple-row">
                          <div className="dds2025__simple-name">
                            {ev.studentName || ev.familyName || ev.lessonName || "Lesson"}
                          </div>
                          <div className="dds2025__simple-sub">
                            {when} • {ev.lessonLength || ev.rowLength || "--"} mins • Teacher{" "}
                            {ev.teacherName || ev.teacherId || ev.teacherID || ev.TeacherID}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="dds2025__simple-sub">No events loaded yet.</div>
                  )}
                </div>
              </div>
              <div className="dds2025__section">
                <div className="dds2025__section-title">Candidate Slots (pre-optimization)</div>
                <div className="dds2025__simple-list">
                  {Array.from(candidateSlotsByTeacher.entries()).map(([tid, dayMap]) => {
                    const teacherLabel = teacherNameById.get(String(tid)) || tid;
                    return (
                      <div key={tid} className="dds2025__simple-row">
                        <div className="dds2025__simple-name">{teacherLabel}</div>
                        <div className="dds2025__simple-sub">
                          {weekDays.map((day, idx) => {
                            const slots = dayMap?.[idx] || [];
                            const formatted = slots
                              .map((slot) => {
                                const h = Math.floor(slot.start / 60)
                                  .toString()
                                  .padStart(2, "0");
                                const m = (slot.start % 60).toString().padStart(2, "0");
                                return `${h}:${m}`;
                              })
                              .join(", ");
                            return (
                              <div key={`${tid}-${day}`} style={{ marginBottom: 4 }}>
                                <strong>{day}:</strong>{" "}
                                {formatted || "—"}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {!candidateSlotsByTeacher.size && (
                    <div className="dds2025__simple-sub">No candidate slots yet.</div>
                  )}
                </div>
              </div>
              <div className="dds2025__section">
                <div className="dds2025__section-title">Student Slot Options (drive-aware)</div>
                <div className="dds2025__simple-list">
                  {studentSlotOptions.map(({ student, options }) => (
                    <div
                      key={student.id || student.studentId || student.studentName}
                      className="dds2025__simple-row"
                    >
                      <div className="dds2025__simple-name">
                        {student.studentName || student.familyName || student.id || "Student"} — {options.length} options
                      </div>
                      <div className="dds2025__simple-sub">
                        {options.slice(0, 6).map((opt) => {
                          const time = `${String(Math.floor(opt.startMinutes / 60)).padStart(2, "0")}:${String(
                            opt.startMinutes % 60
                          ).padStart(2, "0")}`;
                          const teacherLabel = teacherNameById.get(String(opt.teacherId)) || opt.teacherId;
                          const delta = Number.isFinite(opt.extraDriveMinutes)
                            ? `${opt.extraDriveMinutes.toFixed(0)} min`
                            : "n/a";
                          const gapNote = Number.isFinite(opt.score) && Number.isFinite(opt.extraDriveMinutes)
                            ? `gap≈${(opt.score - opt.extraDriveMinutes).toFixed(0)}m`
                            : "";
                          return (
                            <span key={opt.slotId} style={{ display: "block" }}>
                              {weekDays[opt.dayIndex]} @ {time} • {teacherLabel} • Δdrive {delta} {gapNote}
                            </span>
                          );
                        })}
                        {options.length > 6 && <span>…and {options.length - 6} more</span>}
                        {!options.length && "No feasible slots"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="dds2025__section">
                <div className="dds2025__section-title">Drive Edges ({driveEdgesForModal.length})</div>
                <div className="dds2025__simple-list">
                  {driveEdgesForModal.length ? (
                    driveEdgesForModal.map((edge) => (
                      <div key={edge.id} className="dds2025__simple-row">
                        <div className="dds2025__simple-name">
                          {edge.fromLabel} → {edge.toLabel}
                        </div>
                        <div className="dds2025__simple-sub">
                          {Number.isFinite(edge.minutes) ? `${edge.minutes} mins` : "Unavailable"} • {edge.bucket || ""}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="dds2025__simple-sub">No drive edges cached yet.</div>
                  )}
                </div>
              </div>
            </div>
            <div className="dds2025__modal-actions">
              <button className="dds2025__nav-btn" onClick={() => setShowCalendarEventsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showEventDriveModal && (
        <div className="dds2025__modal-backdrop">
          <div className="dds2025__event-modal-system">
            <div className="dds2025__event-modal-x">
              <button
                className="dds2025__icon-btn"
                onClick={() => {
                  setShowEventDriveModal(false);
                  setEventModalData(null);
                }}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            {eventModalData && (
              <>
                <div className="dds2025__event-modal-left">
                  <div className="dds2025__event-modal-student">
                    {eventModalData.studentName || eventModalData.familyName || "Student"}
                  </div>
                  <div>
                    <label className="dds2025__welcome-label">Lesson length</label>
                    <select
                      value={
                        eventModalData.lessonLength ||
                        (eventModalData.rowLength ? eventModalData.rowLength * SLOT_MINUTES : 30)
                      }
                      onChange={(e) => handleModalLessonLengthChange(e.target.value)}
                      style={{ width: "100%", padding: "8px", borderRadius: 8, border: "1px solid #d0d5dd" }}
                    >
                      {[15, 20, 25, 30, 45, 60, 75, 90].map((len) => (
                        <option key={len} value={len}>
                          {len} minutes
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="dds2025__welcome-label">Instruments</label>
                    <div className="dds2025__student-chips" style={{ marginBottom: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {instrumentsForModal.map((inst, idx) => (
                        <span
                          key={idx}
                          className="student-info-tab-center-gutter__instrument-chip"
                          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                        >
                          {inst.parentInstrument || inst.instrument || inst.name || "Instrument"}
                          <button
                            type="button"
                            onClick={() => handleModalRemoveInstrument(inst)}
                            style={{ border: "none", background: "transparent", cursor: "pointer", color: "#ef4444" }}
                            aria-label="Remove instrument"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      {!instrumentsForModal.length && (
                        <span className="dds2025-chip dds2025-chip--neutral">None listed</span>
                      )}
                    </div>
                    <select
                      value=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) handleModalInstrumentChange(val);
                      }}
                      style={{ width: "100%", padding: "8px", borderRadius: 8, border: "1px solid #d0d5dd" }}
                    >
                      <option value="">Select instrument</option>
                      {(allInstruments.length ? allInstruments : Array.from(instrumentSetForSchool)).map((inst, idx) => {
                        const label = inst.parentInstrument || inst.instrument || inst.name || inst;
                        const value = inst.parentInstrument || inst.instrument || inst.name || inst;
                        return (
                          <option key={`${value}-${idx}`} value={value}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="dds2025__event-modal-edges">
                    <div className="dds2025__section-title">Drive Times</div>
                    <div className="dds2025__simple-list">
                      {driveEdgesForPlaceDirectory.length ? (
                        driveEdgesForPlaceDirectory.map((edge) => (
                          <div key={edge.id} className="dds2025__simple-row">
                            <div className="dds2025__simple-name">
                              {edge.fromLabel} → {edge.toLabel}
                            </div>
                            <div className="dds2025__simple-sub">
                              {Number.isFinite(edge.minutes) ? `${edge.minutes} mins` : "Unknown"}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="dds2025__simple-sub">No drive edges found for this school.</div>
                      )}
                    </div>
                    {eventModalData?.planned && (
                      <button
                        type="button"
                        className="dds2025__remove-calendar-btn"
                        onClick={handleRemoveFromCalendar}
                      >
                        Remove From Calendar
                      </button>
                    )}
                  </div>
                </div>
                <div className="dds2025__event-modal-center">
                  {!showEventMap ? (
                    <div
                      className="dds2025__event-modal-map"
                      style={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8 }}
                    >
                      <div className="dds2025__simple-sub">Map loading is paused to avoid freezing.</div>
                      <button
                        type="button"
                        className="dds2025__nav-btn dds2025__nav-btn--primary"
                        onClick={() => setShowEventMap(true)}
                      >
                        Load map
                      </button>
                    </div>
                  ) : eventModalLocation ? (
                    <div className="dds2025__event-modal-map">
                      <APIProvider apiKey={"AIzaSyCTz6Xc5kEtX2QxgcKe3-sQPwsEwRE8WmU"}>
                        <GoogleMap
                          zoom={10}
                          center={{ lat: eventModalLocation.lat, lng: eventModalLocation.lng }}
                          gestureHandling={"greedy"}
                          disableDefaultUI={true}
                          mapId={"3324feda48bc0f19"}
                        >
                          {mapMarkers.map((marker) => (
                            <AdvancedMarker
                              key={marker.id}
                              position={marker.location}
                              gmpClickable={true}
                              onClick={() =>
                                setActiveMapMarkerId((prev) => (prev === marker.id ? null : marker.id))
                              }
                            >
                              <div className="dds2025__map-pin" style={{ cursor: "pointer" }}>
                                <Pin
                                  background={
                                    marker.role === "teacher"
                                      ? "#2563eb"
                                      : marker.role === "other"
                                      ? "#14532d"
                                      : "#dc2626"
                                  }
                                  borderColor={
                                    marker.role === "teacher"
                                      ? "#1e3a8a"
                                      : marker.role === "other"
                                      ? "#052e16"
                                      : "#991b1b"
                                  }
                                  glyphColor="#ffffff"
                                />
                              </div>
                            </AdvancedMarker>
                          ))}
                          {activeMapMarker && (
                            <InfoWindow
                              position={activeMapMarker.location}
                              onCloseClick={() => setActiveMapMarkerId(null)}
                            >
                              <div className="dds2025__map-marker">
                                <div className="dds2025__map-marker__name">
                                  {activeMapMarker.name} {activeMapMarker.role === "teacher" ? "Teacher" : "Family"}
                                </div>
                                {activeMapMarker.lessonTime ? (
                                  <div className="dds2025__map-marker__address">{activeMapMarker.lessonTime}</div>
                                ) : null}
                                {activeMapMarker.address ? (
                                  <div className="dds2025__map-marker__address">{activeMapMarker.address}</div>
                                ) : null}
                                <div className="dds2025__map-marker__icon-row">
                                  {activeMapMarker.students != null && (
                                    <div className="dds2025__map-marker__students">
                                      <MdAccessibility />
                                      <div>{activeMapMarker.students}</div>
                                    </div>
                                  )}
                                  {activeMapMarker.inst?.length ? (
                                    <div className="dds2025__map-marker__instruments">
                                      {activeMapMarker.inst.map((inst, idx) => (
                                        <div key={`active-inst-${idx}`} className="dds2025__map-marker__inst">
                                          {inst}
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </InfoWindow>
                          )}
                        </GoogleMap>
                      </APIProvider>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>No location available for this student.</div>
                  )}
                </div>
                <div className="dds2025__event-modal-right student-onboarding-card-modal-fall-2023__notes-area">
                  {eventModalFamily?.familyId ? (
                    <NoteAreaFall2023
                      key={eventModalFamily.familyId}
                      modalInfo={{
                        id: eventModalFamily.familyId,
                        type: "familyId",
                        displayName: eventModalFamily.familyId,
                      }}
                      userData={userData}
                      school={(eventModalFamily.school || selectedSchool || "").toLowerCase()}
                    />
                  ) : (
                    <div className="dds2025__simple-sub" style={{ padding: 12 }}>
                      Notes not available.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {dragPreview && (
        <div
          className="dds2025__drag-preview"
          style={{ left: dragPreview.x, top: dragPreview.y }}
        >
          {dragPreview.label}
        </div>
      )}
    </div>
  );
};

export default DragAndDropScheduler2025;
